/*
  Sylvans — Nebelwald-Szene

  Live gerenderter Hintergrund fuer den Hero der Produktseite
  product.sylvans.json, nach dem Rezept der Ghosthaven-Szene
  (ghosthaven-scene.js): Three.js aus dem Theme, kein einziges
  geladenes Bild -- Himmel, Mond, Baeume, Nebel und die beiden
  Eulenwesen entstehen beim Aufruf aus dem Seed.

  Die Szene ist bewusst viel kleiner als Ghosthaven: gestaffelte
  Silhouetten-Ebenen statt Geometrie, nur Sprites und unbeleuchtete
  Flaechen. Sie muss neben einer Kaufseite laufen, nicht statt ihr.

  Das Duell der Nebelschwingen ist der eine inszenierte Moment:
  zwei Eulenwesen -- eines hell, eines dunkel, wie die beiden
  Seiten im Spiel -- ziehen in unregelmaessigen Abstaenden durch
  den Wald, und hin und wieder kreuzen sie einander.

  Faellt irgendetwas aus (kein WebGL, Software-Renderer, zu
  niedrige Bildrate, Kontextverlust), bleibt der Hero exakt so
  stehen, wie er ohne dieses Skript aussieht: CSS-Mond, Ringe
  und Nebelbaenke aus section-sylvans.css. Die Szene ist eine
  Zugabe, nie eine Voraussetzung.
*/
(function () {
  'use strict';
  var THREE = window.THREE;

  /* ------------------------------------------------------------------ */
  /* Kleinkram                                                           */
  /* ------------------------------------------------------------------ */

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function lerp(a, b, t) { return a + (b - a) * t; }

  // Deterministischer Zufall. Gleicher Seed, gleicher Wald.
  function mulberry32(a) {
    return function () {
      a |= 0;
      a = (a + 0x6d2b79f5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  var now = (window.performance && performance.now)
    ? function () { return performance.now(); }
    : function () { return +new Date(); };

  // Läuft WebGL ueber die CPU, gar nicht erst anfangen -- Begruendung
  // und Muster stammen aus ghosthaven-scene.js.
  function softwareRenderer() {
    var c = document.createElement('canvas');
    var gl = c.getContext('webgl2') || c.getContext('webgl');
    if (!gl) return true;
    var name = '';
    try {
      var dbg = gl.getExtension('WEBGL_debug_renderer_info');
      if (dbg) name = String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || '');
    } catch (e) { /* Browser darf das verweigern */ }
    var lost = gl.getExtension('WEBGL_lose_context');
    if (lost) lost.loseContext();
    return /swiftshader|llvmpipe|software|basic render|microsoft basic/i.test(name);
  }

  function makeCanvas(w, h) {
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    return c;
  }

  // Bewusst KEIN Farbmanagement, weder hier noch am Renderer: das
  // gepinnte r149 kennt texture.colorSpace/outputColorSpace noch nicht
  // (kam erst r152), und die frueher versuchte Erkennung ueber die
  // Existenz von THREE.SRGBColorSpace griff ins Leere -- die Konstante
  // gibt es in r149 laengst, nur die Properties nicht. Der lineare
  // 1:1-Durchlauf ist der gewollte Zustand: die Canvas-Farben kommen
  // exakt so auf den Schirm, wie der CSS-Fallback sie definiert.
  // Wer three.js aktualisiert, muss die Farben hier neu abstimmen.
  function tex(canvas) {
    var t = new THREE.CanvasTexture(canvas);
    t.anisotropy = 2;
    // Keine Mipmaps: die Texturen stehen nahe 1:1 oder vergroessert
    // im Bild, die Pyramide wuerde nur VRAM (~8 MB) und Aufbauzeit
    // kosten -- und die NPOT-Eulen bekaemen in WebGL1 ohnehin keine.
    t.generateMipmaps = false;
    t.minFilter = THREE.LinearFilter;
    return t;
  }

  // Farbe mit Alpha aus Hex -- die Texturen mischen viel mit
  // halbtransparenten Schichten, das spart die staendige Umrechnung.
  function rgba(hex, a) {
    var c = new THREE.Color(hex);
    return 'rgba(' + Math.round(c.r * 255) + ',' + Math.round(c.g * 255) + ',' +
      Math.round(c.b * 255) + ',' + a + ')';
  }

  /* ------------------------------------------------------------------ */
  /* Texturen                                                            */
  /* ------------------------------------------------------------------ */

  // Himmel, Mond, Strahlenkranz, Sterne, Lichtfaecher.
  //
  // Nachthimmel: nur noch ein Verlauf. Die Sterne steckten frueher in
  // dieser Textur, und weil das Quad 4:2 auf einer 1:2-Leinwand lag,
  // wurde jeder Stern zum waagerechten Strich gezogen. Sterne und
  // Sternenstaub sind jetzt eine eigene Points-Wolke (isotrop), der
  // Verlauf hier verbraucht keinen Zufall mehr. Die drei Stops stehen
  // 1:1 als Untergrund in section-sylvans.css (Abschnitt 14) -- wer
  // hier dreht, dreht dort mit.
  var SKY_STOPS = [[0, '#0f0a24'], [0.42, '#1f174f'], [0.68, '#1d1650'], [1, '#2b2969']];
  function texSky() {
    var W = 256, H = 512;
    var c = makeCanvas(W, H), x = c.getContext('2d');
    var g = x.createLinearGradient(0, 0, 0, H);
    for (var i = 0; i < SKY_STOPS.length; i++) g.addColorStop(SKY_STOPS[i][0], SKY_STOPS[i][1]);
    x.fillStyle = g;
    x.fillRect(0, 0, W, H);
    return c;
  }

  // Weiter Halo um den Mond: nur noch der weiche Schein und die beiden
  // Kreisbahnen des Spielplans. Der helle Innenhof von frueher, der
  // die Scheibe grau einbettete, ist durch den knappen Kernhof im
  // Mond-Shader ersetzt. Radius 1 = Quadrand; die Scheibe endet bei
  // MOON_RD (0.168 vom Quadradius).
  function texMoonHalo(cfg) {
    var S = 512, c = makeCanvas(S, S), x = c.getContext('2d');
    var m = S / 2;
    var halo = x.createRadialGradient(m, m, 0, m, m, m);
    halo.addColorStop(0, rgba(cfg.moon, 0.34));
    halo.addColorStop(0.17, rgba(cfg.moon, 0.26));
    halo.addColorStop(0.3, rgba('#b9b4e6', 0.11));
    halo.addColorStop(0.6, rgba(cfg.haze, 0.035));
    halo.addColorStop(1, rgba(cfg.haze, 0));
    x.fillStyle = halo;
    x.fillRect(0, 0, S, S);
    x.lineWidth = 1.5;
    x.strokeStyle = rgba(cfg.moon, 0.2);
    x.beginPath(); x.arc(m, m, S * 0.21, 0, 7); x.stroke();
    x.strokeStyle = rgba(cfg.moon, 0.1);
    x.beginPath(); x.arc(m, m, S * 0.34, 0, 7); x.stroke();
    return c;
  }

  // Die Mondscheibe mit Zeichnung: Randverdunklung, ein paar weiche
  // Maria in kaltem Violett, eine haarfeine Frost-Sichel am Rand.
  // Wird NORMAL (deckend) gemischt -- additiv auf Halo und Schleier
  // gelegt lief die alte Scheibe flach und grau aus. Radius 0.42 der
  // Textur, der Rest ist Rand fuer die Kantenglaettung. Mipmaps nur
  // hier: 430 Texel Scheibe auf ~120 px Bild flimmern sonst am Rand.
  function texMoonDisc(cfg, rnd) {
    var S = 512, c = makeCanvas(S, S), x = c.getContext('2d');
    var m = S / 2, R = S * 0.42;
    // Kern auf 0,9 statt 1,0: die hellsten Partien des gedruckten
    // Covers bleiben heller als der Mond -- er ist Kulisse, die Box
    // ist die Hauptsache. Rand 0,8 davon.
    var base = new THREE.Color(cfg.moon).multiplyScalar(0.9);
    var limb = base.clone().multiplyScalar(0.8).lerp(new THREE.Color('#a9a5cc'), 0.25);
    var g = x.createRadialGradient(m - R * 0.18, m - R * 0.2, R * 0.05, m, m, R);
    g.addColorStop(0, '#' + base.getHexString());
    g.addColorStop(0.55, '#' + base.clone().lerp(limb, 0.35).getHexString());
    g.addColorStop(1, '#' + limb.getHexString());
    x.fillStyle = g;
    x.beginPath(); x.arc(m, m, R, 0, 7); x.fill();
    // Ab hier nur noch innerhalb der Scheibe zeichnen
    x.save();
    x.beginPath(); x.arc(m, m, R - 0.5, 0, 7); x.clip();
    // Maria: 5-8 Flecken UEBER DIE GANZE SCHEIBE gestreut (Mindest-
    // abstand, Winkel im goldenen Schnitt weitergedreht), zwei mittlere
    // und sonst kleine -- die erste Fassung haeufte drei Kleckse oben
    // links zu einem Fleck. Jeder Fleck aus 2-4 ueberlappenden weichen
    // Ellipsen, einzelne runde Kleckse lasen sich als Gesicht.
    var n = 5 + Math.floor(rnd() * 4), spots = [], ang = rnd() * 6.283;
    for (var i = 0; i < n; i++) {
      var big = i < 2, a, d, ok = false;
      for (var tr = 0; tr < 30 && !ok; tr++) {
        a = ang + i * 2.39996 + (rnd() - 0.5) * 0.9;
        d = (big ? 0.15 + rnd() * 0.35 : 0.3 + rnd() * 0.45) * R;
        ok = true;
        for (var q = 0; q < spots.length; q++) {
          var ex = Math.cos(a) * d - spots[q][0], ey = Math.sin(a) * d - spots[q][1];
          if (ex * ex + ey * ey < Math.pow(R * 0.34, 2)) ok = false;
        }
      }
      spots.push([Math.cos(a) * d, Math.sin(a) * d]);
      var al = 0.06 + rnd() * 0.04;
      var size = big ? 0.24 + rnd() * 0.1 : 0.08 + rnd() * 0.06;
      var parts = big ? 4 + Math.floor(rnd() * 2) : 2 + Math.floor(rnd() * 2);
      for (var pp = 0; pp < parts; pp++) {
        var mx = m + Math.cos(a) * d + (rnd() - 0.5) * R * size;
        var my = m + Math.sin(a) * d + (rnd() - 0.5) * R * size;
        var mr = R * size * (0.7 + rnd() * 0.5);
        var mg = x.createRadialGradient(mx, my, 0, mx, my, mr);
        mg.addColorStop(0, rgba('#6f64a8', al * 0.85));
        mg.addColorStop(0.45, rgba('#6f64a8', al * 0.6));
        mg.addColorStop(1, rgba('#6f64a8', 0));
        x.fillStyle = mg;
        x.save();
        x.translate(mx, my); x.rotate(rnd() * 3.14); x.scale(1, 0.5 + rnd() * 0.5); x.translate(-mx, -my);
        x.beginPath(); x.arc(mx, my, mr, 0, 7); x.fill();
        x.restore();
      }
    }
    // Frost-Sichel: haarfeiner, kaelterer Saum auf der Schattenseite,
    // zur Mitte hin am staerksten, an den Enden auslaufend -- als
    // Folge kurzer Bogenstuecke mit steigender/fallender Deckkraft.
    x.lineCap = 'butt';
    var a0 = -0.5, a1 = 2.1, steps = 40;
    for (var si = 0; si < steps; si++) {
      var f0 = si / steps, f1 = (si + 1) / steps;
      var env = Math.sin(Math.PI * (f0 + f1) / 2);
      x.strokeStyle = rgba(cfg.frost, 0.75 * env);
      x.lineWidth = S * (0.004 + 0.008 * env);
      x.beginPath(); x.arc(m, m, R - S * 0.005, a0 + (a1 - a0) * f0, a0 + (a1 - a0) * f1 + 0.005); x.stroke();
      x.strokeStyle = rgba(cfg.glimmer, 0.16 * env);
      x.lineWidth = S * 0.035 * env;
      x.beginPath(); x.arc(m, m, R - S * 0.016, a0 + (a1 - a0) * f0, a0 + (a1 - a0) * f1 + 0.005); x.stroke();
    }
    x.restore();
    return c;
  }

  // Strahlenkranz hinter dem Cover: 16 schmale Keile in Lavendel, die
  // aus der Mitte duenn ansetzen, aussen breiter werden und spitz
  // auslaufen -- die gedruckten Lichtbahnen der Packung, im Wald
  // weitergezogen. Lang und kurz im Wechsel wie auf dem Cover.
  function texRays(rnd) {
    var S = 512, c = makeCanvas(S, S), x = c.getContext('2d');
    var m = S / 2, R = S / 2 - 2;
    var N = 16;
    for (var i = 0; i < N; i++) {
      var a = (i / N) * Math.PI * 2 + (rnd() - 0.5) * 0.14;
      var L = R * (i % 2 ? 0.66 + rnd() * 0.16 : 0.86 + rnd() * 0.14);
      var w = 0.022 + rnd() * 0.022;          // halbe Keilbreite in rad bei 0.6 L
      var r0 = L * 0.12;
      var g = x.createRadialGradient(m, m, 0, m, m, L);
      g.addColorStop(0, 'rgba(185,168,234,0)');
      g.addColorStop(0.3, 'rgba(185,168,234,0.35)');
      g.addColorStop(0.55, 'rgba(185,168,234,1)');
      g.addColorStop(0.8, 'rgba(185,168,234,0.45)');
      g.addColorStop(1, 'rgba(185,168,234,0)');
      x.fillStyle = g;
      var ca = Math.cos(a), sa = Math.sin(a);
      function P(r, da) { return [m + Math.cos(a + da) * r, m + Math.sin(a + da) * r]; }
      var p0 = P(r0, -w * 0.15), p1 = P(L * 0.6, -w), tip = [m + ca * L, m + sa * L],
          p2 = P(L * 0.6, w), p3 = P(r0, w * 0.15);
      x.beginPath();
      x.moveTo(p0[0], p0[1]);
      x.quadraticCurveTo(P(L * 0.4, -w * 0.9)[0], P(L * 0.4, -w * 0.9)[1], p1[0], p1[1]);
      x.lineTo(tip[0], tip[1]);
      x.lineTo(p2[0], p2[1]);
      x.quadraticCurveTo(P(L * 0.4, w * 0.9)[0], P(L * 0.4, w * 0.9)[1], p3[0], p3[1]);
      x.closePath();
      x.fill();
    }
    return c;
  }

  // Vierzackiger Funkelstern des Covers: vier duenne, spitz zulaufende
  // Strahlen mit eingezogenen Flanken, die Diagonalen kuerzer, ein
  // heller Kern. Weiss in der Textur, die Farbe gibt der Shader.
  function texStar4() {
    var S = 128, c = makeCanvas(S, S), x = c.getContext('2d');
    var m = S / 2;
    // Zacken wie auf dem Cover: tief eingezogene Flanken (Kontrollpunkt
    // fast im Zentrum), also Nadeln statt Rautenblaetter; die senkrechte
    // Achse laenger als die waagerechte, oben bis an den Texturrand.
    function star(lens, wid, rot, alpha) {
      x.save();
      x.translate(m, m); x.rotate(rot);
      x.fillStyle = 'rgba(255,255,255,' + alpha + ')';
      x.beginPath();
      x.moveTo(lens[0], 0);
      for (var k = 0; k < 4; k++) {
        var b = k * Math.PI / 2 + Math.PI / 4, n = (k + 1) * Math.PI / 2, L = lens[(k + 1) % 4];
        x.quadraticCurveTo(Math.cos(b) * wid, Math.sin(b) * wid, Math.cos(n) * L, Math.sin(n) * L);
      }
      x.closePath();
      x.fill();
      x.restore();
    }
    // Abnahme: die erste Fassung (Flanke 0,13, Laenge 0,47) las sich als
    // gedrungene Raute. Jetzt ein Viertel der Flankenbreite, die Zacken
    // laufen bis an den Texturrand aus, die Diagonalen sind Haarstriche.
    var core = x.createRadialGradient(m, m, 0, m, m, S * 0.12);
    core.addColorStop(0, 'rgba(255,255,255,0.9)');
    core.addColorStop(0.35, 'rgba(255,255,255,0.25)');
    core.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = core;
    x.fillRect(0, 0, S, S);
    // weicher Schein unter den Nadeln, damit sie bei 1 px nicht zerfallen
    x.shadowColor = 'rgba(255,255,255,0.55)';
    x.shadowBlur = S * 0.035;
    // Reihenfolge: rechts, unten, links, oben in Canvas-y. Die Punkt-
    // Sprites lesen die Textur ueber gl_PointCoord (y nach unten) bei
    // flipY -- auf dem Schirm steht sie also KOPF: die lange Nadel
    // 'unten' im Canvas zeigt im Bild nach oben, wie auf dem Cover.
    star([S * 0.3, S * 0.5, S * 0.3, S * 0.4], S * 0.032, 0, 1);
    x.shadowBlur = 0;
    star([S * 0.17, S * 0.17, S * 0.17, S * 0.17], S * 0.012, Math.PI / 4, 0.3);
    return c;
  }

  // Mondlicht-Faecher: drei bis vier feine Bahnen, die oben im
  // Blaetterdach zusammenlaufen und nach unten auseinandergehen.
  // Lange Achse = Strahlrichtung (128), quer 32. Die Textur traegt
  // die volle Dynamik bis Alpha 1, die leise Spitze (~0.08) setzt
  // der Shader -- 8 Bit Alpha bei 0.08 gaebe nur 20 Stufen, also
  // Banding im Verlauf.
  function texFan(cfg, rnd) {
    // 128x32 reicht fuer weiches Licht (vorher 256x64: 3-11 ms Aufbau)
    var W = 128, H = 32, c = makeCanvas(W, H), x = c.getContext('2d');
    var img = x.createImageData(W, H), d = img.data;
    var col = new THREE.Color(cfg.frost);
    var bands = [];
    var nb = 3 + Math.floor(rnd() * 2);
    for (var b = 0; b < nb; b++) {
      bands.push({ off: (b / (nb - 1) - 0.5) * 0.5 + (rnd() - 0.5) * 0.06,
                   wid: 0.05 + rnd() * 0.06, a: 0.55 + rnd() * 0.45 });
    }
    for (var i = 0; i < W; i++) {
      var u = i / (W - 1);                       // 0 = oben (Kronendach), 1 = unten
      // Unten traegt das Licht laenger: dort stehen die Staemme, die
      // es anschneiden sollen (vorher bei Stammhoehe nur noch 38 %).
      var along = Math.min(1, u / 0.18) * Math.pow(1 - u, 0.45);
      var spread = 0.35 + 0.65 * u;              // unten weiter auseinander
      for (var j = 0; j < H; j++) {
        var v = j / (H - 1) - 0.5;
        var s = 0;
        for (var k = 0; k < bands.length; k++) {
          var q = (v - bands[k].off * spread) / (bands[k].wid * (0.6 + 0.6 * u));
          s += bands[k].a * Math.exp(-q * q);
        }
        var env = Math.exp(-Math.pow(v / 0.36, 4));   // weiche Aussenkante
        var a = Math.min(1, s * env * along);
        var p = (j * W + i) * 4;
        d[p] = col.r * 255; d[p + 1] = col.g * 255; d[p + 2] = col.b * 255;
        d[p + 3] = Math.round(a * 255);
      }
    }
    x.putImageData(img, 0, 0);
    return c;
  }

  // Shader fuer Strahlenkranz und Faecher: additives Licht mit einer
  // harten Garantie fuer die Textspalte. uXMax/uYMin sind Weltgrenzen
  // aus measureStage(); was rechts davon (Desktop) bzw. darunter
  // (Hochformat) laege, wird im Fragment ausgeblendet -- egal wie
  // Quad, Drehung oder Bahn stehen. uAngle dreht die Textur statt des
  // Quads, damit die Hochformat-Blende (uLow) unten bleibt.
  var GLOW_VS = [
    'varying vec2 vUv; varying vec2 vW;',
    'void main() {',
    '  vUv = uv;',
    '  vec4 wp = modelMatrix * vec4(position, 1.0);',
    '  vW = wp.xy;',
    '  gl_Position = projectionMatrix * viewMatrix * wp;',
    '}'
  ].join('\n');
  var GLOW_FS = [
    'uniform sampler2D map; uniform float uOpacity, uAngle, uXMax, uYMin, uLow;',
    'varying vec2 vUv; varying vec2 vW;',
    'void main() {',
    '  vec2 c = vUv - 0.5; float s = sin(uAngle), k = cos(uAngle);',
    '  vec4 tx = texture2D(map, vec2(k * c.x - s * c.y, s * c.x + k * c.y) + 0.5);',
    '  float a = tx.a * uOpacity;',
    '  a *= 1.0 - smoothstep(uXMax - 0.08, uXMax, vW.x);',
    '  a *= smoothstep(uYMin, uYMin + 0.08, vW.y);',
    '  a *= mix(1.0, smoothstep(0.4, 0.6, vUv.y), uLow);',
    '  gl_FragColor = vec4(tx.rgb, a);',
    '}'
  ].join('\n');
  function glowMaterial(map) {
    return new THREE.ShaderMaterial({
      uniforms: {
        map: { value: map }, uOpacity: { value: 0 }, uAngle: { value: 0 },
        uXMax: { value: 99 }, uYMin: { value: -99 }, uLow: { value: 0 }
      },
      vertexShader: GLOW_VS, fragmentShader: GLOW_FS,
      transparent: true, depthWrite: false, depthTest: false,
      blending: THREE.AdditiveBlending
    });
  }

  // Mond in EINEM Zeichenaufruf: Halo und Kernhof additiv, die
  // Scheibe deckend darueber. Das geht mit vormultipliziertem Alpha
  // (ONE, ONE_MINUS_SRC_ALPHA): Alpha 0 heisst addieren, Alpha 1
  // heisst ersetzen. Spart zwei Draw-Calls gegenueber drei Quads.
  var MOON_RD = 0.084;    // Scheibenradius in Quad-UV (Quad = Halo-Durchmesser)
  var MOON_FS = [
    'uniform sampler2D uHalo, uDisc; uniform float uHaloA, uKernA, uRd;',
    'uniform vec3 uKernCol;',
    'varying vec2 vUv;',
    'void main() {',
    '  vec2 c = vUv - 0.5; float r = length(c);',
    '  vec4 h = texture2D(uHalo, vUv);',
    '  vec2 du = c * (0.42 / uRd) + 0.5;',
    '  vec4 d = texture2D(uDisc, clamp(du, 0.0, 1.0));',
    '  d *= step(abs(du.x - 0.5), 0.5) * step(abs(du.y - 0.5), 0.5);',
    '  float x = clamp((r - uRd) / (0.42 * uRd), 0.0, 1.0);',
    '  float k = (1.0 - x) * (1.0 - x);',
    '  vec3 glow = h.rgb * h.a * uHaloA + uKernCol * k * uKernA;',
    '  gl_FragColor = vec4(d.rgb + glow * (1.0 - d.a), d.a);   // d ist vormultipliziert',
    '}'
  ].join('\n');

  // Sterne und Sternenstaub: eine Points-Wolke, zwei Sorten. kind 0 ist
  // ein runder Staubpunkt (Kreis aus gl_PointCoord), kind 1 der
  // vierzackige Funkelstern aus texStar4. x ist auf die halbe sichtbare
  // Breite normiert (uHalfW), damit der Himmel jedes Seitenverhaeltnis
  // fuellt, ohne einen Stern zu verzerren. Rechts von x=0 (Textspalte,
  // Mond) ist die Helligkeit gedeckelt.
  var STAR_VS = [
    'attribute vec4 aData;   // size px, phase, freq, kind',
    'attribute float aTint;',
    'uniform float uTime, uHalfW, uDpr;',
    'varying float vA; varying float vKind; varying float vTint;',
    'void main() {',
    '  vec3 p = position; p.x *= uHalfW;',
    '  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);',
    '  float tw = 0.5 + 0.5 * sin(uTime * aData.z + aData.y);',
    '  float glint = 0.0;',
    '  if (aData.w > 0.5 && aData.w < 1.5) glint = pow(max(0.0, sin(uTime * aData.z * 0.11 + aData.y * 1.7)), 90.0);',
    '  float a = mix(aData.w > 0.5 ? 0.5 : 0.45, 1.0, tw) * (1.0 + 0.7 * glint);',
    '  if (aData.w > 1.5) a = 0.09 + 0.02 * tw;',
    '  a *= mix(1.0, 0.35, smoothstep(-0.06, 0.06, p.x));',
    '  float sz = aData.x * uDpr * (1.0 + 0.7 * glint);',
    '  float minSz = 1.8;',
    '  if (aData.w < 0.5 && sz < minSz) { a *= (sz / minSz) * (sz / minSz); sz = minSz; }',
    '  gl_PointSize = aData.x > 0.0 ? sz : 0.0;',
    '  vA = a; vKind = aData.w; vTint = aTint;',
    '}'
  ].join('\n');
  var STAR_FS = [
    'uniform sampler2D uStar; uniform vec3 uCol, uCol2;',
    'varying float vA; varying float vKind; varying float vTint;',
    'void main() {',
    '  float a;',
    '  if (vKind > 1.5) { float d2 = length(gl_PointCoord - 0.5) * 2.0; a = max(0.0, exp(-d2 * d2 * 4.0) * (1.0 - d2)); }',
    '  else if (vKind > 0.5) a = texture2D(uStar, gl_PointCoord).a;',
    '  else { float d = length(gl_PointCoord - 0.5) * 2.0; a = 1.0 - smoothstep(0.45, 1.0, d); }',
    '  gl_FragColor = vec4(mix(uCol, uCol2, vTint), a * vA);',
    '}'
  ].join('\n');
  var STAR4_MAX = 8;
  // Die Plan-Werte (Spitze 0.09, Ruhe 0.05-0.07) sind als Deckkraft
  // EINES vollen Strahls gemeint; die Keile sind aber schmal und laufen
  // weich aus -- im Mittel traegt ein sichtbarer Strahlabschnitt nur
  // ~1/RAY_GAIN davon. Der Faktor stellt die Plan-Wirkung her.
  var RAY_GAIN = 4;
  // Faecher-Spitze: 0.08 laut Plan war im Bild nicht wahrnehmbar
  // (Differenzbild: ~6/255). 0.16 liest sich als Lichtbahn und bleibt
  // leiser als der Strahlenkranz.
  var FAN_PEAK = 0.16;

  /* ------------------------------------------------------------------ */
  /* der Wald des Spielplans                                */
  /* ------------------------------------------------------------------ */
  //
  // Vorlage ist der Spielplan (sylvans-bg.webp): dunkle, gegabelte
  // Laubbaeume, an den Astenden tuerkise Ahornbueschel um einen dunklen
  // Kern, Licht nur auf der mondzugewandten Seite. Alles hier wird beim
  // Aufbau EINMAL gebacken -- zur Laufzeit verschieben sich nur Quads.
  //
  // Werteleiter statt lerp: jede Tiefe hat ihren festen Ton, die
  // Leuchtbueschel verlieren nach hinten Saettigung Richtung Dunst.
  var WALD = {
    texW: 2048,          // Texturbreite der Waldebenen ...
    worldW: 2.7,         // ... auf fest 2,7 Welt: isotrop, ~1 Texel/px
    kulD: 1100,          // Kulissen-Texel je Welteinheit (~1,4 Texel/px bei 1600 px)
    // Kamm im Browser gegen den neuen Himmel (Paket himmel, Horizont
    // #3a3a8c) abgestimmt: #2e2b66 stand heller als der Himmel auf
    // Kammhoehe und drueckte den Fliesstext unter 4,5:1 -- jetzt knapp
    // dunkler als der Himmel, aber klar heller als die Mitte.
    ridge: '#28245c', mid: '#1f1842', front: '#120b23',
    core: '#0e2f45', shadow: '#0e3b52', midTone: '#1d7f9a', light: '#4cc3d4',
    mistHigh: '#b4a8e0', mistLow: '#7fcfd8',
    mistH: 0.3,          // Hoehe einer Nebelbank in Welt
    // Glimmwelle, Sekunden nach tStart. Der Funke des Eroeffnungsduells
    // zuendet sie (this.waveAt); waveKulisse gilt nur, wenn er ausbleibt,
    // die Mitte folgt der Kulisse um waveMid - waveKulisse.
    // Die Welle laeuft als Kamm von links nach rechts ueber die Bueschel
    // (sweep: Laufzeit ueber die ganze Quad-Breite, ~0,15 s je Bueschel),
    // jeder Punkt steigt in rise auf peak und faellt mit fall auf Ruhe.
    waveKulisse: 8, waveMid: 8.5, waveRamp: 0.8, waveFall: 1.4,
    waveRise: 0.35, sweepKul: 1.1, sweepMid: 0.9, peakKul: 1.6, peakMid: 1.5,
    // Schutzzonen in TEXTUR-Welt-x (Quads fest 2,7 breit, Textspalte auf
    // jedem Querformat bei 0,06..0,70): kein Laub, keine Staemme.
    zFoliage: [-0.04, 0.8], zMidTree: [-0.16, 0.9], zFrontTree: [-0.6, 0.92],
    zRidgeTree: [-0.1, 0.88], zRidgeLeaf: [-0.05, 0.84],
    // Hochformat: Welt-x, das die Ebenen in der Bildmitte zeigen (dort
    // stehen die Baeume, die Schutzzone liegt ausserhalb des Fensters)
    portraitCenter: -0.92
  };
  // Drei Ebenen von hinten nach vorn (die alte Vorderebene d=1 ersetzen
  // die Kulissen). foot = Fusslinie in Bruchteilen der Sichthoehe von
  // oben; top/below = Weltmass ueber/unter dem Fuss; sat = Saettigung
  // der Bueschel; depth = Rekursionstiefe der Aeste; moonK = Anteil der
  // Mondkante (hintere Ebenen 40 %).
  var WALD_LAYERS = [
    { d: 0.12, foot: 0.70, top: 0.36, below: 0.08, col: 'ridge', sat: 0.15, depth: 2, moonK: 0.4 },
    { d: 0.40, foot: 0.75, top: 0.58, below: 0.07, col: 'mid', sat: 0.45, depth: 3, moonK: 0.4, glow: true },
    { d: 0.68, foot: 0.80, top: 0.62, below: 0.22, col: 'front', sat: 1.0, depth: 4, moonK: 1 }
  ];

  // Angleich der neuen Bodennebel-Textur an die mittlere Deckung der
  // alten (nachgemessen im Harness), damit die Kontrastspitzen bleiben.
  var WALD_MIST_NORM = 1.0;

  function wMix(a, b, t) { return '#' + new THREE.Color(a).lerp(new THREE.Color(b), t).getHexString(); }
  function wGain(hex, f) {
    var c = new THREE.Color(hex);
    return '#' + new THREE.Color(Math.min(1, c.r * f), Math.min(1, c.g * f), Math.min(1, c.b * f)).getHexString();
  }
  // Buntheitsdeckel: Chroma (max-min) auf hoechstens maxC ziehen, bei
  // gleichem Farbton und Hellwert -- Tuerkis bleibt Tuerkis, liegt aber
  // sicher unter dem Kaufbutton (glimmer). Kein Neon.
  function wChroma(hex) {
    var c = new THREE.Color(hex);
    return Math.max(c.r, c.g, c.b) - Math.min(c.r, c.g, c.b);
  }
  function wCapChroma(hex, maxC) {
    var c = new THREE.Color(hex), mx = Math.max(c.r, c.g, c.b), ch = wChroma(hex);
    if (ch <= maxC) return hex;
    var k = maxC / ch;
    return '#' + new THREE.Color(mx - (mx - c.r) * k, mx - (mx - c.g) * k, mx - (mx - c.b) * k).getHexString();
  }
  // Periodisches Rauschen ueber u in [0,1): ganzzahlige Sinusse, damit
  // die Waldtexturen auf Ultrabreit nahtlos kacheln.
  function wNoise(rnd, n) {
    var k = [], p = [], a = [], s = 0;
    for (var i = 0; i < n; i++) {
      k.push(Math.round(2 + i * 2.6 + rnd() * 2));
      p.push(rnd() * 6.283);
      a.push(1 / Math.pow(k[i], 0.8));
      s += a[i];
    }
    return function (u) {
      var v = 0;
      for (var j = 0; j < n; j++) v += a[j] * Math.sin(6.2831853 * k[j] * u + p[j]);
      return v / s;
    };
  }

  // Ein Ahornblatt, EINMAL je Ton gebacken und danach nur gestempelt:
  // fuenf Lappen mit Zaehnchen, Mittelader 30 % heller. 32 statt 24 px,
  // damit es auch auf den nahen Kulissen ohne Hochskalieren scharf
  // bleibt. Gebacken als Streifen aus 12 Drehungen: das Stempeln ist
  // dann ein reines drawImage ohne Transformwechsel (der Aufbau bleibt
  // so unter dem Budget, bei tausenden Blaettern).
  var LEAF_S = 32, LEAF_ROT = 12;
  function texLeafStamp(color) {
    var S = LEAF_S, c = makeCanvas(S * LEAF_ROT, S), x = c.getContext('2d');
    var cx = S / 2, cy = S * 0.52, R = S * 0.42;
    var rad = [0.62, 0.86, 1, 0.86, 0.62];
    function pt(a, r) { return [cx + Math.cos(a) * r, cy + Math.sin(a) * r]; }
    var pts = [pt(Math.PI / 2 - 0.5, R * 0.26)], k, i;
    for (k = 2; k >= -2; k--) {
      var th = -Math.PI / 2 + k * 0.98, r = rad[k + 2] * R;
      pts.push(pt(th + 0.3, r * 0.74), pt(th + 0.14, r * 0.86), pt(th, r),
               pt(th - 0.14, r * 0.86), pt(th - 0.3, r * 0.74));
      if (k > -2) pts.push(pt(th - 0.49, R * 0.5));
    }
    pts.push(pt(Math.PI / 2 + 0.5, R * 0.26));
    var rib = wGain(color, 1.3);
    for (var q = 0; q < LEAF_ROT; q++) {
      x.setTransform(1, 0, 0, 1, 0, 0);
      x.translate(q * S + S / 2, S / 2);
      x.rotate(q / LEAF_ROT * 6.2831853);
      x.translate(-S / 2, -S / 2);
      x.fillStyle = color;
      x.beginPath();
      x.moveTo(pts[0][0], pts[0][1]);
      for (i = 1; i < pts.length; i++) x.lineTo(pts[i][0], pts[i][1]);
      x.closePath();
      x.fill();
      x.strokeStyle = rib;
      x.lineWidth = 1;
      x.lineCap = 'round';
      x.beginPath();
      for (k = -2; k <= 2; k++) {
        var tip = pt(-Math.PI / 2 + k * 0.98, rad[k + 2] * R * 0.8);
        x.moveTo(cx, cy); x.lineTo(tip[0], tip[1]);
      }
      x.moveTo(cx, cy); x.lineTo(cx, cy + R * 0.6);
      x.stroke();
    }
    return c;
  }

  // Aquarell-Granulation: periodisches Value-Noise aus drei Oktaven,
  // weiss bzw. schwarz mit |n| als Alpha. Per source-atop gelegt hellt
  // und dunkelt es nur, wo schon Farbe liegt -- das Papierkorn des
  // gemalten Spielplans, ohne Rauschen in den Himmel zu streuen.
  function texGrainNoise(rnd) {
    var S = 128, c = makeCanvas(S, S), x = c.getContext('2d');
    var img = x.createImageData(S, S), d = img.data;
    var oct = [[32, 0.5], [16, 0.3], [8, 0.2]], grids = [], o, i;
    for (o = 0; o < oct.length; o++) {
      var n = S / oct[o][0], g = new Float32Array(n * n);
      for (i = 0; i < g.length; i++) g[i] = rnd() * 2 - 1;
      grids.push(g);
    }
    var vals = new Float32Array(S * S), mx = 1e-6;
    for (var y = 0; y < S; y++) {
      for (var X = 0; X < S; X++) {
        var v = 0;
        for (o = 0; o < oct.length; o++) {
          var cell = oct[o][0], n2 = S / cell, gg = grids[o];
          var fx = X / cell, fy = y / cell, ix = Math.floor(fx), iy = Math.floor(fy);
          var tx = fx - ix, ty = fy - iy;
          tx = tx * tx * (3 - 2 * tx); ty = ty * ty * (3 - 2 * ty);
          var x0 = ix % n2, x1 = (ix + 1) % n2, y0 = iy % n2, y1 = (iy + 1) % n2;
          var a = gg[y0 * n2 + x0], b = gg[y0 * n2 + x1], cc = gg[y1 * n2 + x0], dd = gg[y1 * n2 + x1];
          v += oct[o][1] * ((a + (b - a) * tx) * (1 - ty) + (cc + (dd - cc) * tx) * ty);
        }
        vals[y * S + X] = v;
        if (Math.abs(v) > mx) mx = Math.abs(v);
      }
    }
    for (i = 0; i < S * S; i++) {
      var w = vals[i] / mx, p = i * 4, c8 = w > 0 ? 255 : 0;
      d[p] = d[p + 1] = d[p + 2] = c8;
      d[p + 3] = Math.round(Math.abs(w) * 255);
    }
    x.putImageData(img, 0, 0);
    return c;
  }
  function wGranulate(x, noise, amp, w, h) {
    x.save();
    x.setTransform(1, 0, 0, 1, 0, 0);
    x.globalCompositeOperation = 'source-atop';
    x.globalAlpha = amp;
    x.fillStyle = x.createPattern(noise, 'repeat');
    x.fillRect(0, 0, w, h);
    x.restore();
  }

  // Der Pinsel fuer Baeume, Bueschel und Unterholz -- gemeinsam fuer
  // Waldebenen und Kulissen. x: Zielkontext, gx: Glimm-Maske in halber
  // Aufloesung (oder null). P: fill, frost, tones, stamps [Schatten,
  // Mittel, Licht], glowStamp, allow(px) 0/1/2, moonEdge(px), leaf
  // [min,max] Blattgroesse, rc [min,max] Bueschelradius, maxY(px)
  // optional (Kulissen: hoechste erlaubte Unterkante).
  function wBrush(x, gx, P) {
    // td: Dekor-Zufall (Rinde, Mondkante, Fuss), getrennt vom
    // Struktur-Zufall der Aeste -- sonst liefe der Trockenlauf auseinander.
    var B = { ox: 0, dry: false, box: null, td: P.td };
    B.offset = function (dx) {
      B.ox = dx;
      x.setTransform(1, 0, 0, 1, dx, 0);
      if (gx) gx.setTransform(0.5, 0, 0, 0.5, dx * 0.5, 0);
    };
    // Die Glimm-Maske liegt in halber Aufloesung: ihre Transformation
    // muss von Anfang an stehen, nicht erst nach dem ersten Versatz.
    B.offset(0);
    // Stempel aus dem Drehungsstreifen; die Grund-Transformation (nur
    // der Kachelversatz) bleibt stehen.
    // noGlow: Schattenlaub glimmt nicht mit -- nur Mittel- und Lichtstufe
    // tragen die Laubwelle.
    B.stamp = function (img, px, py, s, rot, noGlow) {
      var r = ((Math.round(rot / 6.2831853 * LEAF_ROT) % LEAF_ROT) + LEAF_ROT) % LEAF_ROT;
      x.drawImage(img, r * LEAF_S, 0, LEAF_S, LEAF_S, px - s / 2, py - s / 2, s, s);
      if (gx && !noGlow) gx.drawImage(P.glowStamp, r * LEAF_S, 0, LEAF_S, LEAF_S, px - s / 2, py - s / 2, s, s);
    };
    // Ein Astsegment: gefuelltes Trapez mit Bogenkanten (unten w, oben
    // w*0.62), Gelenk-Kreis am Knoten, 2-3 Kinder. Zwei Kinder gabeln
    // sich als Y wie im Spielplan, drei setzen den Leittrieb fort.
    B.branch = function (tr, x0, y0, ang, len, w, depth, tips, trunk) {
      var ca = Math.cos(ang), sa = Math.sin(ang);
      var ex = x0 + ca * len, ey = y0 + sa * len;
      if (P.maxY) {
        var lim = P.maxY(ex) - w * 0.4;
        if (ey > lim) { ey = lim; ang = Math.atan2(ey - y0, ex - x0); }
      }
      var nx = -Math.sin(ang), ny = Math.cos(ang);
      var bend = (tr() - 0.5) * (trunk ? (P.trunkBend != null ? P.trunkBend : 0.1) : 0.3) * len;
      var mx = (x0 + ex) / 2 + nx * bend, my = (y0 + ey) / 2 + ny * bend;
      var w2 = w * 0.62, wm = (w + w2) / 4;
      if (B.dry) {
        // Trockenlauf: nur Ausdehnung messen, nichts malen, keinen
        // Dekor-Zufall verbrauchen -- die Struktur bleibt identisch.
        if (ey - w2 < B.box.top) B.box.top = ey - w2;
        if (ex < B.box.l) B.box.l = ex;
        if (ex > B.box.r) B.box.r = ex;
      } else {
      x.fillStyle = P.fill;
      x.beginPath();
      x.moveTo(x0 - nx * w / 2, y0 - ny * w / 2);
      x.quadraticCurveTo(mx - nx * wm, my - ny * wm, ex - nx * w2 / 2, ey - ny * w2 / 2);
      x.lineTo(ex + nx * w2 / 2, ey + ny * w2 / 2);
      x.quadraticCurveTo(mx + nx * wm, my + ny * wm, x0 + nx * w / 2, y0 + ny * w / 2);
      x.closePath();
      x.fill();
      x.beginPath(); x.arc(ex, ey, w2 / 2, 0, 7); x.fill();
      var td = B.td;
      if (trunk && P.bark) B.bark(td, x0, y0, mx, my, ex, ey, nx, ny, w, w2);
      // Mondkante: 1-1,5 px Frost auf der +x-Flanke, jedes zweite Segment
      var s = P.moonEdge(x0);
      if (s > 0.01 && td() < 0.5) {
        var sg = nx >= 0 ? 1 : -1, j = (td() - 0.5) * 0.8, ins = 0.7;
        x.strokeStyle = rgba(P.frost, s * (0.75 + td() * 0.25));
        x.lineWidth = 1 + td() * 0.5;
        x.beginPath();
        x.moveTo(x0 + sg * nx * (w / 2 - ins) + j, y0 + sg * ny * (w / 2 - ins));
        x.quadraticCurveTo(mx + sg * nx * (wm - ins), my + sg * ny * (wm - ins),
                           ex + sg * nx * (w2 / 2 - ins), ey + sg * ny * (w2 / 2 - ins));
        x.stroke();
      }
      }
      if (depth <= 0) { tips.push([ex, ey, ang, w2, 0]); return; }
      if (depth === 1) tips.push([ex, ey, ang, w2, 1]);
      if (depth === 2 && P.deepTips) tips.push([ex, ey, ang, w2, 2]);
      var n = tr() < 0.4 ? 3 : 2;
      var side = tr() < 0.5 ? -1 : 1;
      // Spreizung und Aufwaertszug: Baeume verzweigen steiler (hohe,
      // runde Kronen wie im Spielplan statt Schirmen), die Zweige der
      // Kulissen behalten die weite Gabel.
      var sp = P.spread || [0.35, 0.35], pull = P.pull != null ? P.pull : 0.12;
      for (var i = 0; i < n; i++) {
        var a, cw;
        if (n === 3 && i === 0) { a = ang + (tr() - 0.5) * 0.3; cw = w2 * 0.9; }
        else { a = ang + side * (sp[0] + tr() * sp[1]); side = -side; cw = w2 * (n === 3 ? 0.7 : 0.82); }
        a += (-Math.PI / 2 - a) * pull;          // nach oben gezogen
        B.branch(tr, ex, ey, a, len * (0.68 + tr() * 0.1), cw, depth - 1, tips, false);
      }
    };
    // Rinde: Faserstriche entlang der Stammkurve
    B.bark = function (tr, x0, y0, mx, my, ex, ey, nx, ny, w, w2) {
      var n = Math.round((20 + Math.floor(tr() * 21)) * (P.barkK || 1));
      for (var i = 0; i < n; i++) {
        var f = (tr() - 0.5) * 0.8;
        var t0 = tr() * 0.85, t1 = Math.min(1, t0 + 0.08 + tr() * 0.3);
        x.lineWidth = 0.8 + tr() * 0.8;
        x.strokeStyle = tr() < 0.6 ? 'rgba(0,0,0,' + (0.12 + tr() * 0.14).toFixed(3) + ')'
                                   : rgba(P.frost, 0.025 + tr() * 0.035);
        x.beginPath();
        for (var k = 0; k <= 5; k++) {
          var t = t0 + (t1 - t0) * k / 5, u = 1 - t;
          var bx = u * u * x0 + 2 * u * t * mx + t * t * ex;
          var by = u * u * y0 + 2 * u * t * my + t * t * ey;
          var ww = (w + (w2 - w) * t) * f + Math.sin(t * 11 + i) * 0.7;
          if (k === 0) x.moveTo(bx + nx * ww, by + ny * ww);
          else x.lineTo(bx + nx * ww, by + ny * ww);
        }
        x.stroke();
      }
    };
    // Stammfuss: 2,2-fach ausgestellt, mit 2-3 Wurzelboegen
    B.foot = function (tr, tx, fy, w) {
      var fw = w * 1.1;
      x.fillStyle = P.fill;
      x.beginPath();
      x.moveTo(tx - fw, fy + 3);
      x.quadraticCurveTo(tx - w * 0.5, fy - w * 0.1, tx - w * 0.48, fy - w * 1.4);
      x.lineTo(tx + w * 0.48, fy - w * 1.4);
      x.quadraticCurveTo(tx + w * 0.5, fy - w * 0.1, tx + fw, fy + 3);
      x.closePath();
      x.fill();
      // Wurzeln als gekruemmte, sich verjuengende Bogen: oben gewoelbt
      // aus der Flanke, die Spitze taucht in den Boden -- keine geraden
      // Keile wie ein Staenderfuss.
      var nr = 2 + (tr() < 0.5 ? 1 : 0);
      x.fillStyle = P.fill;
      for (var r = 0; r < nr; r++) {
        var s = r % 2 ? -1 : 1, L = w * (1.0 + tr() * 1.3), th = w * (0.22 + tr() * 0.1);
        var bx = tx + s * w * 0.42, by = fy - w * (0.35 + tr() * 0.25);
        var ex = tx + s * (w * 0.55 + L), ey = fy + 2 + tr() * 3;
        x.beginPath();
        x.moveTo(bx, by - th * 0.5);
        x.quadraticCurveTo(tx + s * (w * 0.5 + L * 0.45), by - th * 0.2, ex, ey);
        x.quadraticCurveTo(tx + s * (w * 0.45 + L * 0.4), fy + 1, bx - s * w * 0.1, fy + 3);
        x.closePath();
        x.fill();
      }
    };
    // Laubbueschel: dunkler Kern (das Kegelkern-Prinzip aus La Bete),
    // darueber dicht gestempeltes Laub in drei Tonstufen. Licht nur im
    // mondseitigen oberen rechten Viertel und nur, wo allow() es zulaesst.
    // Die Stempelzahl folgt der Flaeche (gut 2,5-fache Deckung), der Kern
    // ist kleiner als die Stempelwolke und verschwindet ganz darunter --
    // dichtes Laub statt Wattebausch mit Konfetti. cap: hoechste Tonstufe
    // (die Schattenfuellung der Kulisse nutzt 0).
    B.clusterDirect = function (tr, cx, cy, rc, allow) {
      x.fillStyle = P.tones.coreDark || P.tones.core;
      x.beginPath(); x.ellipse(cx, cy, rc * 0.7, rc * 0.6, 0, 0, 7); x.fill();
      var lm = (P.leaf[0] + P.leaf[1]) / 2;
      var n = Math.round(clamp(2.6 * 2.9 * rc * rc / (lm * lm * 0.55), 28, 150));
      var lists = [[], [], []];
      for (var i = 0; i < n; i++) {
        var a = tr() * 6.283, rr = Math.sqrt(tr()) * rc;
        var sx = cx + Math.cos(a) * rr * 1.02, sy = cy + Math.sin(a) * rr * 0.9;
        var qx = (sx - cx) / rc, qy = (sy - cy) / rc;
        var score = qx * 0.7 - qy * 0.9 + (tr() - 0.5) * 0.6;
        var tone = (qx > 0 && qy < 0.05 && score > 0.55) ? 2 : (score > -0.3 ? 1 : 0);
        if (tone > allow) tone = allow;
        // +-25 % Groessenstreuung um die Tiefenstufe
        lists[tone].push([sx, sy, lm * (0.75 + tr() * 0.5), (tr() - 0.5) * 1.8 + qx * 0.7]);
      }
      for (var t = 0; t < 3; t++) {
        var img = P.stamps[t];
        for (i = 0; i < lists[t].length; i++) {
          var L = lists[t][i];
          B.stamp(img, L[0], L[1], L[2], L[3], t === 0);
        }
      }
      B.offset(B.ox);
    };
    // Bueschel-Vorlagen: je Tonstufe, Groessenband und vier Varianten
    // EINMAL voll gestempelt (samt Glimm-Maske in halber Aufloesung),
    // danach wird jedes Bueschel als ein einziges drawImage gesetzt --
    // tausende Blattstempel pro Ebene schrumpfen auf ein paar hundert
    // Kopien, und der Aufbau bleibt unter dem Budget. Die Mondseite
    // liegt in jeder Variante oben rechts, Spiegeln entfaellt also.
    var sprites = {}, sprTr = mulberry32(Math.floor(P.td() * 4294967296));
    B.sprite = function (allow, rc, v) {
      var band = rc <= P.rc[1] * 0.72 ? (rc <= P.rc[1] * 0.5 ? 2 : 1) : 0;
      var ref = P.rc[1] * [1, 0.72, 0.5][band];
      var key = allow + '/' + band + '/' + v;
      var sp = sprites[key];
      if (!sp) {
        var S = Math.ceil(ref * 2.5 + P.leaf[1] * 1.6);
        var sc = makeCanvas(S, S), sg = allow > 0 && gx ? makeCanvas(Math.ceil(S / 2), Math.ceil(S / 2)) : null;
        var Ps = Object.create(P);
        Ps.allow = function () { return allow; };
        var sub = wBrush(sc.getContext('2d'), sg ? sg.getContext('2d') : null, Ps);
        sub.offset(0);
        sub.clusterDirect(sprTr, S / 2, S / 2, ref, allow);
        sp = sprites[key] = { c: sc, g: sg, S: S, ref: ref };
      }
      return sp;
    };
    B.cluster = function (tr, cx, cy, rc, cap) {
      var allow = P.allow(cx);
      if (allow < 0) return;                       // Schutzzone: kein Laub
      if (cap != null && cap < allow) allow = cap;
      var sp = B.sprite(allow, rc, Math.floor(tr() * 4));
      var s = sp.S * rc / sp.ref;
      x.drawImage(sp.c, cx - s / 2, cy - s / 2, s, s);
      if (sp.g) gx.drawImage(sp.g, cx - s / 2, cy - s / 2, s, s);
    };
    // Grosser Laubklumpen: 3-5 Teilbueschel als Kuppel, jedes mit eigenem
    // dunklem Kern und eigener Mondseite -- die gelappten Kronenballen
    // des Spielplans statt eines einzelnen Flecks.
    B.clump = function (tr, cx, cy, R, cap) {
      if (R < 34) { B.cluster(tr, cx, cy, R, cap); return; }
      var n = R > 60 ? 5 : R > 45 ? 4 : 3, sub = [];
      for (var i = 0; i < n; i++) {
        var a = Math.PI * (i + 0.5) / n + (tr() - 0.5) * 0.4;
        var rr = R * (0.5 + tr() * 0.14);
        sub.push([cx + Math.cos(a) * R * 0.5, cy + 0.15 * R - Math.sin(a) * R * 0.42, rr]);
      }
      sub.push([cx + (tr() - 0.5) * R * 0.2, cy - R * 0.32, R * 0.52]);
      sub.sort(function (p, q) { return q[1] - p[1]; });
      for (i = 0; i < sub.length; i++) B.cluster(tr, sub[i][0], sub[i][1], sub[i][2], cap);
    };
    // Farnwedel: eingerollte Mittelrippe mit 12-18 Fiederpaaren
    B.fern = function (tr, fx, fy, len) {
      var a = -Math.PI / 2 + (tr() - 0.5) * 1.5;
      var curl = (a < -Math.PI / 2 ? -1 : 1) * (0.5 + tr() * 0.6);
      var n = 12 + Math.floor(tr() * 7), step = len / n;
      var px = fx, py = fy, pts = [];
      x.strokeStyle = P.fill;
      x.lineCap = 'round';
      x.lineWidth = Math.max(1, len * 0.035);
      x.beginPath();
      x.moveTo(px, py);
      for (var i = 0; i < n; i++) {
        a += curl / n;
        px += Math.cos(a) * step; py += Math.sin(a) * step;
        x.lineTo(px, py);
        pts.push([px, py, a, i]);
      }
      x.stroke();
      x.lineWidth = Math.max(0.9, len * 0.022);
      x.beginPath();
      for (i = 0; i < pts.length; i++) {
        var p = pts[i], fl = len * 0.2 * (1 - i / n) + 1.5;
        for (var s = -1; s <= 1; s += 2) {
          var fa = p[2] + s * 1.15;
          x.moveTo(p[0], p[1]);
          x.quadraticCurveTo(p[0] + Math.cos(fa) * fl * 0.6, p[1] + Math.sin(fa) * fl * 0.6,
                             p[0] + Math.cos(fa + s * 0.35) * fl, p[1] + Math.sin(fa + s * 0.35) * fl + fl * 0.3);
        }
      }
      x.stroke();
    };
    // Gezeichneter Ast oder Stamm fuer die Kulissen: Catmull-Rom durch
    // Stuetzpunkte, verjuengt von w0 auf w1, mit Rinde und einer Mondkante
    // auf der Flanke, die zum Mond schaut. Liefert at(t) -> [x, y, Winkel,
    // Breite] (t ueber die Bogenlaenge), um Zweige und Bueschel anzusetzen.
    B.limb = function (pts, w0, w1, bark) {
      var S = [], i, j, n = pts.length;
      function pk(k) { return pts[Math.max(0, Math.min(n - 1, k))]; }
      for (i = 0; i < n - 1; i++) {
        var p0 = pk(i - 1), p1 = pk(i), p2 = pk(i + 1), p3 = pk(i + 2);
        for (j = 0; j < 12; j++) {
          var t = j / 12, t2 = t * t, t3 = t2 * t, q = [];
          for (var d = 0; d < 2; d++) {
            q.push(0.5 * (2 * p1[d] + (-p0[d] + p2[d]) * t + (2 * p0[d] - 5 * p1[d] + 4 * p2[d] - p3[d]) * t2 +
                          (-p0[d] + 3 * p1[d] - 3 * p2[d] + p3[d]) * t3));
          }
          S.push(q);
        }
      }
      S.push(pts[n - 1]);
      var m = S.length, L = [0], N = [];
      for (i = 1; i < m; i++) L.push(L[i - 1] + Math.hypot(S[i][0] - S[i - 1][0], S[i][1] - S[i - 1][1]));
      var tot = L[m - 1] || 1;
      for (i = 0; i < m; i++) {
        var a = S[Math.min(m - 1, i + 1)], b = S[Math.max(0, i - 1)];
        var dx = a[0] - b[0], dy = a[1] - b[1], dl = Math.hypot(dx, dy) || 1;
        N.push([-dy / dl, dx / dl, (w0 + (w1 - w0) * L[i] / tot) / 2, Math.atan2(dy, dx)]);
      }
      x.fillStyle = P.fill;
      x.beginPath();
      x.moveTo(S[0][0] - N[0][0] * N[0][2], S[0][1] - N[0][1] * N[0][2]);
      for (i = 1; i < m; i++) x.lineTo(S[i][0] - N[i][0] * N[i][2], S[i][1] - N[i][1] * N[i][2]);
      for (i = m - 1; i >= 0; i--) x.lineTo(S[i][0] + N[i][0] * N[i][2], S[i][1] + N[i][1] * N[i][2]);
      x.closePath();
      x.fill();
      x.beginPath(); x.arc(S[m - 1][0], S[m - 1][1], w1 / 2, 0, 7); x.fill();
      var td = B.td, k;
      if (bark) {
        // Klein skaliert (hochkant) duenner gesaet: sonst liest sich die
        // Rinde als senkrechtes Streifenmuster statt als Faser.
        var nb = Math.round((20 + Math.floor(td() * 21)) * (P.barkK || 1));
        for (k = 0; k < nb; k++) {
          var f = (td() - 0.5) * 1.6, i0 = Math.floor(td() * (m - 4)), i1 = Math.min(m - 1, i0 + 3 + Math.floor(td() * m * 0.3));
          x.lineWidth = 0.8 + td() * 0.9;
          x.strokeStyle = td() < 0.6 ? 'rgba(0,0,0,' + (0.14 + td() * 0.14).toFixed(3) + ')'
                                     : rgba(P.frost, 0.025 + td() * 0.035);
          x.beginPath();
          for (i = i0; i <= i1; i++) {
            var ww = N[i][2] * f + Math.sin(i * 0.9 + k) * 0.7;
            if (i === i0) x.moveTo(S[i][0] + N[i][0] * ww, S[i][1] + N[i][1] * ww);
            else x.lineTo(S[i][0] + N[i][0] * ww, S[i][1] + N[i][1] * ww);
          }
          x.stroke();
        }
      }
      // Mondkante abschnittsweise (jeder zweite Abschnitt), auf der Flanke,
      // deren Normale zum Mond zeigt
      for (k = 0; k + 2 < m; k += 7) {
        var e = Math.min(m - 1, k + 7), s = P.moonEdge(S[k][0]);
        if (s <= 0.01 || td() > 0.6) continue;
        var md = P.moonDir ? P.moonDir(S[k][0], S[k][1]) : [1, -0.4];
        var sg = N[k][0] * md[0] + N[k][1] * md[1] >= 0 ? 1 : -1;
        x.strokeStyle = rgba(P.frost, s * (0.75 + td() * 0.25));
        x.lineWidth = 1 + td() * 0.5;
        x.beginPath();
        for (i = k; i <= e; i++) {
          var o = sg * (N[i][2] - 0.7);
          if (i === k) x.moveTo(S[i][0] + N[i][0] * o, S[i][1] + N[i][1] * o);
          else x.lineTo(S[i][0] + N[i][0] * o, S[i][1] + N[i][1] * o);
        }
        x.stroke();
      }
      return function (t) {
        var target = clamp(t, 0, 1) * tot, idx = 0;
        while (idx < m - 1 && L[idx + 1] < target) idx++;
        return [S[idx][0], S[idx][1], N[idx][3], N[idx][2] * 2];
      };
    };
    // Moospolster am Fuss
    B.moss = function (tr, mx, my, r) {
      x.fillStyle = P.fill;
      x.beginPath();
      x.ellipse(mx, my, r, r * (0.38 + tr() * 0.15), 0, Math.PI, 0);
      x.fill();
      for (var k = 0; k < 3; k++) {
        var bx = mx + (tr() - 0.5) * r * 1.3, br2 = r * (0.3 + tr() * 0.3);
        x.beginPath(); x.ellipse(bx, my - r * 0.2, br2, br2 * 0.7, 0, Math.PI, 0); x.fill();
      }
    };
    return B;
  }

  // Glimmen backen: die Laubmaske (Stempel in glimmer, halbe Aufloesung)
  // mit Alpha 0,35 plus ein Hof, der ueber eine Viertel-Kopie und
  // bilineares Hochziehen entsteht -- dieselbe weiche Kante wie ein
  // 12-px-shadowBlur, aber ohne dessen Kosten beim Aufbau.
  function wGlowBake(mask) {
    var w = mask.width, h = mask.height;
    var q = makeCanvas(Math.max(1, Math.round(w / 4)), Math.max(1, Math.round(h / 4)));
    var qx = q.getContext('2d');
    qx.imageSmoothingEnabled = true;
    qx.drawImage(mask, 0, 0, q.width, q.height);
    var g = makeCanvas(w, h), gx = g.getContext('2d');
    gx.imageSmoothingEnabled = true;
    gx.globalAlpha = 0.35;
    gx.drawImage(q, 0, 0, w, h);
    gx.drawImage(q, -2, -2, w + 4, h + 4);
    gx.drawImage(mask, 0, 0);
    return g;
  }

  // Eine Waldebene als nahtlos kachelnde Silhouette (2048 x isotrop).
  // Die Schutzzonen haengen an der TEXTUR-Weltkoordinate, nicht am
  // Seitenverhaeltnis beim Aufbau: die Textspalte steht auf jedem
  // Querformat bei Welt-x 0,06..0,70 (nachgemessen von 1024 bis 2560 px
  // Breite), und die Ebenen-Quads sind fest 2,7 breit -- so bleibt der
  // Grund hinter dem Text auch nach Drehen oder Fenster-Ziehen ruhig.
  function texForest(rnd, cfg, spec, view, kit) {
    var W = WALD.texW, D = W / WALD.worldW;
    var H = Math.round(D * (spec.top + spec.below));
    var footY = Math.round(H - spec.below * D);
    var c = makeCanvas(W, H), x = c.getContext('2d');
    var gm = null, gx = null;
    if (spec.glow) { gm = makeCanvas(W / 2, Math.ceil(H / 2)); gx = gm.getContext('2d'); }
    var fill = WALD[spec.col];
    var back = spec.col === 'ridge', front = spec.col === 'front';
    function wrapPx(px) { return ((px % W) + W) % W; }
    function worldX(px) { return (wrapPx(px) / W - 0.5) * WALD.worldW; }
    function inZ(px, z) { var w = worldX(px); return w > z[0] && w < z[1]; }
    // Blattgroesse nach Tiefe: hinten ~45 %, Mitte ~65 % der vorderen
    var P = {
      fill: fill, frost: cfg.frost, bark: front,
      tones: kit.tones(spec.sat), stamps: kit.stamps(spec.sat), glowStamp: kit.glowStamp,
      allow: function (px) { return view.allowW(worldX(px)); },
      moonEdge: function (px) { return view.moonEdgeW(worldX(px)) * spec.moonK; },
      leaf: back ? [5, 8.5] : front ? [12, 18] : [7.5, 12.5],
      rc: back ? [8, 13] : front ? [24, 40] : [21, 31],
      spread: [0.22, 0.28], pull: 0.22, deepTips: !back,
      td: mulberry32(Math.floor(rnd() * 4294967296))
    };
    var B = wBrush(x, gx, P);
    var gnd = wNoise(rnd, 4);

    // Ein Objekt, das ueber die Kachelkante ragt, wird auf der Gegenseite
    // ein zweites Mal gemalt -- mit demselben Teilzufall, also identisch.
    function wrapped(px, half, fn) {
      var seed = Math.floor(rnd() * 4294967296);
      B.offset(0); fn(seed, px);
      if (px - half < 0) { B.offset(W); fn(seed, px); }
      if (px + half > W) { B.offset(-W); fn(seed, px); }
      B.offset(0);
    }
    // Baum mit Trockenlauf: erst die Aststruktur nur vermessen, dann so
    // skaliert malen, dass die Krone samt Buescheln genau th hoch wird --
    // nie ueber die Texturkante (abgeschnittene Kronen), nie als Stummel.
    function tree(px, th, depth, clusterP) {
      th = Math.min(th, footY - 6);
      var rcMax = P.rc[1];
      wrapped(px, th * 0.75, function (seed, tx) {
        var sum = 0, j;
        for (j = 0; j <= depth; j++) sum += Math.pow(0.72, j);
        var lean = (mulberry32(seed + 1)() - 0.5) * 0.14;
        function grow(tr, k, tips) {
          var w0 = th * (back ? 0.05 : front ? 0.058 : 0.05) * Math.sqrt(k);
          B.branch(tr, tx, footY + 2, -Math.PI / 2 + lean, th / sum * 0.9 * k, w0, depth, tips, true);
          return w0;
        }
        B.dry = true;
        B.box = { top: 1e9, l: 1e9, r: -1e9 };
        grow(mulberry32(seed), 1, []);
        B.dry = false;
        var k = clamp((th - rcMax * 1.9) / Math.max(1, footY - B.box.top), 0.3, 2.2);
        B.td = mulberry32(seed ^ 0x5bd1e995);
        var tr = mulberry32(seed), tips = [];
        var w0 = grow(tr, k, tips);
        if (!back) B.foot(B.td, tx, footY, w0);
        // Bueschel an den Astenden, kleinere an den vorletzten Knoten;
        // an den Enden zusaetzlich ein zweites, kleineres darueber -- die
        // Krone baut sich in die Hoehe (Aspekt ~1:1 wie die runden
        // Laubmassen des Spielplans). Von unten nach oben gemalt, damit
        // die oberen die unteren decken.
        var cl = [];
        for (j = 0; j < tips.length; j++) {
          // Laub auch an den inneren Knoten: die Krone fuellt sich nach
          // unten zu einer geschlossenen runden Masse, statt einzelne
          // Bommel auf kahlen Stangen zu tragen.
          var tp = tips[j], inner = tp[4] > 0;
          if (tr() > (tp[4] === 2 ? clusterP * 0.6 : inner ? clusterP * 0.8 : clusterP)) continue;
          var rc = (P.rc[0] + tr() * (P.rc[1] - P.rc[0])) * (tp[4] === 2 ? 0.85 : inner ? 0.9 : 1);
          var ccx = tp[0] + Math.cos(tp[2]) * rc * 0.3, ccy = tp[1] - rc * 0.35;
          cl.push([ccx, ccy, rc]);
          if (!inner && tr() < 0.75) cl.push([ccx + (tr() - 0.5) * rc * 0.5, ccy - rc * (0.7 + tr() * 0.2), rc * (0.72 + tr() * 0.12)]);
        }
        cl.sort(function (a, b) { return b[1] - a[1]; });
        for (j = 0; j < cl.length; j++) B.cluster(tr, cl[j][0], cl[j][1], cl[j][2]);
      });
    }

    // 1. Boden ab der Fusslinie, mit flachen Erdbuckeln statt einer
    // Linealkante (eigener Teilstrom, die Baumstellung bleibt unberuehrt)
    var mound = wNoise(mulberry32(Math.round(spec.d * 1000) + 77), 3);
    var mAmp = front ? D * 0.022 : back ? 0 : D * 0.012;
    x.fillStyle = fill;
    x.beginPath();
    x.moveTo(0, H);
    for (var gx0 = 0; gx0 <= W; gx0 += 12) {
      x.lineTo(gx0, footY - 2 - (0.5 + 0.5 * gnd(gx0 / W)) * 7 - Math.max(0, mound(gx0 / W) + 0.2) * mAmp);
    }
    x.lineTo(W, H);
    x.closePath();
    x.fill();

    var px, i;
    if (back) {
      // 2a. Horizontkamm: geschlossene Kronenlinie aus ueberlappenden
      // Kreisen entlang einer Rauschkurve, darunter vollflaechig. Kuppen
      // bei 6-18 % Sichthoehe ueber dem Fuss + r -- nie ueber 62 %.
      var rn = wNoise(rnd, 6);
      var crown = function (u) { return footY - D * (0.06 + 0.12 * (0.5 + 0.5 * rn(u))); };
      x.beginPath();
      x.moveTo(0, H);
      for (px = 0; px <= W; px += 8) x.lineTo(px, crown(px / W) + 22);
      x.lineTo(W, H);
      x.closePath();
      x.fill();
      var bumps = [];
      for (px = 0; px < W; px += 10 + rnd() * 16) {
        var r = 18 + rnd() * 42, cy = crown(px / W) + r * 0.55;
        bumps.push([px, cy, r]);
        for (var dx = -W; dx <= W; dx += W) {
          if ((dx < 0 && px + r < W) || (dx > 0 && px - r > 0)) continue;
          x.beginPath(); x.arc(px - dx, cy, r, 0, 7); x.fill();
        }
      }
      // Laubstruktur auf der Kammkante: kleine Stempel, fast ganz im Dunst
      B.offset(0);
      for (i = 0; i < bumps.length; i++) {
        var bp = bumps[i];
        // hinter der Textspalte ruhig: keine Blattstruktur, keine Tupfen
        if (rnd() < 0.35 || inZ(bp[0], WALD.zRidgeLeaf)) continue;
        for (var s2 = 0; s2 < 6; s2++) {
          var aa = -Math.PI * (0.15 + rnd() * 0.7);
          B.stamp(P.stamps[rnd() < 0.7 ? 0 : Math.min(1, P.allow(bp[0]))],
                  bp[0] + Math.cos(aa) * bp[2] * 0.9, bp[1] + Math.sin(aa) * bp[2] * 0.9,
                  P.leaf[0] + rnd() * 4, rnd() * 6.28, true);
        }
      }
      B.offset(0);
      // 2b. Einzelne Kronen ragen aus dem Kamm
      for (px = rnd() * 120; px < W; px += D * (0.14 + rnd() * 0.2)) {
        var rth = D * (0.17 + rnd() * 0.13);
        if (inZ(px, WALD.zRidgeTree)) continue;
        tree(px, rth, spec.depth, 0.7);
      }
    } else {
      // 2. Baeume. Mitte: ueberall ausser hinter der Textspalte (dort
      // bleibt nur der Kamm). Vorn: nur an den Bildraendern -- die Mitte
      // gehoert Cover und Text. Ausserhalb der ueblichen Bildbreite
      // (nur Ultrabreit und hochkant sichtbar) sparsamer.
      px = rnd() * D * 0.1;
      while (px < W) {
        var wxp = worldX(px), th, off = Math.abs(wxp) > 1.05;
        if (front) {
          var ok = !inZ(px, WALD.zFrontTree);
          // links hoch (die Kulisse rahmt dort ohnehin), rechts niedrig:
          // der Mond und der Titel bleiben frei
          th = wxp > 0 && wxp < 1.25 ? D * (0.28 + rnd() * 0.08) : D * (0.46 + rnd() * 0.14);
          if (ok) tree(px, th, spec.depth, off ? 0.4 : 0.65);
          px += D * (ok ? (off ? 0.2 : 0.13) + rnd() * 0.1 : 0.05);
        } else {
          var textZone = inZ(px, WALD.zMidTree);
          th = D * (0.36 + rnd() * 0.16);
          if (!textZone) tree(px, th, spec.depth, off ? 0.55 : 0.75);
          px += D * (textZone ? 0.04 : (off ? 0.2 : 0.14) + rnd() * 0.1);
        }
      }
      // 3. Unterholz: Farnwedel und Moospolster entlang des Fusses
      var nf = front ? 26 : 16, fl = front ? [0.04, 0.08] : [0.025, 0.05];
      for (i = 0; i < nf; i++) {
        var fpx = rnd() * W, flen = D * (fl[0] + rnd() * (fl[1] - fl[0]));
        wrapped(fpx, flen, function (seed, fx) {
          var tr = mulberry32(seed);
          B.fern(tr, fx, footY + 2, flen);
          if (tr() < 0.5) B.fern(tr, fx + flen * 0.3, footY + 2, flen * 0.8);
        });
      }
      var nm = front ? 30 : 18;
      for (i = 0; i < nm; i++) {
        var mpx = rnd() * W, mr = (front ? 9 : 6) + rnd() * (front ? 16 : 10);
        wrapped(mpx, mr, function (seed, mx2) { B.moss(mulberry32(seed), mx2, footY + 3, mr); });
      }
      // Cyanes Moosglimmen -- nur vorn links (Lichtstufe erlaubt, Bodenzone)
      if (front) {
        for (i = 0; i < 46; i++) {
          var gpx = rnd() * W, gwx = worldX(gpx);
          // vorn links (Querformat) und im Hochformat-Fenster
          if (gwx > -0.45 || gwx < -1.25) continue;
          var gr = 1.5 + rnd() * 1.5;
          x.fillStyle = rgba(cfg.glimmer, 0.25 + rnd() * 0.25);
          x.beginPath(); x.arc(gpx, footY - rnd() * D * 0.012, gr, 0, 7); x.fill();
        }
      }
    }

    // 4. Dunst: vertikaler Verlauf per source-atop, 0 oben, 0,25 am Fuss
    x.setTransform(1, 0, 0, 1, 0, 0);
    x.globalCompositeOperation = 'source-atop';
    var hz = x.createLinearGradient(0, footY - D * spec.top, 0, footY);
    hz.addColorStop(0, rgba(cfg.haze, 0));
    var hzA = back ? 0.12 : 0.25;
    hz.addColorStop(1, rgba(cfg.haze, hzA));
    x.fillStyle = hz;
    x.fillRect(0, 0, W, footY);
    x.fillStyle = rgba(cfg.haze, hzA);
    x.fillRect(0, footY, W, H - footY);
    if (front) {
      // Bodennebel-Saum: die Oberkante des Bodens liegt im Dunst, statt
      // als harte Waagerechte gegen den Nebel darueber zu stehen
      // (hochkant direkt ueber der Eyebrow sichtbar).
      var rim = x.createLinearGradient(0, footY - D * 0.04, 0, footY + D * 0.07);
      rim.addColorStop(0, rgba(WALD.mistHigh, 0));
      rim.addColorStop(0.4, rgba(WALD.mistHigh, 0.2));
      rim.addColorStop(1, rgba(WALD.mistHigh, 0));
      x.fillStyle = rim;
      x.fillRect(0, footY - D * 0.04, W, D * 0.11);
      // Waldboden: vom Fuss in die Void -- die letzte Zeile ist reine Void,
      // die Klammer der Geometrie zieht sie bis unter den Bildrand, und
      // der CSS-Schleier uebernimmt nahtlos.
      var gv = x.createLinearGradient(0, footY + D * 0.01, 0, H - 2);
      gv.addColorStop(0, rgba(cfg.skyLow, 0));
      gv.addColorStop(1, rgba(cfg.skyLow, 1));
      x.fillStyle = gv;
      x.fillRect(0, footY, W, H - footY);
    }
    x.globalCompositeOperation = 'source-over';
    // 5. Granulation
    wGranulate(x, kit.grain, 0.06, W, H);

    var glow = gm ? wGlowBake(gm) : null;
    return { canvas: c, glow: glow };
  }

  // Blaetterdach-Kulisse: ein naher Baum am Bildrand als Rahmen.
  // side -1 = links (Stamm am Rand, Hauptast ueber die obere linke Ecke
  // bis ~35 % Breite, Unterkante ueber 12 % -- das Cover bleibt frei);
  // side +1 = rechts (Stamm jenseits 92 %, ein Ast laeuft UNTER der
  // Mondscheibe her und rahmt sie). Masse in Welt, D = WALD.kulD.
  // Fusspunkt (Drehpunkt fuer den Wind) liegt bei (foot, Unterkante).
  function texKulisse(rnd, cfg, side, view, kit) {
    var D = WALD.kulD;
    var ww = side < 0 ? 0.86 : 0.5, wh = 1.08;
    var W = Math.round(ww * D), H = Math.round(wh * D);
    var c = makeCanvas(W, H), x = c.getContext('2d');
    var gm = null, gx = null;
    if (side < 0) { gm = makeCanvas(Math.ceil(W / 2), Math.ceil(H / 2)); gx = gm.getContext('2d'); }
    var footW = side < 0 ? 0.11 : 0.38;              // Stammfuss im Quad, Welt
    // Hochkant endet das Quad unterhalb der Bildoberkante: was ueber die
    // Texturkante hinausragt, wuerde dort als gerade Linie abgeschnitten.
    // Krone und Aeste bleiben darum ganz in der Textur.
    var tall = view.viewW < 1;
    // Texturzeile <-> Bildhoehe von oben (Quad oben bei Bildrand +0,05)
    function tyOf(f) { return (f + 0.05) * D; }
    function worldX(px) { return view.kulX(side, px / D - footW); }
    // Die Kulissen tragen volles Licht: ihr Laub steht immer ausserhalb
    // der Textspalte -- layoutForest() misst die Spalte zur Laufzeit und
    // schiebt bzw. verbirgt die rechte Kulisse, wenn es eng wird.
    var P = {
      fill: cfg.ink, frost: cfg.frost, bark: true,
      tones: kit.tones(1), stamps: kit.stamps(1), glowStamp: kit.glowStamp,
      allow: function () { return 2; },
      moonEdge: function (px) { return view.moonEdgeK(worldX(px)); },
      leaf: [15, 24], rc: [18, 76], trunkBend: 0.02,
      barkK: view.ks < 0.6 ? 0.3 : 1,
      td: mulberry32(Math.floor(rnd() * 4294967296)),
      maxY: side < 0
        ? function (px) { return px < 0.24 * D ? H : tyOf(0.12); }
        : function () { return H; }
    };
    // Mondrichtung in Texturkoordinaten, fuer die Lichtkante der Aeste
    var mTX = (footW + (view.moonXw - view.kulPivot(side)) / view.ks) * D, mTY = tyOf(cfg.moonY);
    P.moonDir = function (px, py) {
      var dx = mTX - px, dy = mTY - py, l = Math.hypot(dx, dy) || 1;
      return [dx / l, dy / l];
    };
    var B = wBrush(x, gx, P);
    var tr = rnd, k, tw = [];
    var tx = footW * D, w0 = 0.075 * D, sway = side < 0 ? 1 : -1;
    // Stamm: leichte S-Kurve ins Bild hinein, bis ueber die Oberkante
    var tp0 = [], ph = tr() * 6.28;
    for (k = 0; k <= 6; k++) {
      var fk = k / 6;
      tp0.push([tx + sway * (0.03 * D * fk * fk + Math.sin(fk * 3.1 + ph) * 0.01 * D), H + 6 - fk * (H + 60)]);
    }
    B.foot(B.td, tx, H - 2, w0);
    var trunk = B.limb(tp0, w0, w0 * 0.55, true);
    function onTrunk(f) { return trunk((H + 6 - tyOf(f)) / (H + 60)); }
    // Zweige: kurze Gabeln (Rekursion 1) mit Bueschelplaetzen an den Enden
    function twig(pt, ang, len, w) {
      B.branch(tr, pt[0], pt[1], ang, len, w, 1, tw, false);
    }
    var clusters = [];   // [x, y, rc]
    if (side < 0) {
      // Hauptast durch die linke obere Ecke bis ~35 % Bildbreite; seine
      // Unterkante bleibt ueber 12 % Hoehe, das Cover darunter frei.
      var a0 = onTrunk(0.27);
      var limbA = B.limb([[a0[0], a0[1]], [a0[0] + 0.07 * D, tyOf(0.17)], [a0[0] + 0.24 * D, tyOf(0.085)],
                          [a0[0] + 0.44 * D, tyOf(0.05)], [a0[0] + 0.63 * D, tyOf(0.075)]], a0[3] * 0.62, 0.007 * D, true);
      var la = [0.5, 0.72, 0.9];
      for (k = 0; k < la.length; k++) {
        var pa = limbA(la[k]);
        twig(pa, -Math.PI / 2 + (tr() - 0.3) * 0.9, (0.04 + tr() * 0.04) * D, pa[3] * 0.7);
      }
      // steiler Ast nach oben aus dem Bild
      var b0 = onTrunk(0.11);
      B.limb([[b0[0], b0[1]], [b0[0] + 0.05 * D, tyOf(tall ? 0.06 : 0.03)], [b0[0] + 0.09 * D, tyOf(tall ? 0.03 : -0.07)]], b0[3] * 0.6, 0.012 * D, true);
      // kurzer Ast nach links aus dem Bild, neben dem Cover
      var c0 = onTrunk(0.44);
      B.limb([[c0[0], c0[1]], [c0[0] - 0.08 * D, tyOf(0.41)], [c0[0] - 0.17 * D, tyOf(0.36)]], c0[3] * 0.55, 0.01 * D, true);
      clusters.push([c0[0] - 0.1 * D, tyOf(0.385), 0.042 * D]);
      // Das Blaetterdach: eine geschlossene Laubmasse, die ueber die
      // Oberkante hinausblutet und zur Ecke hin dichter und groesser
      // wird. Unterkante unregelmaessig: am Stamm tief, ueber dem Cover
      // nie unter 12 % Bildhoehe, zur Astspitze hin ausduennend.
      var xEnd = a0[0] + 0.66 * D;
      var under = function (px) {
        var f = px / D;
        return f < 0.24 ? tyOf(0.3 - 0.17 * clamp((f - 0.08) / 0.16, 0, 1))
                        : tyOf(0.12 - 0.05 * clamp((f - 0.24) / (xEnd / D - 0.24), 0, 1));
      };
      var canopy = [], ry, cx2;
      for (ry = tyOf(-0.06); ry < tyOf(0.3); ry += 0.045 * D) {
        for (cx2 = -0.02 * D + tr() * 0.04 * D; cx2 < xEnd; ) {
          var xf = clamp(cx2 / xEnd, 0, 1);
          var R = D * (0.072 - 0.042 * xf) * (0.75 + tr() * 0.5);
          var cyc = ry + (tr() - 0.5) * 0.03 * D;
          var lim = under(cx2) + (tr() - 0.5) * 0.03 * D;
          // zur Astspitze hin mehr Luecken -- die Masse franst aus
          var keep = tr() > 0.05 + 0.4 * xf * xf;
          if (keep && cyc + R * 0.55 <= lim && (!tall || cyc - R * 1.1 >= 0.01 * D)) canopy.push([cx2, cyc, R]);
          cx2 += R * (1.05 + tr() * 0.35);
        }
      }
      // Luecken zuerst mit Schattenlaub fuellen (dunkle Tiefe des Dachs,
      // kein Himmel zwischen den Klumpen) ...
      for (k = 0; k < canopy.length; k++) {
        var cpk = canopy[k];
        B.cluster(tr, cpk[0] + (tr() - 0.5) * cpk[2] * 0.6, cpk[1] + cpk[2] * 0.15, cpk[2] * 0.95, 0);
      }
      // ... dann die Klumpen von oben nach unten: die unteren liegen
      // naeher am Betrachter, ihre Mondseite legt sich ueber die oberen.
      canopy.sort(function (p, q) { return p[1] - q[1]; });
      // Mondlicht erreicht das Dach nur stellenweise: Lichtstufe auf etwa
      // einem Fuenftel der Klumpen, sonst bleibt die Ecke ein Neon-Fleck.
      for (k = 0; k < canopy.length; k++) B.clump(tr, canopy[k][0], canopy[k][1], canopy[k][2], tr() < 0.2 ? 2 : 1);
    } else {
      // Mondast: knapp unter der Scheibe nach links, das Ende biegt
      // leicht auf -- eine Wiege fuer den Mond, nie davor.
      var m0 = onTrunk(0.4);
      var ml = B.limb([[m0[0], m0[1]], [m0[0] - 0.07 * D, tyOf(0.35)], [m0[0] - 0.16 * D, tyOf(0.338)],
                       [m0[0] - 0.24 * D, tyOf(0.338)], [m0[0] - 0.29 * D, tyOf(0.31)]], m0[3] * 0.55, 0.005 * D, true);
      var lm = [0.35, 0.6, 0.85];
      for (k = 0; k < lm.length; k++) {
        var pm = ml(lm[k]);
        // haengende Zweiglein mit kleinem Laub -- unter dem Ast, nie davor
        twig(pm, Math.PI / 2 + (tr() - 0.5) * 1.2, (0.025 + tr() * 0.02) * D, pm[3] * 0.6);
        clusters.push([pm[0] + (tr() - 0.5) * 0.02 * D, pm[1] + 0.03 * D, (0.016 + tr() * 0.008) * D]);
      }
      // Krone am Stammkopf, nach oben aus dem Bild -- rechts des Mondes
      var k0 = onTrunk(0.15);
      B.limb([[k0[0], k0[1]], [k0[0] - 0.04 * D, tyOf(0.06)], [k0[0] - 0.06 * D, tyOf(tall ? 0.03 : -0.07)]], k0[3] * 0.6, 0.012 * D, true);
      B.limb([[k0[0], k0[1]], [k0[0] + 0.06 * D, tyOf(0.06)], [k0[0] + 0.1 * D, tyOf(tall ? 0.04 : -0.06)]], k0[3] * 0.55, 0.012 * D, true);
      clusters.push([k0[0] - 0.035 * D, tyOf(0.065), 0.045 * D]);
      clusters.push([k0[0] + 0.05 * D, tyOf(0.09), 0.05 * D]);
      clusters.push([k0[0] + 0.0 * D, tyOf(0.02), 0.04 * D]);
      // kurzer Ast nach rechts aus dem Bild
      var r0 = onTrunk(0.58);
      B.limb([[r0[0], r0[1]], [r0[0] + 0.08 * D, tyOf(0.53)], [r0[0] + 0.16 * D, tyOf(0.5)]], r0[3] * 0.5, 0.01 * D, true);
      clusters.push([r0[0] + 0.1 * D, tyOf(0.505), 0.04 * D]);
    }
    // Zweigenden bekommen kleines Laub, die gesetzten Plaetze grosses
    for (k = 0; k < tw.length; k++) {
      if (tw[k][4] !== 0 || tr() < 0.4) continue;
      clusters.push([tw[k][0], tw[k][1] - 0.008 * D, (side < 0 ? 0.022 : 0.014) * D * (0.8 + tr() * 0.4)]);
    }
    clusters.sort(function (p, q) { return q[1] - p[1]; });
    for (k = 0; k < clusters.length; k++) {
      var cl = clusters[k], cyk = cl[1];
      if (side < 0 && cl[0] > 0.24 * D) cyk = Math.min(cyk, tyOf(0.12) - cl[2] * 0.7);
      B.clump(tr, cl[0], cyk, cl[2]);
    }
    // Laub-Ausdehnung fuer das Layout (Welt relativ zum Stammfuss, bei
    // ks = 1): die rechte Kulisse muss mit ihrem Laub rechts der
    // Textspalte enden, die linke links davon.
    var lx0 = 1e9, lx1 = -1e9, boxes = [];
    var all = side < 0 ? clusters.concat(canopy) : clusters;
    for (k = 0; k < all.length; k++) {
      var q = all[k], qr = q[2] * 1.25;
      lx0 = Math.min(lx0, q[0] - qr);
      lx1 = Math.max(lx1, q[0] + qr);
      boxes.push([(q[0] - qr) / D - footW, (H - q[1] - qr) / D, (q[0] + qr) / D - footW, (H - q[1] + qr) / D]);
    }
    B.offset(0);
    x.setTransform(1, 0, 0, 1, 0, 0);
    wGranulate(x, kit.grain, 0.04, W, H);
    // Fuss ausblenden: die letzten 9 % laufen in Transparenz aus. Steht
    // der Fuss im Bild (hochkant), gibt es so keine waagerechte
    // Schnittkante, der Stamm versinkt im Bodennebel.
    x.globalCompositeOperation = 'destination-out';
    var fo = x.createLinearGradient(0, H * 0.91, 0, H);
    fo.addColorStop(0, 'rgba(0,0,0,0)');
    fo.addColorStop(1, 'rgba(0,0,0,1)');
    x.fillStyle = fo;
    x.fillRect(0, H * 0.91, W, H * 0.09);
    // Hochkant liegt auch die Oberkante im Bild: Stamm und Zweige, die
    // dort aus der Textur laufen, blenden aus statt gerade abzubrechen.
    if (tall) {
      var ft = x.createLinearGradient(0, 0, 0, 0.04 * D);
      ft.addColorStop(0, 'rgba(0,0,0,1)');
      ft.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = ft;
      x.fillRect(0, 0, W, 0.04 * D);
    }
    x.globalCompositeOperation = 'source-over';
    var glow = null;
    if (gm) glow = wGlowBake(gm);
    return { canvas: c, glow: glow, w: ww, h: wh, foot: footW, tall: tall,
             leafL: lx0 / D - footW, leafR: lx1 / D - footW, boxes: boxes };
  }

  // Nebelbank: liegt am Boden statt in den Kronen. Dicht an der
  // Unterkante, nach oben ausfransend; flache Schwaden statt Ballen,
  // ausgestanzte Loecher und feine Faeden. Additiv gemischt -- hier
  // braucht nichts THREE.Fog, die Ghosthaven-fog-Falle stellt sich nicht.
  function texMist(rnd, cfg, col) {
    // 1024 x 128 auf 4,4 x 0,3 Welt: in der Hoehe bewusst unter 1 Texel/px,
    // das bilineare Hochziehen glaettet die flachen Schwaden zu Dunst
    // statt zu Streifen.
    var W = 1024, H = 128;
    var c = makeCanvas(W, H), x = c.getContext('2d');
    var n = 70 + Math.floor(rnd() * 51), i;
    for (i = 0; i < n; i++) {
      var v = Math.pow(rnd(), 1.5);                 // 0 = unten: dichter am Boden
      var my = H * (0.9 - v * 0.75), mx = rnd() * W;
      var rx = 120 + rnd() * 300, ry = (5 + rnd() * 11) * H / 256;
      var a = 0.04 + rnd() * 0.05;
      x.save();
      x.translate(mx, my);
      x.rotate((rnd() - 0.5) * 0.105);
      x.scale(1, ry / rx);
      var g = x.createRadialGradient(0, 0, 0, 0, 0, rx);
      g.addColorStop(0, rgba(col, a));
      g.addColorStop(0.55, rgba(col, a * 0.6));
      g.addColorStop(1, rgba(col, 0));
      x.fillStyle = g;
      x.beginPath(); x.arc(0, 0, rx, 0, 7); x.fill();
      x.restore();
    }
    // feine Faeden
    var nf = 8 + Math.floor(rnd() * 5);
    x.lineCap = 'round';
    for (i = 0; i < nf; i++) {
      var fy = H * (0.45 + rnd() * 0.45), fx = rnd() * W * 0.8, fl = 150 + rnd() * 350;
      x.strokeStyle = rgba(col, 0.06 + rnd() * 0.04);
      x.lineWidth = (1 + rnd()) * H / 256;
      x.beginPath();
      x.moveTo(fx, fy);
      x.bezierCurveTo(fx + fl * 0.3, fy - (6 + rnd() * 10) * H / 256, fx + fl * 0.7, fy + (4 + rnd() * 8) * H / 256, fx + fl, fy - rnd() * 6 * H / 256);
      x.stroke();
    }
    // Loecher: 64x16-Rauschen, auf Bankgroesse gezogen, ab 0,6 gestanzt
    var hn = makeCanvas(64, 16), hx = hn.getContext('2d'), img = hx.createImageData(64, 16);
    for (i = 0; i < 64 * 16; i++) img.data[i * 4 + 3] = Math.round(255 * clamp((rnd() - 0.6) / 0.3, 0, 1));
    hx.putImageData(img, 0, 0);
    x.globalCompositeOperation = 'destination-out';
    x.imageSmoothingEnabled = true;
    x.drawImage(hn, 0, 0, W, H);
    // Maske: unten 0..0,12 auf, voll bis 0,5, darueber ((1-v)/0,5)^2 aus
    x.globalCompositeOperation = 'destination-in';
    var vg = x.createLinearGradient(0, H, 0, 0);
    vg.addColorStop(0, 'rgba(0,0,0,0)');
    vg.addColorStop(0.12, 'rgba(0,0,0,1)');
    vg.addColorStop(0.5, 'rgba(0,0,0,1)');
    for (var k = 1; k <= 5; k++) {
      var vv = 0.5 + k * 0.1;
      vg.addColorStop(vv, 'rgba(0,0,0,' + Math.pow((1 - vv) / 0.5, 2).toFixed(3) + ')');
    }
    x.fillStyle = vg;
    x.fillRect(0, 0, W, H);
    var hg = x.createLinearGradient(0, 0, W, 0);
    hg.addColorStop(0, 'rgba(0,0,0,0)');
    hg.addColorStop(0.12, 'rgba(0,0,0,1)');
    hg.addColorStop(0.88, 'rgba(0,0,0,1)');
    hg.addColorStop(1, 'rgba(0,0,0,0)');
    x.fillStyle = hg;
    x.fillRect(0, 0, W, H);
    x.globalCompositeOperation = 'source-over';
    return c;
  }

  // Weicher Lichtpunkt fuer die Glimmerfunken.
  function texGlow(cfg) {
    var S = 64, c = makeCanvas(S, S), x = c.getContext('2d');
    var g = x.createRadialGradient(S / 2, S / 2, 1, S / 2, S / 2, S / 2);
    g.addColorStop(0, rgba(cfg.glimmer, 1));
    g.addColorStop(0.35, rgba(cfg.glimmer, 0.4));
    g.addColorStop(1, rgba(cfg.glimmer, 0));
    x.fillStyle = g;
    x.fillRect(0, 0, S, S);
    return c;
  }

  // Die beiden Bestien, neu gezeichnet nach dem Artwork.
  //
  // Das helle Wesen ist die GEWEIH-EULE vom Cover: grosse Schwingen aus
  // einzelnen, verjuengten Federn in Cyan, die zur Spitze in Lavendel
  // und an der Spitze in Dunkelviolett laufen, darueber zwei Lagen
  // Deckfedern; eiförmiger Rumpf mit Federschuppen, runder Schleier mit
  // schmalen, mandelfoermigen violetten Augen und Lidstrich, ein
  // verjuengtes Frost-Geweih mit Gabelungen. Das dunkle ist der
  // SCHATTENPIRSCHER von Karte 7 als Negativ dazu: eine Eule mit
  // Luchskopf -- Eulenrumpf, riesige Schwingen mit gebaenderten
  // Handschwingen und Cyan-Flecken am Fluegelbug, Luchsgesicht mit
  // Frost-Kranz, Pinselohren, kurzer buschiger Schweif, haengende Laeufe.
  //
  // Fuenf Posen je Wesen, als Fluegelschlag gelesen:
  //   0 oben, 1 Flex (Hand eingeknickt), 2 Gleit-V (offen gehoben wie
  //   auf dem Cover), 3 halb unten, 4 unten (verkuerzt nach unten-vorn).
  // arm = Winkel des Oberarms in Grad (0 = nach hinten, 90 = nach
  // oben, -90 = nach unten), bend = Knick der Hand, spread = Faecherung
  // der Handschwingen, rear = Armwinkel des hinteren, verkuerzten
  // Fluegels, len = Verkuerzung.
  //
  // Das Blatt ist 384x240 bei Pfadkoordinaten im alten 256x160-Raster
  // (ctx.scale 1,5): alle Linien >= 1 Einheit = 1,5 px, damit bei
  // Spritegroesse nichts flimmert. Die Gestalt selbst ist um OWL_S um
  // den Rumpfanker verkleinert, damit die um ~20 % groesseren Schwingen
  // samt Schein ins Blatt passen; OWL_ZOOM gleicht das am Sprite aus.
  // Beide Wesen blicken im Blatt nach RECHTS; die Gegenrichtung entsteht
  // als UV-gespiegelter Klon derselben Quelle (siehe owl()).
  var OWL_POSES = [
    { arm: 80, bend: -18, spread: 0.85, rear: 72, len: 0.96 },
    { arm: 54, bend: -46, spread: 0.5, rear: 46, len: 0.9 },
    { arm: 30, bend: -8, spread: 1.0, rear: 24, len: 1.0 },
    { arm: -14, bend: -34, spread: 0.8, rear: -6, len: 0.86 },
    { arm: -64, bend: -38, spread: 0.62, rear: -44, len: 0.7 }
  ];
  // Koerpermitte im Blatt (Pfadraster): dort sitzt der Sprite-Anker,
  // damit Nicken um den Rumpf dreht und Bahnpunkte den Koerper meinen.
  var OWL_W = 256, OWL_H = 160, OWL_OY = 104, OWL_BX = 152, OWL_BY = 4;
  var OWL_S = 0.8, OWL_ZOOM = 1.12;

  // Pfadraum eines Blattes: 1,5-fach, Ursprung auf OWL_OY, Gestalt um
  // den Rumpfanker auf OWL_S verkleinert.
  function owlSpace(x) {
    x.scale(1.5, 1.5);
    x.translate(OWL_BX, OWL_OY + OWL_BY);
    x.scale(OWL_S, OWL_S);
    x.translate(-OWL_BX, -OWL_BY);
    x.lineCap = 'round';
    x.lineJoin = 'round';
  }

  function owlDir(deg) {
    var a = deg * Math.PI / 180;
    return [-Math.cos(a), -Math.sin(a)];
  }

  // Ein verjuengter Strich entlang einer quadratischen Kurve (Geweih,
  // Pinsel, Kiele): Breite w0 an der Wurzel, w1 an der Spitze.
  function taper(x, x0, y0, cx, cy, x1, y1, w0, w1) {
    var N = 12, L = [], R = [], i;
    for (i = 0; i <= N; i++) {
      var t = i / N, it = 1 - t;
      var px = it * it * x0 + 2 * it * t * cx + t * t * x1;
      var py = it * it * y0 + 2 * it * t * cy + t * t * y1;
      var dx = 2 * it * (cx - x0) + 2 * t * (x1 - cx), dy = 2 * it * (cy - y0) + 2 * t * (y1 - cy);
      var dl = Math.sqrt(dx * dx + dy * dy) || 1, w = lerp(w0, w1, t) / 2;
      L.push([px - dy / dl * w, py + dx / dl * w]);
      R.push([px + dy / dl * w, py - dx / dl * w]);
    }
    x.beginPath();
    x.moveTo(L[0][0], L[0][1]);
    for (i = 1; i <= N; i++) x.lineTo(L[i][0], L[i][1]);
    x.arc((L[N][0] + R[N][0]) / 2, (L[N][1] + R[N][1]) / 2, w1 / 2, 0, 7);
    for (i = N; i >= 0; i--) x.lineTo(R[i][0], R[i][1]);
    x.closePath();
    x.fill();
  }
  function qAt(x0, y0, cx, cy, x1, y1, t) {
    var it = 1 - t;
    return [it * it * x0 + 2 * it * t * cx + t * t * x1, it * it * y0 + 2 * it * t * cy + t * t * y1];
  }

  // Eine verjuengte Feder von der Wurzel (rx, ry) in Richtung (ux, uy).
  function featherPath(x, rx, ry, ux, uy, L, w) {
    var nx = -uy, ny = ux;
    function P(a, b) { return [rx + ux * L * a + nx * w * b, ry + uy * L * a + ny * w * b]; }
    var b0 = P(0, 0.32), b1 = P(0.42, 0.55), b2 = P(0.86, 0.34), tp = P(1, 0);
    var c0 = P(0, -0.32), c1 = P(0.42, -0.5), c2 = P(0.86, -0.3);
    x.beginPath();
    x.moveTo(b0[0], b0[1]);
    x.quadraticCurveTo(b1[0], b1[1], (b1[0] + b2[0]) / 2, (b1[1] + b2[1]) / 2);
    x.quadraticCurveTo(b2[0], b2[1], tp[0], tp[1]);
    x.quadraticCurveTo(c2[0], c2[1], (c1[0] + c2[0]) / 2, (c1[1] + c2[1]) / 2);
    x.quadraticCurveTo(c1[0], c1[1], c0[0], c0[1]);
    x.closePath();
  }

  function drawFeather(x, pal, rx, ry, ux, uy, L, w, quill) {
    featherPath(x, rx, ry, ux, uy, L, w);
    var g = x.createLinearGradient(rx, ry, rx + ux * L, ry + uy * L);
    g.addColorStop(0, pal.f0);
    g.addColorStop(0.5, pal.f1);
    g.addColorStop(0.84, pal.f2);
    g.addColorStop(1, pal.f3);
    x.fillStyle = g;
    x.fill();
    if (pal.bands) {
      // Baenderung quer zur Feder wie auf Karte 7, nur auf der Fahne
      x.save();
      x.clip();
      x.strokeStyle = pal.bands;
      x.lineWidth = L * 0.07;
      x.beginPath();
      for (var bI = 0; bI < 3; bI++) {
        var f = 0.34 + bI * 0.2, nx = -uy * w, ny = ux * w;
        x.moveTo(rx + ux * L * f - nx, ry + uy * L * f - ny);
        x.lineTo(rx + ux * L * (f + 0.06) + nx, ry + uy * L * (f + 0.06) + ny);
      }
      x.stroke();
      x.restore();
      featherPath(x, rx, ry, ux, uy, L, w);
    }
    x.lineWidth = 1;
    x.strokeStyle = pal.edge;
    x.stroke();
    if (quill) {
      x.lineWidth = pal.quillW;
      x.strokeStyle = pal.quill;
      x.beginPath();
      x.moveTo(rx + ux * L * 0.06, ry + uy * L * 0.06);
      x.lineTo(rx + ux * L * 0.8, ry + uy * L * 0.8);
      x.stroke();
    }
  }

  // Ein Fluegel als Faecher einzelner Federn auf eigener Ebene: erst
  // Armschwingen, dann Handschwingen von innen nach aussen (die
  // aeussere liegt oben, wie auf dem Cover), darueber zwei Reihen
  // Deckfedern und die Vorderkante. Danach die Lichtfuehrung per
  // source-atop: oben heller, unten violett -- nur auf den Federn.
  function drawWing(cfg, pale, sx, sy, armDeg, bendDeg, spread, scale, rear) {
    var c = makeCanvas(OWL_W * 1.5, OWL_H * 1.5), x = c.getContext('2d');
    owlSpace(x);
    var pal = pale ? {
      f0: '#6fdbe3', f1: '#93c2ef', f2: '#b19deb', f3: '#5b3fa8',
      edge: 'rgba(91,63,168,0.42)', quill: 'rgba(242,246,248,0.42)', quillW: 1
    } : {
      f0: '#241b52', f1: '#2b2163', f2: '#3a2e80', f3: '#4b3c9c',
      edge: 'rgba(8,5,20,0.7)', quill: 'rgba(195,226,234,0.36)', quillW: 1.2,
      bands: 'rgba(87,177,208,0.16)'
    };
    var d = owlDir(armDeg), h = owlDir(armDeg + bendDeg);
    // Schwingen ~20 % groesser als zuvor: sie sollen die Gestalt tragen
    var La = 35 * scale, Lh = 32 * scale, Lp = 62 * scale, FW = 11.5 * scale;
    var Wx = sx + d[0] * La, Wy = sy + d[1] * La;
    var Hx = Wx + h[0] * Lh, Hy = Wy + h[1] * Lh;
    // Hinterkante: die Senkrechte zum Arm, die nach hinten zeigt
    var tr = d[1] > 0 ? [-d[1], d[0]] : [d[1], -d[0]];
    if (tr[0] > 0) { tr[0] = -tr[0]; tr[1] = -tr[1]; }
    var aH = Math.atan2(h[1], h[0]), aT = Math.atan2(tr[1], tr[0]);
    var dA = aT - aH;
    while (dA > Math.PI) dA -= 2 * Math.PI;
    while (dA < -Math.PI) dA += 2 * Math.PI;
    var i, f, a, rx, ry;
    // Armschwingen
    var m = 7;
    for (i = m - 1; i >= 0; i--) {
      f = i / (m - 1);
      rx = lerp(Wx, sx, 0.06 + 0.84 * f); ry = lerp(Wy, sy, 0.06 + 0.84 * f);
      a = aT - dA * 0.16 * (1 - f) + 0.1;
      drawFeather(x, pal, rx, ry, Math.cos(a), Math.sin(a), (36 - 10 * f) * scale, FW, true);
    }
    // Handschwingen, innen zuerst
    var n = 9;
    for (i = n - 1; i >= 0; i--) {
      f = i / (n - 1);
      rx = lerp(Hx, Wx, 0.04 + 0.92 * f); ry = lerp(Hy, Wy, 0.04 + 0.92 * f);
      a = aH - dA * 0.1 + dA * spread * 0.86 * f;
      var len = Lp * (1 - 0.36 * f) * (i === 0 ? 0.88 : 1);
      drawFeather(x, pal, rx, ry, Math.cos(a), Math.sin(a), len, FW * 0.95, true);
    }
    // Grosse Deckfedern ueber den Wurzeln
    var cov = pale ? {
      f0: '#a6ecf0', f1: '#6fd9e2', f2: '#8fb8ee', f3: '#a594e4',
      edge: 'rgba(91,63,168,0.4)', quill: '', quillW: 1
    } : {
      f0: '#1d1640', f1: '#251c50', f2: '#2c2160', f3: '#3c3080',
      edge: 'rgba(195,226,234,0.22)', quill: '', quillW: 1
    };
    var rows = [[8, 17, 0.6], [6, 10, 0.25]];
    for (var rI = 0; rI < rows.length; rI++) {
      var cnt = rows[rI][0], cl = rows[rI][1] * scale, off = rows[rI][2];
      for (i = 0; i < cnt; i++) {
        f = i / (cnt - 1);
        // Leitlinie Schulter -> Handgelenk -> Hand
        var px, py;
        if (f < 0.55) { px = lerp(sx, Wx, f / 0.55); py = lerp(sy, Wy, f / 0.55); }
        else { px = lerp(Wx, Hx, (f - 0.55) / 0.45 * 0.7); py = lerp(Wy, Hy, (f - 0.55) / 0.45 * 0.7); }
        px += tr[0] * off * 7 * scale; py += tr[1] * off * 7 * scale;
        a = aT - dA * (0.25 + 0.35 * f);
        drawFeather(x, cov, px, py, Math.cos(a), Math.sin(a), cl, 10 * scale, false);
      }
    }
    // Vorderkante (Flughaut ueber dem Arm)
    x.lineWidth = 7 * scale;
    x.strokeStyle = pale ? '#a9eef2' : '#1d1640';
    x.beginPath();
    x.moveTo(sx, sy);
    x.quadraticCurveTo(Wx, Wy, lerp(Wx, Hx, 0.75), lerp(Wy, Hy, 0.75));
    x.stroke();
    if (!pale) {
      // Cyan-Flecken auf dem Fluegelbug wie auf der Karte
      x.fillStyle = 'rgba(127,224,230,0.5)';
      var sp = [[0.55, 2, 1.5], [0.8, -1, 1.2], [0.95, 3, 1.4], [1.15, 0, 1.1], [0.7, 6, 1.1], [1.0, 7, 1.2], [1.3, 4, 1]];
      for (i = 0; i < sp.length; i++) {
        var q = sp[i][0], qx, qy;
        if (q <= 1) { qx = lerp(sx, Wx, q); qy = lerp(sy, Wy, q); }
        else { qx = lerp(Wx, Hx, q - 1); qy = lerp(Wy, Hy, q - 1); }
        qx += tr[0] * sp[i][1] * scale; qy += tr[1] * sp[i][1] * scale;
        x.beginPath(); x.arc(qx, qy, sp[i][2] * scale, 0, 7); x.fill();
      }
    }
    // Lichtfuehrung nur auf den Federn
    x.globalCompositeOperation = 'source-atop';
    var top = Math.min(sy, Hy, Wy) - Lp, bot = Math.max(sy, Hy, Wy) + Lp;
    var lg = x.createLinearGradient(0, top, 0, bot);
    if (pale) {
      lg.addColorStop(0, 'rgba(255,255,255,0.15)');
      lg.addColorStop(0.35, 'rgba(255,255,255,0)');
      lg.addColorStop(0.7, 'rgba(91,63,168,0)');
      lg.addColorStop(1, 'rgba(91,63,168,0.32)');
    } else {
      lg.addColorStop(0, 'rgba(195,226,234,0.07)');
      lg.addColorStop(0.5, 'rgba(195,226,234,0)');
      lg.addColorStop(1, 'rgba(8,5,20,0.3)');
    }
    x.fillStyle = lg;
    x.fillRect(-80, -OWL_OY - 60, OWL_W + 160, OWL_H + 120);
    if (rear) {
      // Der ferne Fluegel liegt im Schatten des Rumpfs
      x.fillStyle = pale ? 'rgba(70,52,140,0.38)' : 'rgba(5,3,14,0.35)';
      x.fillRect(-80, -OWL_OY - 60, OWL_W + 160, OWL_H + 120);
    }
    // Frost-Lichtkante der Silhouette (nur dunkel): Vorderkante
    if (!pale) {
      x.globalCompositeOperation = 'source-over';
      x.lineWidth = 1.4;
      x.strokeStyle = rear ? 'rgba(195,226,234,0.35)' : 'rgba(195,226,234,0.6)';
      x.beginPath();
      x.moveTo(sx, sy - 3 * scale);
      x.quadraticCurveTo(Wx, Wy - 3 * scale, lerp(Wx, Hx, 0.8), lerp(Wy, Hy, 0.8) - 2.4 * scale);
      x.stroke();
    }
    return c;
  }

  // Rumpf als Ei: Brust vorn-oben rund, hinten schmal zum Schwanz.
  function owlBodyPath(b) {
    b.beginPath();
    b.moveTo(120, 6);
    b.bezierCurveTo(128, -10, 156, -19, 174, -13);
    b.bezierCurveTo(190, -7, 192, 14, 178, 22);
    b.bezierCurveTo(160, 30, 132, 22, 120, 6);
    b.closePath();
  }

  // Federschuppen in Reihen, nur auf dem Rumpf (Clip): je Schuppe ein
  // Bogen mit weichem Schatten darunter.
  // Gezeichnet von unten nach oben, damit jede Reihe die naechste
  // ueberdeckt wie echtes Gefieder: Schuppe = Tropfen mit Lichtkante
  // oben und Schattensaum unten.
  function owlScales(b, rowsY, x0, x1, light, shade, edge) {
    b.save();
    owlBodyPath(b);
    b.clip();
    for (var r = rowsY.length - 1; r >= 0; r--) {
      var y = rowsY[r], step = 9, off = r % 2 ? step / 2 : 0;
      for (var xx = x1 - off; xx > x0; xx -= step) {
        var g = b.createLinearGradient(0, y - 3, 0, y + 7);
        g.addColorStop(0, light);
        g.addColorStop(0.65, light);
        g.addColorStop(1, shade);
        b.fillStyle = g;
        b.beginPath();
        b.moveTo(xx - 5, y - 3);
        b.bezierCurveTo(xx - 5, y + 4, xx - 2, y + 7.5, xx + 1, y + 7.5);
        b.bezierCurveTo(xx + 4, y + 7.5, xx + 5, y + 3, xx + 5, y - 3);
        b.closePath();
        b.fill();
        b.strokeStyle = edge;
        b.lineWidth = 1;
        b.beginPath();
        b.moveTo(xx - 5, y + 1);
        b.bezierCurveTo(xx - 4, y + 5.5, xx - 1.5, y + 7.5, xx + 1, y + 7.5);
        b.bezierCurveTo(xx + 3.5, y + 7.5, xx + 5, y + 4, xx + 5, y + 1);
        b.stroke();
      }
    }
    b.restore();
  }

  // Mandelauge mit Lidstrich (Cover): aussen leicht angehoben.
  function almondEye(b, ex, ey, w, h, iris, lid, tilt) {
    var x0 = ex - w / 2, x1 = ex + w / 2, y0 = ey + tilt * 0.5, y1 = ey - tilt * 0.5;
    b.fillStyle = iris;
    b.beginPath();
    b.moveTo(x0, y0);
    b.quadraticCurveTo(ex, ey - h * 1.25, x1, y1);
    b.quadraticCurveTo(ex, ey + h * 0.95, x0, y0);
    b.closePath();
    b.fill();
    b.fillStyle = '#21144d';
    b.beginPath(); b.ellipse(ex + w * 0.06, ey - h * 0.08, w * 0.17, h * 0.42, 0, 0, 7); b.fill();
    b.fillStyle = '#ffffff';
    b.beginPath(); b.arc(ex - w * 0.12, ey - h * 0.28, Math.max(0.75, w * 0.08), 0, 7); b.fill();
    // Lidstrich oben, aussen ausgezogen
    b.strokeStyle = lid;
    b.lineWidth = 1.2;
    b.beginPath();
    b.moveTo(x0 - 0.4, y0 - 0.2);
    b.quadraticCurveTo(ex, ey - h * 1.3, x1 + 1.4, y1 - 0.9);
    b.stroke();
  }

  function texOwl(cfg, pale, pose, dir) {
    var W = OWL_W * 1.5, H = OWL_H * 1.5;
    var P = OWL_POSES[pose];
    var c = makeCanvas(W, H), x = c.getContext('2d');
    var body = makeCanvas(W, H), b = body.getContext('2d');
    if (dir < 0) { x.translate(W, 0); x.scale(-1, 1); }
    owlSpace(b);
    var sx = 160, sy = -8;

    // 1. ferner Fluegel, verkuerzt und ueber dem Ruecken
    var rearW = drawWing(cfg, pale, sx - 7, sy - 4, P.rear, P.bend, P.spread * 0.85, 0.78 * P.len, true);
    b.save(); b.setTransform(1, 0, 0, 1, 0, 0); b.drawImage(rearW, 0, 0); b.restore();

    var g, i, hx = 187, hy = -14;
    if (pale) {
      // 2. Schwanzfaecher: fuenf kurze Federn, leicht gesenkt
      for (i = 0; i < 5; i++) {
        var ta = Math.PI + (i - 2) * 0.16 - 0.14;
        drawFeather(b, {
          f0: '#bfe9f2', f1: '#8fd2ea', f2: '#ab9ce8', f3: '#5b3fa8',
          edge: 'rgba(91,63,168,0.4)', quill: 'rgba(255,255,255,0.35)', quillW: 1
        }, 128, 10, Math.cos(ta), Math.sin(ta), 18 - Math.abs(i - 2) * 2, 7.5, true);
      }
      // 3. Rumpf als Ei, oben im Licht, unten lavendel
      g = b.createLinearGradient(0, -18, 0, 28);
      g.addColorStop(0, '#f6fbfc');
      g.addColorStop(0.5, '#d6f0f5');
      g.addColorStop(1, '#ab9de6');
      b.fillStyle = g;
      owlBodyPath(b);
      b.fill();
      // Federschuppen: drei Reihen ueber Brust und Bauch
      owlScales(b, [-1, 6, 13, 20], 134, 190, 'rgba(240,250,252,0.55)', 'rgba(150,132,222,0.5)', 'rgba(91,63,168,0.32)');
      // Lichtfuehrung per source-atop auf dem Rumpf
      b.save();
      owlBodyPath(b);
      b.clip();
      g = b.createLinearGradient(130, -14, 160, 30);
      g.addColorStop(0, 'rgba(255,255,255,0.35)');
      g.addColorStop(0.45, 'rgba(255,255,255,0)');
      g.addColorStop(1, 'rgba(91,63,168,0.28)');
      b.fillStyle = g;
      b.fillRect(110, -30, 90, 70);
      b.restore();
      // Faenge, violett, angezogen
      b.strokeStyle = '#5b3fa8';
      b.lineWidth = 2;
      b.beginPath();
      b.moveTo(160, 24); b.quadraticCurveTo(164, 31, 170, 30);
      b.moveTo(151, 25); b.quadraticCurveTo(154, 32, 160, 32);
      b.stroke();

      // 4. Geweih in Frost, verjuengt, je Stange drei Sprossen
      var antlers = [
        // fern (hinten), dunkler und etwas kleiner
        { b: [hx - 7, hy - 9], c: [hx - 19, hy - 24], e: [hx - 12, hy - 46], s: -1, k: 0.88,
          col0: '#5fa6c8', col1: '#bfe3ee' },
        // nah
        { b: [hx + 4, hy - 11], c: [hx + 15, hy - 27], e: [hx + 9, hy - 49], s: 1, k: 1,
          col0: '#6fd3df', col1: '#e6f8fa' }
      ];
      for (i = 0; i < antlers.length; i++) {
        var A = antlers[i], kA = A.k;
        var ag = b.createLinearGradient(A.b[0], A.b[1], A.e[0], A.e[1]);
        ag.addColorStop(0, A.col0);
        ag.addColorStop(1, A.col1);
        b.fillStyle = ag;
        taper(b, A.b[0], A.b[1], A.c[0], A.c[1], A.e[0], A.e[1], 4.2 * kA, 0.8);
        // Sprossen: aussen-vorn, innen, aussen oben (Gabel)
        var tines = [[0.3, 10, -5, 2.2], [0.55, -9, -9, 1.9], [0.78, 8, -8, 1.6]];
        for (var tI = 0; tI < tines.length; tI++) {
          var T0 = qAt(A.b[0], A.b[1], A.c[0], A.c[1], A.e[0], A.e[1], tines[tI][0]);
          var tx = T0[0] + tines[tI][1] * A.s * kA, ty = T0[1] + tines[tI][2] * kA;
          taper(b, T0[0], T0[1], lerp(T0[0], tx, 0.4), lerp(T0[1], ty, 0.75), tx, ty, tines[tI][3] * kA, 0.7);
        }
      }

      // 5. Kopf: runder Kopf, Halskrause aus kurzen Federn
      g = b.createRadialGradient(hx + 4, hy - 5, 2, hx, hy, 18);
      g.addColorStop(0, '#f6fcfd');
      g.addColorStop(1, '#b9dfec');
      b.fillStyle = g;
      b.beginPath(); b.arc(hx, hy, 15, 0, 7); b.fill();
      b.fillStyle = 'rgba(183,166,234,0.5)';
      for (i = 0; i < 5; i++) {
        var na = 1.75 + i * 0.28;
        drawFeather(b, {
          f0: '#e9f8fa', f1: '#c6ecf2', f2: '#b7c9ef', f3: '#a594e4',
          edge: 'rgba(91,63,168,0.3)', quill: '', quillW: 1
        }, hx + Math.cos(na) * 10, hy + Math.sin(na) * 10, Math.cos(na + 0.5), Math.sin(na + 0.5), 9, 6, false);
      }
      // Schleier: rund-oval, oben nur eine flache Delle, Rand aus feinen
      // Federspitzen in Lavendel
      var fx = hx + 5, fy = hy + 1;
      b.fillStyle = '#ffffff';
      b.beginPath();
      b.moveTo(fx, fy - 9);
      b.bezierCurveTo(fx - 6, fy - 12.5, fx - 13, fy - 9, fx - 12.5, fy - 1);
      b.bezierCurveTo(fx - 12, fy + 7, fx - 5, fy + 11, fx, fy + 12);
      b.bezierCurveTo(fx + 5, fy + 11, fx + 12, fy + 7, fx + 12.5, fy - 1);
      b.bezierCurveTo(fx + 13, fy - 9, fx + 6, fy - 12.5, fx, fy - 9);
      b.closePath();
      b.fill();
      b.strokeStyle = 'rgba(165,148,228,0.8)';
      b.lineWidth = 1;
      for (i = 0; i < 16; i++) {
        var ra = -0.2 + i / 15 * (Math.PI + 0.4);
        var rx0 = fx + Math.cos(ra) * 11.4, ry0 = fy + 0.5 + Math.sin(ra) * 10.5;
        b.beginPath();
        b.moveTo(rx0, ry0);
        b.lineTo(fx + Math.cos(ra) * 13.6, fy + 0.5 + Math.sin(ra) * 12.4);
        b.stroke();
      }
      // Gesichtsfedern: zarte Kiele von der Mitte nach aussen
      b.strokeStyle = 'rgba(127,224,230,0.35)';
      for (i = 0; i < 6; i++) {
        var fa = 0.5 + i * 0.42;
        b.beginPath();
        b.moveTo(fx + Math.cos(fa) * 5, fy + 3 + Math.sin(fa) * 4);
        b.lineTo(fx + Math.cos(fa) * 9, fy + 3 + Math.sin(fa) * 7.5);
        b.stroke();
      }
      // Augen: schmal, mandelfoermig, violett mit Lidstrich
      almondEye(b, fx - 5.4, fy - 2, 6.4, 3.1, '#6b4fc0', '#3b2580', -0.8);
      almondEye(b, fx + 5.6, fy - 2, 7.2, 3.3, '#6b4fc0', '#3b2580', 1.4);
      // Schnabel: klein, gebogen, dunkelviolett
      b.fillStyle = '#4a3594';
      b.beginPath();
      b.moveTo(fx - 1.8, fy + 1.5);
      b.quadraticCurveTo(fx + 2.4, fy + 1.2, fx + 1.2, fy + 7.5);
      b.quadraticCurveTo(fx - 0.6, fy + 5, fx - 1.8, fy + 1.5);
      b.closePath();
      b.fill();
    } else {
      // 2. Kurzer, buschiger Luchsschweif mit gedaempfter Ringelung
      b.save();
      b.translate(124, 8);
      b.rotate(0.32);
      b.beginPath();
      b.moveTo(4, -6);
      b.bezierCurveTo(-8, -9, -20, -7, -24, -1);
      b.bezierCurveTo(-26, 4, -18, 8, -8, 7);
      b.bezierCurveTo(-2, 6, 4, 5, 6, 3);
      b.closePath();
      b.fillStyle = '#1d1640';
      b.fill();
      b.save();
      b.clip();
      b.fillStyle = 'rgba(58,46,128,0.5)';
      b.beginPath(); b.ellipse(-9, 0, 2.6, 9, 0.1, 0, 7); b.fill();
      b.beginPath(); b.ellipse(-16, 0, 2.4, 9, 0.1, 0, 7); b.fill();
      b.fillStyle = '#0f0b26';
      b.beginPath(); b.ellipse(-24, 1, 4.5, 9, 0, 0, 7); b.fill();
      b.restore();
      b.strokeStyle = 'rgba(195,226,234,0.45)';
      b.lineWidth = 1.3;
      b.beginPath(); b.moveTo(4, -6); b.bezierCurveTo(-8, -9, -20, -7, -24, -1); b.stroke();
      b.restore();
      // 3. Haengende Hinterlaeufe mit Pfoten
      b.strokeStyle = '#18123a';
      b.lineWidth = 6;
      b.beginPath();
      b.moveTo(140, 16); b.quadraticCurveTo(133, 23, 125, 26);
      b.moveTo(150, 20); b.quadraticCurveTo(145, 27, 137, 30);
      b.stroke();
      b.fillStyle = '#18123a';
      b.beginPath(); b.ellipse(124, 26.5, 4, 2.8, 0.3, 0, 7); b.fill();
      b.beginPath(); b.ellipse(136, 30.5, 4, 2.8, 0.3, 0, 7); b.fill();
      b.strokeStyle = 'rgba(195,226,234,0.3)';
      b.lineWidth = 1.2;
      b.beginPath();
      b.moveTo(153, 22); b.quadraticCurveTo(147, 29, 139, 32.5);
      b.stroke();
      // 4. Eulenrumpf in Indigo, oben ein Hauch Frost
      g = b.createLinearGradient(0, -18, 0, 28);
      g.addColorStop(0, '#2e2468');
      g.addColorStop(0.55, '#1d1640');
      g.addColorStop(1, '#130e2e');
      b.fillStyle = g;
      owlBodyPath(b);
      b.fill();
      owlScales(b, [0, 7, 14, 21], 136, 190, 'rgba(46,36,104,0.6)', 'rgba(12,8,30,0.6)', 'rgba(195,226,234,0.14)');
      // Gefleckt wie auf der Karte: gedaempfte Cyan-Tupfen
      b.fillStyle = 'rgba(127,224,230,0.34)';
      var spots = [[134, 4, 1.4], [142, -6, 1.2], [148, 10, 1.5], [156, -2, 1.3], [164, 14, 1.2], [170, 4, 1.4], [138, 14, 1.1], [176, -4, 1.1], [160, 22, 1.1]];
      for (i = 0; i < spots.length; i++) { b.beginPath(); b.arc(spots[i][0], spots[i][1], spots[i][2], 0, 7); b.fill(); }
      // Frost-Kante am Ruecken
      b.strokeStyle = 'rgba(195,226,234,0.5)';
      b.lineWidth = 1.34;
      b.beginPath();
      b.moveTo(122, 3);
      b.bezierCurveTo(130, -11, 156, -19, 172, -14);
      b.stroke();

      // 5. Luchskopf mit Frost-Kranz
      var kx = hx + 1, ky = hy + 1;
      // Kranz als Silhouette: gezackter Kragen um das Gesicht
      b.fillStyle = '#1d1640';
      b.beginPath();
      for (i = 0; i <= 18; i++) {
        var za = -0.2 + i / 18 * (Math.PI + 0.5);
        var zr = i % 2 ? 14.5 : 17;
        var zx = kx + 2 + Math.cos(za) * zr, zy = ky + 2 + Math.sin(za) * zr * 0.92;
        if (i === 0) b.moveTo(zx, zy); else b.lineTo(zx, zy);
      }
      b.closePath();
      b.fill();
      b.beginPath(); b.arc(kx, ky - 1, 14, 0, 7); b.fill();
      // Ohren mit hellerem Inneren und Pinseln
      var ears = [[kx - 12, ky - 6, kx - 10, ky - 31, kx - 1, ky - 13, 0.8], [kx + 1, ky - 13, kx + 9, ky - 34, kx + 13, ky - 7, 1]];
      for (i = 0; i < 2; i++) {
        var e = ears[i];
        b.fillStyle = '#1d1640';
        b.beginPath(); b.moveTo(e[0], e[1]); b.lineTo(e[2], e[3]); b.lineTo(e[4], e[5]); b.closePath(); b.fill();
        b.fillStyle = i ? '#3a3080' : '#2a2160';
        b.beginPath();
        b.moveTo(lerp(e[0], e[4], 0.3), lerp(e[1], e[5], 0.3));
        b.lineTo(lerp(e[2], (e[0] + e[4]) / 2, 0.2), lerp(e[3], (e[1] + e[5]) / 2, 0.2));
        b.lineTo(lerp(e[0], e[4], 0.7), lerp(e[1], e[5], 0.7));
        b.closePath(); b.fill();
        b.strokeStyle = 'rgba(195,226,234,' + (0.45 * e[6]) + ')';
        b.lineWidth = 1.3;
        b.beginPath(); b.moveTo(e[0], e[1]); b.lineTo(e[2], e[3]); b.lineTo(e[4], e[5]); b.stroke();
        // Pinsel: verjuengt, leicht nach vorn geneigt
        b.fillStyle = '#1d1640';
        taper(b, e[2], e[3] + 1, e[2] + 1, e[3] - 5, e[2] + 3, e[3] - 10, 2.4, 0.7);
      }
      // Gesichtsschleier: hellere Maske um Augen und Schnauze
      var fxD = kx + 4, fyD = ky + 1;
      g = b.createRadialGradient(fxD, fyD, 1, fxD, fyD, 12);
      g.addColorStop(0, 'rgba(76,64,150,0.95)');
      g.addColorStop(0.7, 'rgba(52,42,112,0.6)');
      g.addColorStop(1, 'rgba(29,22,64,0)');
      b.fillStyle = g;
      b.beginPath(); b.ellipse(fxD, fyD, 12, 11, 0, 0, 7); b.fill();
      // Frost-Kiele radial um das Gesicht: der Kranz
      for (i = 0; i < 11; i++) {
        var qa = 0.05 + i / 10 * (Math.PI - 0.1);
        var al = 0.3 + 0.25 * ((i * 7) % 5) / 4;
        b.strokeStyle = 'rgba(195,226,234,' + al.toFixed(2) + ')';
        b.lineWidth = 1.2;
        b.beginPath();
        b.moveTo(kx + 2 + Math.cos(qa) * 11, ky + 2 + Math.sin(qa) * 10);
        b.lineTo(kx + 2 + Math.cos(qa + 0.12) * 15, ky + 2 + Math.sin(qa + 0.12) * 14);
        b.stroke();
      }
      // Glimmer-Augen, leicht schraeg
      almondEye(b, fxD - 4.6, fyD - 2.2, 5.6, 3.4, 'rgba(127,224,230,0.95)', 'rgba(14,9,32,0.9)', -0.6);
      almondEye(b, fxD + 5.2, fyD - 2.2, 6.2, 3.6, 'rgba(127,224,230,0.95)', 'rgba(14,9,32,0.9)', 1.0);
      // Nasenspiegel und zwei sehr zarte Schnurrhaare
      b.fillStyle = 'rgba(195,226,234,0.7)';
      b.beginPath();
      b.moveTo(fxD - 1.6, fyD + 3.6); b.lineTo(fxD + 1.8, fyD + 3.6); b.lineTo(fxD + 0.1, fyD + 5.8);
      b.closePath(); b.fill();
      b.strokeStyle = 'rgba(195,226,234,0.2)';
      b.lineWidth = 1;
      b.beginPath();
      b.moveTo(fxD + 4, fyD + 6); b.lineTo(fxD + 15, fyD + 8);
      b.moveTo(fxD + 4, fyD + 7.5); b.lineTo(fxD + 13, fyD + 11.5);
      b.stroke();
    }

    // 6. naher Fluegel
    var frontW = drawWing(cfg, pale, sx, sy, P.arm, P.bend, P.spread, P.len, false);
    b.save(); b.setTransform(1, 0, 0, 1, 0, 0); b.drawImage(frontW, 0, 0); b.restore();

    // Ganzes Wesen einmal mit dem Glimmer-Schein auf das Blatt: der
    // Schein liegt aussen um die Gestalt, nicht zwischen den Federn.
    if (pale) {
      x.shadowColor = rgba(cfg.glimmer, 0.75);
      x.shadowBlur = 22;
    } else {
      // Gegenlicht: ein knapper Lavendel-Saum loest die Silhouette
      // vom Nachthimmel, ohne sie aufzuhellen
      x.shadowColor = 'rgba(183,166,234,0.55)';
      x.shadowBlur = 12;
    }
    x.drawImage(body, 0, 0);
    return c;
  }

  // Der Duell-Funke als eigener Vierzack mit heissem
  // Kern -- die Funkelsterne des Covers: lange Haupt-, kurze
  // Diagonalstrahlen, knapper Glimmer-Hof. Additiv gemischt.
  function texSpark(cfg) {
    var S = 256, c = makeCanvas(S, S), x = c.getContext('2d');
    var m = S / 2;
    x.globalCompositeOperation = 'lighter';
    var h = x.createRadialGradient(m, m, 0, m, m, S * 0.24);
    h.addColorStop(0, rgba(cfg.glimmer, 0.55));
    h.addColorStop(0.3, rgba(cfg.glimmer, 0.16));
    h.addColorStop(1, rgba(cfg.glimmer, 0));
    x.fillStyle = h;
    x.fillRect(0, 0, S, S);
    function ray(ang, len, w) {
      x.save();
      x.translate(m, m);
      x.rotate(ang);
      var g = x.createLinearGradient(0, 0, len, 0);
      g.addColorStop(0, rgba(cfg.moon, 0.95));
      g.addColorStop(0.35, rgba(cfg.frost, 0.6));
      g.addColorStop(1, rgba(cfg.glimmer, 0));
      x.fillStyle = g;
      x.beginPath();
      x.moveTo(0, -w);
      x.quadraticCurveTo(len * 0.22, -w * 0.28, len, 0);
      x.quadraticCurveTo(len * 0.22, w * 0.28, 0, w);
      x.closePath();
      x.fill();
      x.restore();
    }
    for (var i = 0; i < 4; i++) {
      ray(i * Math.PI / 2, m - 3, 7);
      ray(i * Math.PI / 2 + Math.PI / 4, m * 0.42, 4);
    }
    var k = x.createRadialGradient(m, m, 0, m, m, S * 0.06);
    k.addColorStop(0, 'rgba(255,255,255,1)');
    k.addColorStop(0.6, 'rgba(255,255,255,0.55)');
    k.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = k;
    x.beginPath(); x.arc(m, m, S * 0.06, 0, 7); x.fill();
    return c;
  }

  // Je eine Feder, die nach dem Duell abwaerts trudelt:
  // gebogener Kiel, zwei ungleiche Fahnen mit zwei Kerben, weicher
  // Daunenansatz -- liest sich auch klein als Feder, nicht als Stift.
  function texFeather(pale) {
    var W = 64, H = 128, c = makeCanvas(W, H), x = c.getContext('2d');
    x.lineCap = 'round';
    x.lineJoin = 'round';
    // Kiel: quadratische Kurve von unten nach oben, leicht gebogen
    var x0 = 30, y0 = 122, cx = 22, cy = 60, x1 = 38, y1 = 6;
    function Q(t) { return qAt(x0, y0, cx, cy, x1, y1, t); }
    function vane(side, wMax, notch) {
      x.beginPath();
      var p = Q(0.2);
      x.moveTo(p[0], p[1]);
      for (var i = 2; i <= 20; i++) {
        var t = 0.2 + 0.8 * i / 20;
        var q = Q(t), q2 = Q(Math.min(1, t + 0.01));
        var dx = q2[0] - q[0], dy = q2[1] - q[1], dl = Math.sqrt(dx * dx + dy * dy) || 1;
        var prof = Math.sin(Math.PI * Math.min(1, (t - 0.2) / 0.8)) * (t > 0.85 ? (1 - t) / 0.15 * 0.7 + 0.3 : 1);
        var w = wMax * Math.pow(Math.max(0, prof), 0.6);
        // Kerben: die Fahne reisst an zwei Stellen ein
        for (var nI = 0; nI < notch.length; nI++) if (Math.abs(t - notch[nI]) < 0.025) w *= 0.45;
        // Aeste stehen schraeg zur Spitze hin
        x.lineTo(q[0] + side * (-dy / dl) * w + dx / dl * w * 0.35, q[1] + side * (dx / dl) * w + dy / dl * w * 0.35);
      }
      x.lineTo(x1, y1);
      for (i = 20; i >= 2; i--) {
        var qq = Q(0.2 + 0.8 * i / 20);
        x.lineTo(qq[0], qq[1]);
      }
      x.closePath();
    }
    var colA = pale ? '#a8eef2' : '#2b2160', colB = pale ? '#b7a6ea' : '#3d3184';
    var g = x.createLinearGradient(0, H, 0, 0);
    g.addColorStop(0, pale ? '#e9f8fa' : '#241b52');
    g.addColorStop(0.45, colA);
    g.addColorStop(1, colB);
    x.fillStyle = g;
    vane(1, 15, [0.55]);
    x.fill();
    x.strokeStyle = pale ? 'rgba(91,63,168,0.45)' : 'rgba(195,226,234,0.4)';
    x.lineWidth = 1.5;
    x.stroke();
    x.fillStyle = g;
    vane(-1, 10, [0.42, 0.7]);
    x.fill();
    x.stroke();
    // Kiel
    x.strokeStyle = pale ? 'rgba(255,255,255,0.85)' : 'rgba(195,226,234,0.7)';
    x.lineWidth = 2;
    x.beginPath();
    x.moveTo(x0, y0);
    x.quadraticCurveTo(cx, cy, x1, y1);
    x.stroke();
    return c;
  }

  /* ------------------------------------------------------------------ */
  /* Szene                                                               */
  /* ------------------------------------------------------------------ */

  function Scene(root, canvas, cfg) {
    var self = this;
    this.root = root;
    this.canvas = canvas;
    this.cfg = cfg;
    this.teardown = [];
    this.ownTextures = [];
    this.dead = false;
    this.pointer = { x: 0, y: 0, tx: 0, ty: 0 };

    var rnd = mulberry32(cfg.seed);

    this.renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      antialias: false,
      alpha: false,
      powerPreference: 'low-power'
    });
    // Kein outputColorSpace/outputEncoding -- siehe den Kommentar an tex().

    this.scene = new THREE.Scene();
    // Orthografische Kamera, Sichthoehe 1: die Ebenen sind flache
    // Streifen, jede Tiefenwirkung entsteht aus Parallaxe und den in
    // die Texturen gebackenen Dunststufen. Eine perspektivische
    // Kamera haette hier nichts zu tun.
    this.camera = new THREE.OrthographicCamera(-1, 1, 0.5, -0.5, 0.1, 50);
    this.camera.position.z = 10;
    // Sichtbare Breite in Welteinheiten; resize() setzt den echten
    // Wert. Alles, was prozentual im Bild stehen soll (Mond, Eulen,
    // Funken), rechnet mit dieser Breite -- feste Weltkoordinaten
    // gelten nur bei einem einzigen Seitenverhaeltnis und schieben
    // auf schmalen Heroes alles Randstaendige aus dem Bild.
    this.viewW = 2;

    function quad(canvasTex, w, h, opts) {
      var m = new THREE.Mesh(
        new THREE.PlaneGeometry(w, h),
        new THREE.MeshBasicMaterial(Object.assign({
          map: tex(canvasTex),
          transparent: true,
          depthWrite: false,
          depthTest: false
        }, opts || {}))
      );
      self.scene.add(m);
      return m;
    }

    // Reihenfolge = Zeichenreihenfolge; depthTest ist aus, das
    // renderOrder-Feld haelt die Staffelung explizit fest.
    var order = 0;

    // Texturen, die nicht als material.map haengen (Shader-Uniforms),
    // sieht der traverse in destroy() nicht -- sie landen hier.
    this.ownTextures = this.ownTextures || [];
    function own(t) { self.ownTextures.push(t); return t; }

    // Himmel, Strahlenkranz, Sterne, Mond.
    // Eigene Zufallsstroeme (seed*17+n): der Hauptstrom rnd bleibt
    // fuer Funken und Co., und eine Aenderung hier verschiebt dort nichts.
    // Der Himmel ist 1 hoch (= sichtbare Hoehe) und wird in
    // measureStage() auf die sichtbare Breite gezogen: so landen die
    // Verlaufsstops exakt dort, wo der CSS-Untergrund sie hat.
    this.sky = quad(texSky(), 1, 1, { transparent: false });
    this.sky.renderOrder = order++;

    // Strahlenkranz direkt nach dem Himmel: Baeume und Nebel liegen
    // darueber, die Strahlen treten zwischen ihnen hervor. Unsichtbar,
    // bis measureStage() das Cover gefunden hat.
    var raysTex = own(tex(texRays(mulberry32(cfg.seed * 17 + 1))));
    this.rays = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), glowMaterial(raysTex));
    this.rays.renderOrder = order++;
    this.rays.visible = false;
    this.scene.add(this.rays);

    // Sterne: ~260 Punkte, davon ~180 im diagonalen Sternenstaubband
    // (Gauss-Streuung quer zur Bahn, wie die Spielplanmitte), dazu bis
    // zu acht Vierzack-Plaetze, die measureStage() in den freien Himmel
    // legt (Groesse 0 = aus).
    (function () {
      var r = mulberry32(cfg.seed * 17 + 2);
      function gauss() { return Math.sqrt(-2 * Math.log(r() + 1e-6)) * Math.cos(6.2832 * r()); }
      var nDust = 260, n = nDust + STAR4_MAX;
      var pos = new Float32Array(n * 3), dat = new Float32Array(n * 4), tint = new Float32Array(n);
      for (var i = 0; i < nDust; i++) {
        var u, y, sz;
        var kind = 0;
        if (i < 180) {
          // Band von links oben nach rechts unten, ueber dem Cover vorbei.
          // Die ersten 34 sind Dunstflocken (kind 2): gross, sehr leise,
          // sie geben dem Band den milchigen Grund des Spielplans.
          var s = r() * 2.3 - 1.15;
          u = s;
          y = 0.13 - 0.17 * s + gauss() * (0.03 + 0.03 * r());
          if (i < 34) { kind = 2; sz = 22 + r() * 26; y = 0.13 - 0.17 * s + gauss() * 0.02; }
          else sz = 1.0 + 1.5 * Math.pow(r(), 2);
        } else {
          u = r() * 2.1 - 1.05;
          y = -0.12 + Math.pow(r(), 0.8) * 0.6;
          sz = 0.6 + 1.9 * Math.pow(r(), 3);
        }
        pos[i * 3] = u; pos[i * 3 + 1] = y; pos[i * 3 + 2] = 0;
        dat[i * 4] = sz; dat[i * 4 + 1] = r() * 6.283; dat[i * 4 + 2] = 0.4 + r() * 1.2; dat[i * 4 + 3] = kind;
        tint[i] = r() < 0.3 ? 0.6 + r() * 0.4 : r() * 0.2;
      }
      for (var j = nDust; j < n; j++) {
        // Periode 3-7 s; Platz und Groesse setzt measureStage()
        dat[j * 4 + 1] = r() * 6.283; dat[j * 4 + 2] = 6.283 / (3 + r() * 4); dat[j * 4 + 3] = 1;
        tint[j] = r() * 0.25;
      }
      // Mipmaps nur hier (128er, ein paar KB): die haarfeinen Zacken
      // werden auf 13-22 px verkleinert und flimmerten sonst.
      var starTex = own(tex(texStar4()));
      starTex.generateMipmaps = true;
      starTex.minFilter = THREE.LinearMipmapLinearFilter;
      var geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      geo.setAttribute('aData', new THREE.BufferAttribute(dat, 4));
      geo.setAttribute('aTint', new THREE.BufferAttribute(tint, 1));
      self.starDust = nDust;
      self.stars = new THREE.Points(geo, new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 }, uHalfW: { value: 1 }, uDpr: { value: 1 },
          uStar: { value: starTex },
          uCol: { value: new THREE.Color('#e9e4ff') }, uCol2: { value: new THREE.Color(cfg.frost) }
        },
        vertexShader: STAR_VS, fragmentShader: STAR_FS,
        transparent: true, depthWrite: false, depthTest: false,
        blending: THREE.AdditiveBlending
      }));
      self.stars.frustumCulled = false;
      self.stars.renderOrder = order++;
      self.scene.add(self.stars);
    })();

    // Mond: Halo, Kernhof und Scheibe in einem Quad (siehe MOON_FS).
    var discTex = own(tex(texMoonDisc(cfg, mulberry32(cfg.seed * 17 + 4))));
    discTex.generateMipmaps = true;
    // Vormultipliziert hochladen: sonst zieht der Filter die Kante zu
    // den transparenten (schwarzen) Texeln und die Scheibe bekommt
    // einen dunklen Saum.
    discTex.premultiplyAlpha = true;
    discTex.minFilter = THREE.LinearMipmapLinearFilter;
    this.moon = new THREE.Mesh(
      new THREE.PlaneGeometry(cfg.moonSize, cfg.moonSize),
      new THREE.ShaderMaterial({
        uniforms: {
          uHalo: { value: own(tex(texMoonHalo(cfg))) }, uDisc: { value: discTex },
          uHaloA: { value: 0.5 }, uKernA: { value: 0.35 }, uRd: { value: MOON_RD },
          uKernCol: { value: new THREE.Color(cfg.moon).lerp(new THREE.Color(cfg.frost), 0.3) }
        },
        vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
        fragmentShader: MOON_FS,
        transparent: true, depthWrite: false, depthTest: false,
        blending: THREE.CustomBlending,
        blendEquation: THREE.AddEquation,
        blendSrc: THREE.OneFactor,
        blendDst: THREE.OneMinusSrcAlphaFactor
      })
    );
    this.moon.renderOrder = order++;
    this.scene.add(this.moon);
    this.moonScale = 1;

    // drei Waldebenen, Bodennebel, zwei Blaetterdach-
    // Kulissen und die Glimm-Quads der Laubwelle (buildForest).
    order = this.buildForest(cfg, order);

    // Mondlicht-Faecher. Liegt HINTER Kulissen und
    // Vorderstaemmen (lightSlot aus dem Wald-Aufbau, sonst hinter der
    // Mittelebene): die verdecken ihn, und so liest er sich als Licht,
    // das zwischen den Baeumen hindurchfaellt -- das Leuchtturm-Prinzip
    // aus Ghosthaven, nur leise. moonShaft ist ersatzlos entfallen: er
    // war unsichtbar und stand in der Textspalte.
    var fanTex = own(tex(texFan(cfg, mulberry32(cfg.seed * 17 + 5))));
    this.fan = new THREE.Mesh(new THREE.PlaneGeometry(1.5, 0.34), glowMaterial(fanTex));
    // Lange Achse von oben rechts (Mond) nach unten links; die Textur
    // laeuft mit u=0 oben, also um ~110 Grad gedreht.
    this.fan.rotation.z = -1.95;
    // Abnahme: hinter der Mittelebene (+0,5) lag nur noch die Rahmen-
    // Kulisse davor -- das Band lief glatt bis zum Boden durch, kein
    // Stamm schnitt es an. Darum gleich hinter die HINTERSTE Waldebene:
    // alle Staemme davor (Ebenen 2-4) schneiden das Licht an, die
    // Nebelbaenke legen sich weich darueber.
    // Mit dem Wald-Paket gilt dessen lightSlot (hinter Vorder-
    // ebene und Kulissen, vor der Mittelebene samt Glimmen). Nachgeprueft
    // per Differenzbild (fan=0.45 gegen hideobj=fan): Vorderstaemme, der
    // Stamm der linken Kulisse und ihr Blaetterdach schneiden das Band
    // sichtbar an, unten endet es am Waldboden der Vorderebene.
    var walls = this.layers.filter(function (l) { return !l.mist; });
    this.fan.renderOrder = this.lightSlot != null ? this.lightSlot : walls[0].mesh.renderOrder + 0.5;
    this.fan.visible = false;
    this.scene.add(this.fan);
    this.fanP = 0.12;      // Startet schon im Bild, links am Rand
    this.fanPause = 0;

    // Glimmer als Gluehwuermchen. EINE Punktwolke mit
    // eigenem Shader statt zweier gegeneinander pulsierender Wolken:
    // jeder Punkt driftet auf seiner eigenen Lissajous-Bahn und
    // leuchtet selten und asymmetrisch auf (schnell an, langsam aus),
    // statt im Gleichtakt mit allen anderen zu blinken. Drei bis vier
    // Punkte sind kleine Vierzacke wie die Funkelsterne des Covers.
    // Statischer Zufall aus eigenem Teilstrom (Seed-Disziplin).
    var wr = mulberry32(cfg.seed * 29 + 1);
    this.ownTextures = this.ownTextures || [];
    this.glim = null;
    if (cfg.fireflies > 0) {
      var nG = cfg.fireflies;
      var gPos = new Float32Array(nG * 3), gPh = new Float32Array(nG);
      var gFr = new Float32Array(nG), gSz = new Float32Array(nG), gSt = new Float32Array(nG);
      for (var p = 0; p < nG; p++) {
        // x normiert auf +-1; die Breite legt layoutCreatures() ueber
        // uScaleX an das Seitenverhaeltnis.
        gPos[p * 3] = wr() * 2 - 1;
        gPos[p * 3 + 1] = -0.5 + wr() * 0.55;
        gPh[p] = wr();
        gFr[p] = 1 / (5 + wr() * 7);
        gSz[p] = 4 + wr() * 4;
      }
      var nStar = nG >= 30 ? 4 : nG >= 10 ? 3 : 1;
      for (var sI = 0; sI < nStar; sI++) {
        var sj = Math.floor(wr() * nG);
        gSt[sj] = 1;
        gSz[sj] = 13 + wr() * 4;
      }
      var gGeo = new THREE.BufferGeometry();
      gGeo.setAttribute('position', new THREE.BufferAttribute(gPos, 3));
      gGeo.setAttribute('aPhase', new THREE.BufferAttribute(gPh, 1));
      gGeo.setAttribute('aFreq', new THREE.BufferAttribute(gFr, 1));
      gGeo.setAttribute('aSize', new THREE.BufferAttribute(gSz, 1));
      gGeo.setAttribute('aStar', new THREE.BufferAttribute(gSt, 1));
      this.glim = new THREE.Points(gGeo, new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uScaleX: { value: 1.6 },
          uPR: { value: 1 },
          uBr: { value: 0.5 },
          uCapX: { value: 0 },
          uCapY: { value: -10 },
          uColor: { value: new THREE.Color(cfg.glimmer) },
          uCore: { value: new THREE.Color(cfg.moon) }
        },
        vertexShader: [
          'attribute float aPhase; attribute float aFreq; attribute float aSize; attribute float aStar;',
          'uniform float uTime; uniform float uScaleX; uniform float uPR; uniform float uBr;',
          'uniform float uCapX; uniform float uCapY;',
          'varying float vGlow; varying float vStar;',
          'void main() {',
          '  float ph = aPhase * 6.2831853;',
          '  float ax = 0.01 + 0.02 * fract(aPhase * 7.13);',
          '  float ay = 0.008 + 0.014 * fract(aPhase * 3.71);',
          '  float wx = 6.2831853 / (6.0 + 8.0 * fract(aPhase * 5.31));',
          '  float wy = 6.2831853 / (6.0 + 8.0 * fract(aPhase * 9.17));',
          '  vec3 p = vec3(position.x * uScaleX + ax * sin(uTime * wx + ph),',
          '                position.y + ay * sin(uTime * wy + ph * 1.7), 0.0);',
          '  vec4 wp = modelMatrix * vec4(p, 1.0);',
          '  float s = fract(uTime * aFreq + aPhase);',
          '  float pulse = pow(1.0 - s, 3.0) * smoothstep(0.0, 0.035, s);',
          '  float g = (0.3 + 0.7 * pulse) * (0.8 + 0.2 * uBr);',
          // Vor der Textspalte gedeckelt: kein Funke darf dort heller
          // werden als das Grundglimmen.
          '  float cap = max(smoothstep(uCapX - 0.08, uCapX, wp.x), 1.0 - smoothstep(uCapY, uCapY + 0.08, wp.y));',
          '  vGlow = min(g, mix(1.0, 0.42, cap));',
          '  vStar = aStar;',
          '  gl_Position = projectionMatrix * viewMatrix * wp;',
          '  gl_PointSize = aSize * (0.8 + 0.4 * pulse) * uPR;',
          '}'
        ].join('\n'),
        fragmentShader: [
          'uniform vec3 uColor; uniform vec3 uCore;',
          'varying float vGlow; varying float vStar;',
          'void main() {',
          '  vec2 q = gl_PointCoord * 2.0 - 1.0;',
          '  float d = length(q);',
          '  float halo = exp(-d * d * 6.0) * (1.0 - smoothstep(0.75, 1.0, d));',
          '  float core = 1.0 - smoothstep(0.0, 0.3 - 0.16 * vStar, d);',
          '  float rays = vStar * (max(0.0, 1.0 - abs(q.x) * 9.0) * (1.0 - abs(q.y))',
          '             + max(0.0, 1.0 - abs(q.y) * 9.0) * (1.0 - abs(q.x)));',
          '  vec3 col = uColor * (halo * (0.75 - 0.4 * vStar) + rays * 0.8) + uCore * core * 0.8;',
          '  gl_FragColor = vec4(col * vGlow, 1.0);',
          '}'
        ].join('\n'),
        transparent: true,
        depthWrite: false,
        depthTest: false,
        blending: THREE.AdditiveBlending
      }));
      this.glim.frustumCulled = false;
      this.glim.renderOrder = order++;
      this.scene.add(this.glim);
    }

    // gemeinsamer Teilchenpool fuer den Nebelzauber des
    // hellen Wesens und die Splitter des Duells. Ein Draw-Call, nur
    // sichtbar, solange ein Teilchen lebt.
    var glow = tex(texGlow(cfg));
    this.ownTextures.push(glow);
    var POOL = 32;
    var plGeo = new THREE.BufferGeometry();
    var plPos = new THREE.BufferAttribute(new Float32Array(POOL * 3), 3);
    var plCol = new THREE.BufferAttribute(new Float32Array(POOL * 3), 3);
    plPos.setUsage(THREE.DynamicDrawUsage);
    plCol.setUsage(THREE.DynamicDrawUsage);
    plGeo.setAttribute('position', plPos);
    plGeo.setAttribute('color', plCol);
    this.pool = new THREE.Points(plGeo, new THREE.PointsMaterial({
      map: glow, size: 3, sizeAttenuation: false, vertexColors: true,
      transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false
    }));
    this.pool.frustumCulled = false;
    this.pool.visible = false;
    this.pool.renderOrder = order++;
    this.scene.add(this.pool);
    this.poolP = [];
    for (var q = 0; q < POOL; q++) this.poolP.push({ x: 0, y: 0, vx: 0, vy: 0, drag: 0, life: 0, max: 1, a: 0, c: null });
    this.poolColA = new THREE.Color(cfg.glimmer);
    this.poolColB = new THREE.Color(cfg.frost);

    // Die beiden Wesen. Fuenf Posen je Wesen als eigene
    // Texturen, dazu je ein UV-gespiegelter Klon fuer die Gegenrichtung:
    // Klone teilen die Quelle und damit den GL-Speicher (three
    // cached nach source + Parametern) -- die Spiegelung kostet nichts.
    function owl(pale) {
      var maps = [], pose;
      for (pose = 0; pose < OWL_POSES.length; pose++) maps.push(tex(texOwl(cfg, pale, pose, 1)));
      for (pose = 0; pose < OWL_POSES.length; pose++) {
        var mm = maps[pose].clone();
        mm.repeat.x = -1;
        mm.offset.x = 1;
        maps.push(mm);
      }
      var mat = new THREE.SpriteMaterial({
        map: maps[2], transparent: true, depthWrite: false, depthTest: false, opacity: 0
      });
      var sp = new THREE.Sprite(mat);
      sp.center.set(OWL_BX / OWL_W, 1 - (OWL_OY + OWL_BY) / OWL_H);
      sp.visible = false;
      sp.renderOrder = order++;
      self.scene.add(sp);
      return {
        sprite: sp, maps: maps, pale: pale,
        active: false, role: '', go: 0, face: 1, size: 0.1, k: 1,
        path: null, u: 0, v0: 0.2, tempo: 1, started: false,
        fm: 0, ph: 0, beats: 0, st: 0, gl: 0, fx: 0, auto: false,
        rot: 0, bob: 0, sag: 0, dis: 0, disT: 0, emitted: 0,
        band: pale ? 'top' : 'bottom', endT: -1e9
      };
    }
    this.owls = [owl(true), owl(false)];

    // Das Duell. Funke als eigener Vierzack (additiv),
    // EINE leicht elliptische Kreisbahn wie auf dem Spielplan, die als
    // Teilbogen anlaeuft und sich schliesst (Shader, pixelgenaue Linie
    // in jeder Groesse, ohne Textur), zwei trudelnde Federn. Alles nur
    // waehrend des Duells sichtbar.
    var sparkTex = tex(texSpark(cfg));
    this.ownTextures.push(sparkTex);
    this.spark = new THREE.Sprite(new THREE.SpriteMaterial({
      map: sparkTex, transparent: true, depthWrite: false, depthTest: false,
      blending: THREE.AdditiveBlending, opacity: 0
    }));
    this.spark.visible = false;
    this.spark.renderOrder = order++;
    this.scene.add(this.spark);
    this.sparkT = -1;

    this.ring = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(cfg.frost) },
        uOpacity: { value: 0 },
        uPx: { value: 0.01 },
        uHalf: { value: 0.01 },
        uAsp: { value: 0.72 },
        uSweep: { value: 1 },
        uStart: { value: 0 }
      },
      vertexShader: [
        'varying vec2 vUv;',
        'void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }'
      ].join('\n'),
      fragmentShader: [
        'uniform vec3 uColor; uniform float uOpacity; uniform float uPx; uniform float uHalf;',
        'uniform float uAsp; uniform float uSweep; uniform float uStart;',
        'varying vec2 vUv;',
        'void main() {',
        // Ellipse = Einheitskreis in pu; Abstand in Radius-Einheiten
        // ueber den Gradienten, damit die Linie ueberall gleich dick ist
        '  vec2 pu = (vUv * 2.0 - 1.0) * 1.08;',
        '  float f = max(length(pu), 1e-4);',
        '  vec2 gr = vec2(pu.x, pu.y / uAsp) / f;',
        '  float d = (f - 1.0) / max(length(gr), 1e-4);',
        '  float l = 1.0 - smoothstep(uHalf, uHalf + uPx, abs(d));',
        // Teilbogen: laeuft ab uStart gegen den Uhrzeigersinn, Kopf hell,
        // Schwanz leiser, weiche Kante am Kopf
        '  float a = atan(pu.y, pu.x) - uStart;',
        '  float rel = fract(a / 6.2831853 + 1.0);',
        '  float arc = uSweep >= 0.999 ? 1.0 : (1.0 - smoothstep(uSweep - 0.05, uSweep, rel)) * mix(0.45, 1.0, rel / max(uSweep, 1e-3));',
        '  gl_FragColor = vec4(uColor, uOpacity * l * arc);',
        '}'
      ].join('\n'),
      transparent: true, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending
    }));
    this.ring.visible = false;
    this.ring.renderOrder = order++;
    this.scene.add(this.ring);
    this.ringT = -1;

    this.feathers = [];
    for (var fI = 0; fI < 2; fI++) {
      var fTex = tex(texFeather(fI === 0));
      this.ownTextures.push(fTex);
      var fs = new THREE.Sprite(new THREE.SpriteMaterial({
        map: fTex, transparent: true, depthWrite: false, depthTest: false, opacity: 0
      }));
      fs.visible = false;
      fs.renderOrder = order++;
      this.scene.add(fs);
      this.feathers.push({ sprite: fs, t: -1, x0: 0, y: 0, ph: fI * 1.9, fadeY: -1 });
    }
    this.duel = null;

    // Startphase wuerfeln: alle Rhythmen haengen nur an t. So erwischt
    // jede Seitenladung eine andere Nebel- und Atemstellung, statt
    // immer mit exakt demselben Bild zu beginnen.
    this.t = 30 + Math.random() * 120;
    this.tStart = this.t;
    // Feste Ouvertuere statt Wuerfelglueck, Zeiten relativ zu tStart:
    // zuerst das Duell (Funke bei ~4 s -- wer nach 5 s weiterscrollt,
    // hat ihn gesehen; mit freier Gasse bricht die Eule dafuer aus dem
    // Cover, sonst treffen sich beide am Mond), dann die Mondpassage
    // (auf der Scheibe bei 14 s). Danach ein gemischter Stapel, gezogen
    // erst ab 26 s und nur, wenn beide Wesen ruhen.
    this.wesenHold = false;
    this.wesenIntro = [
      { at: 4.0, kind: 'duel', fromCover: true },
      { at: 14.0, kind: 'passage' }
    ];
    this.wesenDeck = [];
    this.wesenTail = 0;
    this.wesenNext = 26;
    this.wesenLastDuel = 0;
    this.wesenLastPassage = 0;
    this.moonFlare = 0;
    this.frames = 0;
    this.slowFrames = 0;
    this.graceUntil = 0;
    this.slowRetried = false;
    this.raf = 0;
    this.running = false;
    this.lastTick = 0;
  }

  /* ------------------------------------------------------------------ */
  /* Flugbahnen, Ouvertuere, Flug, Duell                    */
  /* ------------------------------------------------------------------ */

  function wSmooth01(v) { v = clamp(v, 0, 1); return v * v * (3 - 2 * v); }
  function wSmoothstep(a, b, v) { return wSmooth01((v - a) / (b - a)); }

  // Bahn als Kette kubischer Bezier-Stuecke [p0, c1, c2, p1, c3, c4, p2 ...],
  // nach Bogenlaenge abgetastet: die Wesen fliegen mit Weltgeschwindigkeit
  // statt mit Parametergeschwindigkeit, die Kontrollpunkte duerfen also
  // frei liegen. knots[i] ist die Bogenlaenge am Ende von Stueck i-1.
  function wPath(pts) {
    var N = 24, segs = (pts.length - 1) / 3;
    var n = segs * N + 1;
    var xs = new Float32Array(n), ys = new Float32Array(n), ss = new Float32Array(n);
    var k = 0, L = 0, knots = [0];
    for (var s = 0; s < segs; s++) {
      var a = pts[s * 3], b = pts[s * 3 + 1], c = pts[s * 3 + 2], d = pts[s * 3 + 3];
      for (var i = (s === 0 ? 0 : 1); i <= N; i++) {
        var u = i / N, v = 1 - u;
        var x = v * v * v * a[0] + 3 * v * v * u * b[0] + 3 * v * u * u * c[0] + u * u * u * d[0];
        var y = v * v * v * a[1] + 3 * v * v * u * b[1] + 3 * v * u * u * c[1] + u * u * u * d[1];
        if (k > 0) { var dx = x - xs[k - 1], dy = y - ys[k - 1]; L += Math.sqrt(dx * dx + dy * dy); }
        xs[k] = x; ys[k] = y; ss[k] = L; k++;
      }
      knots.push(L);
    }
    return { x: xs, y: ys, s: ss, n: k, L: L, knots: knots, i: 0 };
  }

  function wAt(P, u, out) {
    var i = P.i;
    if (i > P.n - 2 || P.s[i] > u) i = 0;
    while (i < P.n - 2 && P.s[i + 1] < u) i++;
    P.i = i;
    var s0 = P.s[i], s1 = P.s[i + 1];
    var f = s1 > s0 ? clamp((u - s0) / (s1 - s0), 0, 1) : 0;
    out.x = lerp(P.x[i], P.x[i + 1], f);
    out.y = lerp(P.y[i], P.y[i + 1], f);
    out.tx = P.x[i + 1] - P.x[i];
    out.ty = P.y[i + 1] - P.y[i];
    return out;
  }

  // Viertelbogen als Kubik: haengt [c1, c2, p] an pts an.
  function wArc(pts, cx, cy, r, a0, a1) {
    var kk = 4 / 3 * Math.tan((a1 - a0) / 4);
    var p0 = [cx + r * Math.cos(a0), cy + r * Math.sin(a0)];
    var p3 = [cx + r * Math.cos(a1), cy + r * Math.sin(a1)];
    pts.push([p0[0] - kk * r * Math.sin(a0), p0[1] + kk * r * Math.cos(a0)]);
    pts.push([p3[0] + kk * r * Math.sin(a1), p3[1] - kk * r * Math.cos(a1)]);
    pts.push(p3);
  }

  function wSeg(pts, c1, c2, p) { pts.push(c1, c2, p); }

  // Fluegelschlag in fuenf Posen, asymmetrisch: Abschlag 0,26 s ueber
  // oben -> Flex -> V -> halb unten -> unten, Aufschlag 0,18 s zurueck
  // (~2,3 Hz). Eine Salve endet nur an einer Zyklusgrenze und laeuft
  // ueber Flex ins Gleit-V aus. Im Gleiten federt alle 1,4-2,2 s fuer
  // 0,25 s die Flex-Pose nach.
  // fm: 0 Gleiten, 1 Salve, 2 Auslaufen, 3 Halten (Pose 0)
  function owlFlapStart(o, beats) { o.fm = 1; o.beats = beats; o.ph = 0; }
  function owlFlap(o, dt) {
    if (o.fm === 3) return 0;
    if (o.fm === 1) {
      o.ph += dt / 0.44;
      if (o.ph < o.beats) {
        var c = o.ph % 1;
        return c < 0.59 ? Math.min(4, Math.floor(c / 0.59 * 5)) : 3 - Math.min(2, Math.floor((c - 0.59) / 0.41 * 3));
      }
      o.fm = 2; o.st = 0;
    }
    if (o.fm === 2) {
      o.st += dt;
      if (o.st < 0.16) return o.st < 0.06 ? 0 : 1;
      o.fm = 0;
      o.gl = 1.6 + Math.random() * 1.2;
      o.fx = 1.4 + Math.random() * 0.8;
    }
    o.gl -= dt;
    o.fx -= dt;
    if (o.auto && o.gl <= 0) { owlFlapStart(o, Math.random() < 0.4 ? 3 : 2); return 0; }
    if (o.fx < 0) {
      if (o.fx < -0.25) o.fx = 1.4 + Math.random() * 0.8;
      return 1;
    }
    return 2;
  }

  // Cover- und Textspalten-Rechteck in Weltkoordinaten. Liefert das
  // Paket himmel this.cover/this.textCol, gelten deren Werte; die
  // eigene Messung springt nur ein, solange niemand sonst misst.
  Scene.prototype.wesenMeasure = function () {
    var cr = this.canvas.getBoundingClientRect();
    if (!cr.width || !cr.height) return;
    var vw = this.viewW;
    function w(el) {
      if (!el) return null;
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return null;
      return {
        x0: (r.left - cr.left) / cr.height - vw / 2,
        x1: (r.right - cr.left) / cr.height - vw / 2,
        y0: 0.5 - (r.bottom - cr.top) / cr.height,
        y1: 0.5 - (r.top - cr.top) / cr.height
      };
    }
    this.cover = w(this.root.querySelector('.sylvans-hero__image'));
    this.textCol = w(this.root.querySelector('.sylvans-hero__body'));
  };

  Scene.prototype.wesenLayout = function () {
    function norm(R) {
      if (!R) return null;
      var a = R.x0 !== undefined ? R.x0 : R.left, b = R.x1 !== undefined ? R.x1 : R.right;
      var c = R.y0 !== undefined ? R.y0 : R.bottom, d = R.y1 !== undefined ? R.y1 : R.top;
      if (a === undefined || b === undefined || c === undefined || d === undefined) return null;
      var x0 = Math.min(a, b), x1 = Math.max(a, b), y0 = Math.min(c, d), y1 = Math.max(c, d);
      return { x0: x0, x1: x1, y0: y0, y1: y1, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, w: x1 - x0, h: y1 - y0 };
    }
    var C = norm(this.cover), T = norm(this.textCol);
    var stacked = C && T ? T.y1 < C.y0 + 0.03 : this.viewW < 1;
    // misst der Himmel, gilt SEIN Urteil -- Mond, Licht und
    // Wesen sollen nie unterschiedlicher Meinung ueber das Layout sein.
    if (!this.wesenOwnLayout && typeof this.stacked === 'boolean') stacked = this.stacked;
    return { C: C, T: T, stacked: stacked, k: this.wesenK || Math.min(1, this.viewW / 1.1), hw: this.viewW / 2 };
  };

  // Bahnen durch die freien Korridore um das Cover, nie dahinter.
  Scene.prototype.wesenRoute = function (o, role, opts) {
    var Lo = this.wesenLayout(), C = Lo.C, T = Lo.T, hw = Lo.hw, k = Lo.k;
    var sz = o.size, sw = sz * 1.6, out = hw + sw * 0.62;
    var pts, face, i;
    o.stacked = Lo.stacked;
    // Aufloesezone des hellen Wesens vor der Textspalte
    o.ze = Math.min(-0.04 * this.viewW, T && !Lo.stacked ? T.x0 - 0.06 : 1e3);
    o.zs = o.ze - 0.12 * this.viewW;
    o.ty1 = T ? T.y1 : -1e3;
    o.xClamp = Math.min(-0.02 * this.viewW, T && !Lo.stacked ? T.x0 - 0.03 : 1e3);

    if (role === 'passage') {
      var mx = this.moonX, my = this.moonY, by = my - 0.006;
      // Hochkant steht der Mond (Paket himmel) halb HINTER der
      // oberen Coverkante -- die Scheibenmitte liegt unter dem Cover, und
      // die Silhouette verschwand dort hinter dem HTML-Bild. Ziel ist dann
      // die Mitte der sichtbaren Kappe zwischen Coverkante und Scheibenrand.
      if (Lo.stacked && C) {
        var mR = MOON_RD * this.cfg.moonSize * (this.moonScale || 1);
        if (C.y1 > my - mR && C.y1 < my + mR) by = Math.max(by, (C.y1 + my + mR) / 2);
      }
      var S = [out, my + 0.045];
      if (S[0] - mx < 0.22) S = [mx + 0.26, 0.5 + sz * 0.9];
      // Im Hochformat steigt es steiler aus, damit das Duell der
      // Ouvertuere nicht auf das Ende der Passage warten muss.
      var fq = Lo.stacked ? 0.45 : 1;
      var X = [mx - 0.45 * fq, 0.5 + sz * 1.3];
      pts = [S];
      wSeg(pts, [lerp(S[0], mx, 0.35), S[1] - 0.006], [mx + 0.11, by + 0.004], [mx, by]);
      wSeg(pts, [mx - 0.12 * fq, by - 0.004], [X[0] + 0.12 * fq, X[1] - 0.22 * (Lo.stacked ? 0.6 : 1)], X);
      return { pts: pts, face: -1 };
    }

    if (role === 'duel') {
      var D = opts.duel, M = D.M, r = D.r;
      // Am Mond (Desktop): das helle Wesen stoesst von oben herab und
      // steigt wieder auf, das dunkle kommt von rechts und taucht unter
      // dem Mond nach rechts weg -- links stuende die Schrift.
      var atMoon = D.place === 'moon' && !Lo.stacked;
      pts = [];
      if (o.pale && atMoon) {
        var P0m = [M[0] - r, M[1]], P1m = [M[0] + r, M[1]], topY = 0.5 + sz * 0.9;
        pts.push([P0m[0] - 0.1, topY]);
        wSeg(pts, [P0m[0] - 0.12, 0.46], [P0m[0], P0m[1] + 0.12], P0m);
        D.paleKnot = 1;
        wArc(pts, M[0], M[1], r, Math.PI, Math.PI * 1.5);
        wArc(pts, M[0], M[1], r, Math.PI * 1.5, Math.PI * 2);
        wSeg(pts, [P1m[0], P1m[1] + 0.1], [P1m[0] + 0.06, 0.48], [P1m[0] + 0.1, topY]);
        return { pts: pts, face: 1 };
      }
      if (!o.pale && atMoon) {
        var Q0m = [M[0] + r, M[1]], Q1m = [M[0] - r, M[1]];
        // Kontrollpunkt innerhalb des Bildes: laege er jenseits von out,
        // schluege die Bahn erst hinaus und das Wesen gaelte als gegangen
        pts.push([out, M[1] - 0.06]);
        wSeg(pts, [Math.min(Q0m[0] + 0.25, out - 0.1), M[1] - 0.07], [Q0m[0], Q0m[1] - 0.12], Q0m);
        wArc(pts, M[0], M[1], r, 0, Math.PI * 0.5);
        wArc(pts, M[0], M[1], r, Math.PI * 0.5, Math.PI);
        wSeg(pts, [Q1m[0], Q1m[1] - 0.12], [Q1m[0] + 0.1, M[1] - 0.24], [M[0] + 0.25, M[1] - 0.26]);
        wSeg(pts, [M[0] + 0.4, M[1] - 0.27], [out - 0.1, M[1] - 0.25], [out, M[1] - 0.22]);
        return { pts: pts, face: -1 };
      }
      if (o.pale) {
        // Kommt von links (ueber die Box), kreist unten herum nach rechts
        // und loest sich danach in der Gasse vor der Textspalte auf.
        // Im Eroeffnungsduell startet es als Cover-Eule HINTER der Box
        // (die Leinwand liegt unter dem Bild) und bricht an ihrer Kante
        // von oben in die Kreisbahn.
        var O0 = [M[0] - r, M[1]];
        if (D.fromCover && !Lo.stacked) {
          var cfx = C.x0 + 0.42 * C.w, cfy = C.y1 - 0.36 * C.h;
          pts.push([cfx, cfy]);
          wSeg(pts, [cfx + 0.12, cfy + 0.01], [O0[0], O0[1] + 0.12], O0);
        } else if (D.fromCover) {
          var cP = [C.cx + 0.04, C.y0 + 0.07];
          pts.push(cP);
          wSeg(pts, [cP[0] - 0.02, cP[1] - 0.04], [O0[0], O0[1] + 0.06], O0);
        } else if (C && !Lo.stacked) {
          var yT = Math.min(0.5 - sz * 0.6, Math.max(C.y1 + 0.05, 0.38));
          pts.push([-out, yT - 0.02]);
          wSeg(pts, [-hw + 0.25, yT], [C.cx - 0.1, yT + 0.01], [C.cx + 0.05, yT]);
          wSeg(pts, [C.x1 + 0.02, yT - 0.01], [O0[0], O0[1] + 0.14], O0);
        } else {
          pts.push([-out, M[1] + 0.03]);
          wSeg(pts, [-hw + 0.2, M[1] + 0.03], [O0[0], O0[1] + 0.06], O0);
        }
        D.paleKnot = (pts.length - 1) / 3;
        wArc(pts, M[0], M[1], r, Math.PI, Math.PI * 1.5);
        wArc(pts, M[0], M[1], r, Math.PI * 1.5, Math.PI * 2);
        var O1 = [M[0] + r, M[1]];
        if (Lo.stacked || !C) wSeg(pts, [O1[0] + 0.04, O1[1] + 0.06], [out - 0.2, M[1] + 0.05], [out, M[1] + 0.05]);
        else wSeg(pts, [O1[0], O1[1] + 0.08], [O1[0] + 0.05, O1[1] + 0.16], [O1[0] + 0.09, O1[1] + 0.24]);
        face = 1;
      } else {
        // Kommt von rechts unten herauf, kreist oben herum nach links
        // und taucht unter der Box nach links aus.
        var Q0 = [M[0] + r, M[1]];
        if (C && !Lo.stacked) {
          var yB = Math.min(-0.34, T ? T.y0 - 0.06 : -0.34, C.y0 - 0.1);
          pts.push([out, yB]);
          wSeg(pts, [Q0[0] + 0.45, yB], [Q0[0] + 0.02, Q0[1] - 0.24], Q0);
        } else {
          pts.push([out, M[1] - 0.03]);
          wSeg(pts, [hw - 0.2, M[1] - 0.03], [Q0[0], Q0[1] - 0.06], Q0);
        }
        wArc(pts, M[0], M[1], r, 0, Math.PI * 0.5);
        wArc(pts, M[0], M[1], r, Math.PI * 0.5, Math.PI);
        var Q1 = [M[0] - r, M[1]];
        if (C && !Lo.stacked) {
          var yU = Math.min(C.y0 - 0.1, -0.3);
          wSeg(pts, [Q1[0], Q1[1] - 0.14], [C.x1 - 0.05, yU + 0.02], [C.cx, yU]);
          wSeg(pts, [C.x0 - 0.05, yU - 0.01], [-hw + 0.1, yU + 0.05], [-out, yU + 0.12]);
        } else {
          wSeg(pts, [Q1[0] - 0.04, Q1[1] - 0.06], [-out + 0.2, M[1] - 0.04], [-out, M[1] - 0.04]);
        }
        face = -1;
      }
      return { pts: pts, face: face };
    }

    if (!C) {
      // Ohne Cover-Mass die heutigen Bahnen: gerade durch das untere Band.
      var y0 = -0.25 + Math.random() * 0.27;
      if (o.pale) return { pts: [[-out, y0], [-hw * 0.4, y0 + 0.02], [o.ze - 0.2, y0 + 0.03], [o.ze + 0.02, y0 + 0.04]], face: 1 };
      return { pts: [[out, y0], [hw * 0.3, y0 + 0.02], [-hw * 0.3, y0 - 0.02], [-out, y0]], face: -1 };
    }

    if (role === 'cover') {
      pts = [];
      if (!Lo.stacked) {
        // Desktop: aus der Cover-Eule nach links aus der Box, im Bogen
        // nach links oben in das textfreie Viertel.
        var fx = C.x0 + 0.42 * C.w, oy = C.y1 - 0.36 * C.h;
        var E = [C.x0 - 0.01, oy + 0.035];
        var yTop = Math.min(0.5 - sz * 0.55, Math.max(0.38, C.y1 + 0.06));
        var Ap = [Math.max(-hw + 0.12, C.x0 - 0.12), lerp(E[1], yTop, 0.72)];
        var Z = [-out, yTop];
        pts.push([fx, oy]);
        wSeg(pts, [fx - 0.06, oy + 0.004], [E[0] + 0.07, E[1] - 0.012], E);
        wSeg(pts, [E[0] - 0.09, E[1] + 0.02], [Ap[0] + 0.03, Ap[1] - 0.09], Ap);
        wSeg(pts, [Ap[0] - 0.02, Ap[1] + 0.07], [Z[0] + 0.3, Z[1] + 0.01], Z);
      } else {
        // Hochformat: unter der Box-Unterkante hervor, durch das Band
        // vor der Eyebrow nach links.
        var bandY = T ? (C.y0 + T.y1) / 2 : C.y0 - 0.04;
        var P0 = [C.cx + 0.04, C.y0 + 0.07];
        var E2 = [C.cx - 0.1 * C.w, bandY + 0.004];
        pts.push(P0);
        wSeg(pts, [P0[0] - 0.01, P0[1] - 0.05], [E2[0] + 0.06, E2[1] - 0.01], E2);
        wSeg(pts, [E2[0] - 0.12, E2[1] + 0.01], [-out + 0.15, bandY + 0.012], [-out, bandY + 0.016]);
      }
      return { pts: pts, face: -1 };
    }

    // Solo-Fluege, Baender abwechselnd ueber und unter der Box
    var band = opts.band || o.band;
    o.band = band === 'top' ? 'bottom' : 'top';
    if (Lo.stacked) {
      var topRoom = 0.5 - C.y1;
      var yTop = C.y1 + topRoom * 0.5;
      var yLow = T ? (C.y0 + T.y1) / 2 : C.y0 - 0.04;
      var useTop = (o.pale ? band === 'top' : band !== 'bottom') && topRoom > sz * 0.6;
      var yy = useTop ? yTop : yLow;
      if (o.pale) return { pts: [[-out, yy - 0.01], [-hw * 0.4, yy + 0.008], [hw * 0.4, yy - 0.006], [out, yy + 0.01]], face: 1 };
      return { pts: [[out, yy + 0.01], [hw * 0.4, yy - 0.008], [-hw * 0.4, yy + 0.006], [-out, yy - 0.01]], face: -1 };
    }
    var gx = C.x1 + 0.4 * ((T ? T.x0 : C.x1 + 0.3) - C.x1);
    if (o.pale) {
      pts = [];
      if (band === 'top') {
        var yb = clamp(C.y1 + 0.45 * sz, 0.34, 0.5 - 0.5 * sz);
        pts.push([-out, yb - 0.03]);
        wSeg(pts, [-hw + 0.3, yb - 0.015], [C.cx - 0.25, yb + 0.012], [C.cx, yb + 0.004]);
        wSeg(pts, [C.cx + 0.22, yb], [gx - 0.04, C.y1 + 0.02], [gx, C.y1 - 0.12]);
        wSeg(pts, [gx + 0.02, C.y1 - 0.18], [o.ze - 0.02, C.y1 - 0.2], [o.ze + 0.03, C.y1 - 0.22]);
      } else {
        var yl = Math.min(C.y0 - 0.04 - 0.4 * sz, -0.26);
        pts.push([-out, yl + 0.02]);
        wSeg(pts, [-hw + 0.3, yl + 0.01], [C.cx - 0.25, yl - 0.012], [C.cx, yl]);
        wSeg(pts, [C.cx + 0.2, yl + 0.006], [gx - 0.06, yl + 0.01], [gx, yl + 0.12]);
        wSeg(pts, [gx + 0.03, yl + 0.2], [o.ze, yl + 0.26], [o.ze + 0.03, yl + 0.32]);
      }
      return { pts: pts, face: 1 };
    }
    // dunkles Wesen: unten von rechts, unter der Box hindurch, steigt im
    // linken Korridor auf; oben nur, wenn ueber der Textspalte Platz ist.
    var yTopD = Math.max(T ? T.y1 : 0, C.y1) + 0.5 * sz + 0.01;
    if (band === 'top' && yTopD < 0.5 - 0.35 * sz) {
      return { pts: [[out, yTopD], [hw * 0.3, yTopD + 0.02], [-hw * 0.3, yTopD - 0.01], [-out, yTopD + 0.01]], face: -1 };
    }
    var yd = Math.min(C.y0 - 0.06 - 0.4 * sz, T ? T.y0 - 0.05 : -0.3, -0.3);
    pts = [[out, yd + 0.02]];
    wSeg(pts, [hw * 0.4, yd + 0.01], [C.cx + 0.25, yd - 0.012], [C.cx, yd]);
    var lx = Math.max(-hw + 0.05, C.x0 - 0.1);
    wSeg(pts, [C.x0 + 0.05, yd + 0.006], [lx + 0.05, yd + 0.06], [lx, yd + 0.2]);
    wSeg(pts, [lx - 0.04, yd + 0.3], [-out + 0.1, yd + 0.38], [-out, yd + 0.42]);
    return { pts: pts, face: -1 };
  };

  // Geschwindigkeitsprofil der Mondpassage bis zur Scheibe: rein von
  // der Bahnposition abhaengig, damit die Ankunft vorab integrierbar ist.
  function passageG(o, u) { return lerp(1, 0.38, wSmoothstep(o.uMoon - 0.24, o.uMoon - 0.02, u)); }

  // launchOwl(o, opts): opts.role 'cover' | 'solo' | 'passage' | 'duel',
  // opts.at = Zeitpunkt des Schluesselmoments (Cover: Abflug, Passage:
  // auf der Scheibe, Duell: setzt launchDuel), opts.delay = Startversatz,
  // opts.band = 'top' | 'bottom' fuer Solo-Fluege, opts.path = eigene Bahn.
  Scene.prototype.launchOwl = function (o, opts) {
    opts = (opts && typeof opts === 'object') ? opts : {};
    var role = opts.role || 'solo', r = Math.random;
    var k = this.wesenK || Math.min(1, this.viewW / 1.1);
    o.role = role;
    o.k = k;
    o.size = (role === 'passage' ? 0.095 : role === 'cover' ? 0.15 : role === 'duel' ? 0.122 : 0.12 + r() * 0.025) * k;
    // fitMoon() (Paket himmel) verkleinert den Mond auf schmalen
    // Querformaten auf 0,85/0,75 -- die Silhouette schrumpft mit, damit
    // sie die Scheibe weiter rahmt statt sie zu verdecken. Hochkant
    // schrumpft sie schon ueber k staerker als der Mond.
    if (role === 'passage' && !this.stacked && this.moonScale < 1) o.size *= this.moonScale;
    var route = opts.path ? { pts: opts.path, face: opts.face || (o.pale ? 1 : -1) } : this.wesenRoute(o, role, opts);
    // Am Mond fliegt das helle Wesen nach oben aus, statt sich vor der
    // Textspalte aufzuloesen (deren Partikel-Deckel laege weit links).
    o.noDis = role === 'duel' && !!opts.duel && opts.duel.place === 'moon';
    o.path = wPath(route.pts);
    o.face = route.face;
    o.L = o.path.L;
    o.u = 0;
    o.started = false;
    o.tempo = 1;
    o.rot = 0; o.bob = 0; o.sag = 0;
    o.dis = 0; o.disT = 0; o.emitted = 0;
    o.postT = -1; o.held = false; o.spk = false; o.seen = false;
    o.active = true;
    o.go = this.t + (opts.delay || 0);
    o.auto = role === 'solo';
    o.v0 = role === 'cover' ? 0.135 * k : role === 'passage' ? 0.26 * k : role === 'duel' ? 0.27 * k : (0.16 + r() * 0.04) * k;
    o.beats0 = role === 'cover' ? 3 : 2 + (r() < 0.4 ? 1 : 0);
    o.fm = 0;
    if (role === 'cover') {
      o.uEdge = o.path.knots[1];
      if (opts.at !== undefined) o.go = opts.at;
    } else if (role === 'passage') {
      o.uMoon = o.path.knots[1];
      // Ankunft auf der Scheibe vorab integrieren, Startversatz daraus
      var tt = 0, uu = 0, h = 1 / 120;
      while (uu < o.uMoon && tt < 30) { uu += o.v0 * passageG(o, uu) * h; tt += h; }
      // Reicht die Zeit bis zum Termin nicht (schmale Bildschirme, kleines
      // k), fliegt es entsprechend schneller an: passageG haengt nur an u,
      // die Anflugzeit skaliert also exakt mit 1/v0.
      var avail = opts.at !== undefined ? opts.at - this.t : 1e9;
      if (tt > avail && avail > 0.5) { o.v0 *= tt / avail; tt = avail; }
      o.tMoon = tt;
      o.go = opts.at !== undefined ? opts.at - tt : this.t + (opts.delay || 0);
    } else if (role === 'duel') {
      var D = opts.duel;
      o.uO0 = o.path.knots[o.pale ? D.paleKnot : 1];
      o.uO1 = o.path.knots[(o.pale ? D.paleKnot : 1) + 2];
      // Anflug mit v0, die letzten 0,4 s linear auf 35 % gebremst
      o.tApp = Math.max(0.4, (o.uO0 - 0.27 * o.v0) / o.v0 + 0.4);
      o.go = Infinity;
    }
    o.sprite.visible = false;
    o.sprite.material.opacity = 0;
  };

  // Erzwungenes Duell: Treffpunkt links der Mitte in der Gasse zwischen
  // Cover und Textspalte (Hochformat: unter der Box, rechts der
  // Eyebrow). Beide Wesen erreichen den Kreisbahnanfang zur selben
  // Zeit; Funke bei tm. Gibt tm zurueck.
  // Ohne freie Gasse (grosses flaches Produktbild: am Desktop ~70 px,
  // hochkant ~25 px zwischen Bild und Text) liefe das Duell hinter dem
  // Bild ab. Dann umkreisen sich die Wesen vor dem Mond -- sein Platz
  // ist auf jedem Layout gegen die Schrift abgesichert (fitMoon), und
  // die Kreisbahn liegt auf dem Scheibenrand.
  Scene.prototype.launchDuel = function (atMeet, opts) {
    var Lo = this.wesenLayout(), C = Lo.C, T = Lo.T, k = Lo.k;
    var M, r = 0.085 * k;
    var gap = C && T ? (Lo.stacked ? C.y0 - T.y1 : T.x0 - C.x1) : 1;
    var place = gap < 0.26 * k ? 'moon' : 'gap';
    if (place === 'moon') {
      var mR = MOON_RD * this.cfg.moonSize * (this.moonScale || 1), mY = this.moonY;
      r = Math.max(r, 1.05 * mR);
      // Hochkant steht der Mond halb hinter der oberen Coverkante, frei
      // ist nur der Himmelsstreifen darueber: Bahn in seine Mitte und
      // so eng, dass sie hineinpasst.
      if (Lo.stacked && C) {
        var band = Math.max(0.05, 0.5 - C.y1);
        r = Math.min(r, band * 0.42);
        // Das obere Wesen bleibt ganz auf der Leinwand (ueber ihr liegt
        // der durchsichtige Header, ein Schnitt waere eine harte Linie);
        // unten taucht es lieber kurz hinter das Cover.
        mY = Math.min(C.y1 + band * 0.5, 0.5 - r - 0.061 * k * OWL_ZOOM - 0.004);
      }
      M = [this.moonX, mY];
    } else if (C && !Lo.stacked) {
      var tx0 = T ? T.x0 : C.x1 + 0.3;
      // Gasse zwischen Cover und Textspalte; ist sie schmal (um 1000 px),
      // rueckt der Treffpunkt an die Box heran -- das Wesen darf halb
      // hinter dem Cover verschwinden, nie aber unter die Zeilen geraten.
      var mxw = Math.min(C.x1 + 0.55 * (tx0 - C.x1), tx0 - 0.085 * k - 0.07 * k);
      M = [Math.max(C.x1 - 0.03, mxw), clamp(C.cy + 0.04, C.y0 + 0.1, C.y1 - 0.12)];
    } else if (C) {
      // Hochformat: links der Mitte knapp ueber der Bildunterkante --
      // dort laeuft die Box-Unterkante schraeg hoch und laesst das
      // groesste freie Feld ueber der Eyebrow.
      M = [C.x0 + 0.42 * C.w, T ? Math.max(C.y0 + 0.012, T.y1 + 0.03) : C.y0 - 0.05];
    } else {
      M = [-0.1 * this.viewW, -0.08];
    }
    var D = { M: M, r: r, place: place, paleKnot: 1, k: k, sparked: false,
              fromCover: !!(opts && opts.fromCover && C && place === 'gap'), opening: !!(opts && opts.fromCover) };
    D.ringAsp = 0.72;
    D.ringTilt = (Math.random() - 0.5) * 0.24;
    D.ringStart = Math.random() * Math.PI * 2;
    // Funke und Ring duerfen die Textspalte nie erreichen: Desktop
    // waagrecht gegen T.x0, Hochformat senkrecht gegen die Oberkante
    // der Textspalte (Eyebrow). Die Ellipse ist dort um ringAsp flacher.
    var room = C && !Lo.stacked && T ? T.x0 - M[0] : 1;
    var sMax = 0.34 * k, rMax = 0.4 * k;
    if (place === 'moon') {
      // Am Mond: Abstand zur naechsten Schriftzeile und zum oberen Rand
      var lines = this.forestText ? this.forestText() : [];
      var roomM = Math.min(0.5 - M[1] + 0.02, Lo.hw - M[0] + 0.05);
      for (var li = 0; li < lines.length; li++) {
        var Ln = lines[li];
        var ddx = Math.max(Ln.x0 - M[0], 0, M[0] - Ln.x1), ddy = Math.max(Ln.y0 - M[1], 0, M[1] - Ln.y1);
        roomM = Math.min(roomM, Math.sqrt(ddx * ddx + ddy * ddy));
      }
      D.sparkSize = clamp(2 * (roomM - 0.02), 0.06 * k, sMax);
      D.ringMax = clamp(roomM - 0.03, 0.04 * k, rMax);
    } else if (Lo.stacked && T) {
      var roomY = M[1] - T.y1;
      var roomX = Math.min(M[0] + Lo.hw, Lo.hw - M[0]);
      sMax = Math.min(sMax, 2 * (roomY - 0.008), 2 * (roomX - 0.01));
      rMax = Math.min(rMax, (roomY - 0.01) / (D.ringAsp * 1.0), roomX - 0.02);
      D.sparkSize = Math.max(0.03, sMax);
      D.ringMax = Math.max(0.025, rMax);
    } else {
      D.sparkSize = Math.max(0.08, Math.min(sMax, 2 * (room - 0.03)));
      D.ringMax = Math.max(0.06, Math.min(rMax, room - 0.04));
    }
    var A = this.owls[0], B = this.owls[1];
    this.launchOwl(A, { role: 'duel', duel: D });
    this.launchOwl(B, { role: 'duel', duel: D });
    // Reicht die Zeit bis zum Termin nicht, fliegt das Wesen schneller
    // an (die Anflugzeit ist uO0 / v0 + 0,13), hoechstens um 30 % --
    // darueber rutscht der Funke lieber etwas spaeter. Im Eroeffnungsduell
    // bis 70 %: auf breiten Bildschirmen ist der Weg lang, der Funke soll
    // trotzdem vor 5 s kommen.
    if (atMeet !== undefined) {
      var avail = atMeet - 0.7 - this.t, fMax = D.opening ? 1.7 : 1.3;
      for (var q = 0; q < 2; q++) {
        var oq = q ? B : A;
        if (oq.tApp > avail && avail > 0.8) {
          var f = Math.min(fMax, oq.uO0 / (oq.v0 * (avail - 0.13)));
          oq.v0 *= f;
          oq.tApp = Math.max(0.4, oq.uO0 / oq.v0 + 0.13);
        }
      }
    }
    var tO0 = Math.max(atMeet !== undefined ? atMeet - 0.7 : -1e9, this.t + Math.max(A.tApp, B.tApp));
    A.tO0 = B.tO0 = tO0;
    A.go = tO0 - A.tApp;
    B.go = tO0 - B.tApp;
    D.tm = tO0 + 0.7;
    this.duel = D;
    return D.tm;
  };

  Scene.prototype.wesenShuffle = function () {
    var best = null;
    for (var tries = 0; tries < 24; tries++) {
      var d = ['pale', 'duel', 'passage', 'dark', 'duel'];
      for (var i = d.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1)), s = d[i];
        d[i] = d[j]; d[j] = s;
      }
      var run = this.wesenTail, ok = true;
      for (i = 0; i < d.length; i++) {
        run = d[i] === 'duel' ? 0 : run + 1;
        if (run > 2) { ok = false; break; }
      }
      best = d;
      if (ok) break;
    }
    return best;
  };

  Scene.prototype.wesenSchedule = function () {
    var t = this.t, rel = t - this.tStart, cfg = this.cfg;
    var A = this.owls[0], B = this.owls[1];
    var it = this.wesenIntro[0];
    if (it) {
      // Vorlauf: das Duell braucht bis zu ~5 s Anflug und startet, sobald
      // beide Wesen frei sind; launchDuel haelt den Funken auf it.at.
      if (rel < it.at - (it.kind === 'cover' ? 1.6 : it.kind === 'duel' ? 9 : 5)) return;
      var at = this.tStart + it.at;
      if (it.kind === 'cover') {
        if (A.active) return;
        this.launchOwl(A, { role: 'cover', at: at });
      } else if (it.kind === 'passage') {
        if (B.active) return;
        this.launchOwl(B, { role: 'passage', at: at });
        this.wesenLastPassage = it.at;
      } else {
        if (A.active || B.active) return;
        this.wesenLastDuel = this.launchDuel(at, { fromCover: it.fromCover }) - this.tStart;
      }
      this.wesenIntro.shift();
      return;
    }
    if (A.active || B.active) return;
    var overdue = rel - this.wesenLastDuel > 44;
    if (rel < this.wesenNext && !overdue) return;
    if (!this.wesenDeck.length) this.wesenDeck = this.wesenShuffle();
    // Weiche Passage-Garantie: nach ~100 s ohne Mondpassage wird sie im
    // Stapel vorgezogen (das Duell hat Vorrang).
    if (rel - this.wesenLastPassage > 100 && this.wesenDeck[0] !== 'passage') {
      var pi = this.wesenDeck.indexOf('passage');
      if (pi >= 0) this.wesenDeck.splice(pi, 1);
      this.wesenDeck.unshift('passage');
    }
    var kind;
    if (overdue) {
      var di = this.wesenDeck.indexOf('duel');
      if (di >= 0) this.wesenDeck.splice(di, 1);
      kind = 'duel';
    } else {
      kind = this.wesenDeck.shift();
    }
    if (kind === 'duel') {
      this.wesenLastDuel = this.launchDuel() - this.tStart;
      this.wesenTail = 0;
    } else {
      this.wesenTail++;
      if (kind === 'passage') { this.launchOwl(B, { role: 'passage' }); this.wesenLastPassage = rel; }
      else this.launchOwl(kind === 'pale' ? A : B, { role: 'solo' });
    }
    this.wesenNext = rel + cfg.owlEvery * (0.7 + Math.random() * 0.8);
  };

  var wTmp = { x: 0, y: 0, tx: 0, ty: 0 };

  Scene.prototype.stepOwl = function (o, dt, px) {
    var t = this.t, sp = o.sprite;
    if (!o.active || t < o.go) {
      if (sp.visible) sp.visible = false;
      return;
    }
    var role = o.role, g = 1, tempoOn = true, orbit = false;
    if (!o.started) {
      o.started = true;
      owlFlapStart(o, o.beats0);
      // Frame-Raster ausgleichen, damit Ankunftszeiten exakt bleiben
      o.u = o.v0 * (role === 'cover' ? 3.2 : 1) * Math.min(0.05, t - o.go);
    }
    if (role === 'cover') {
      g = lerp(3.2, 1, wSmoothstep(o.uEdge, o.uEdge + 0.1, o.u));
    } else if (role === 'passage') {
      tempoOn = false;
      if (o.u < o.uMoon) g = passageG(o, o.u);
      else {
        g = 0.38;
        if (o.postT >= 0) {
          g = lerp(0.38, 1.2, wSmooth01((o.postT - 0.3) / 0.6));
          tempoOn = o.postT > 0.9;
        }
      }
    } else if (role === 'duel') {
      tempoOn = false;
      var a = t - o.tO0;
      if (a < 0) g = a < -0.4 ? 1 : lerp(1, 0.35, (a + 0.4) / 0.4);
      else if (a < 1.4) orbit = true;
      else {
        a -= 1.4;
        g = a < 0.35 ? lerp(0.35, 1.3, a / 0.35) : a < 1.35 ? 1.3 : lerp(1.3, 1, clamp((a - 1.35) / 0.6, 0, 1));
      }
    }
    o.tempo += ((o.fm === 1 ? 1.18 : 0.94) - o.tempo) * Math.min(1, dt * 3);
    if (orbit) {
      // halber Umlauf in 1,4 s; die Mischung aus linear und smoothstep
      // laesst Ein- und Austritt mit den 35 % des Anflugs zusammenfallen
      var s = (t - o.tO0) / 1.4;
      o.u = o.uO0 + (o.uO1 - o.uO0) * lerp(s, wSmooth01(s), 0.42);
    } else {
      o.u += o.v0 * g * (tempoOn ? o.tempo : 1) * dt;
    }
    if (o.u >= o.L) { this.endOwl(o); return; }
    var P = wAt(o.path, o.u, wTmp);
    var x = P.x, y = P.y;

    // Rollen-Momente im Schlagrhythmus
    if (role === 'passage') {
      var near = Math.abs(x - this.moonX) < 0.05 * Math.max(0.45, o.k);
      if (near && o.fm !== 3 && !o.held) { o.fm = 3; o.held = true; }
      if (o.held && o.postT < 0 && !near && o.u > o.uMoon) { o.fm = 0; o.gl = 9; o.fx = 9; o.postT = 0; }
      if (o.postT >= 0) {
        var pPrev = o.postT;
        o.postT += dt;
        if (pPrev < 0.3 && o.postT >= 0.3) { owlFlapStart(o, 3); o.auto = true; }
      }
    } else if (role === 'duel') {
      var b = t - o.tO0;
      if (b >= 0.45 && b < 0.7) o.fm = 3;
      else if (b >= 0.7 && !o.spk) { o.spk = true; owlFlapStart(o, 3); o.auto = true; }
    }
    var pose = owlFlap(o, dt);

    // Nicken mit der Bahn, beim gespiegelten Wesen mit umgekehrtem Vorzeichen
    var ang = Math.atan2(P.ty, P.tx * o.face);
    var rt = clamp(ang * 0.6, -0.18, 0.18) * o.face;
    o.rot += (rt - o.rot) * Math.min(1, dt * 4);
    // Koerperhub pro Schlag, eine Viertelperiode hinter dem Fluegel
    var bt = o.fm === 1 ? -0.009 * o.k * Math.cos(o.ph * Math.PI * 2) : 0;
    o.bob += (bt - o.bob) * Math.min(1, dt * 10);
    // Gleiten sackt leicht ab, die naechste Salve faengt es wieder
    if (o.fm === 1) o.sag = Math.min(0, o.sag + 0.03 * o.k * dt);
    else if (o.fm === 0) o.sag = Math.max(-0.018 * o.k, o.sag - 0.005 * o.k * dt);

    var scl = o.size * (role === 'cover' ? lerp(0.78, 1, wSmooth01(o.u / Math.max(0.01, o.uEdge))) : 1);
    var map = o.maps[(o.face < 0 ? OWL_POSES.length : 0) + pose];
    if (sp.material.map !== map) sp.material.map = map;
    sp.material.rotation = o.rot;
    sp.position.set(x - px * 0.02, y + o.bob + o.sag, 0);
    var ss = scl * OWL_ZOOM;
    sp.scale.set(ss * 1.6, ss, 1);
    // Hat das Wesen das Bild einmal betreten und wieder verlassen, ist
    // der Flug vorbei -- der Rest der Bahn liefe nur unsichtbar weiter
    // und hielte im Hochformat die naechsten Momente auf.
    var hwv = this.viewW / 2;
    var gone = x - ss > hwv || x + ss < -hwv || y - ss * 0.75 > 0.5 || y + ss * 0.75 < -0.5;
    if (!gone) o.seen = true;
    else if (o.seen && role !== 'cover' && !(role === 'duel' && o.u < o.uO1)) { this.endOwl(o); return; }

    var op = o.pale ? 0.95 : 0.85;
    var fadeIn = Math.min(1, (t - o.go) / 0.2);
    if (o.pale && role !== 'cover' && !o.noDis) {
      // Aufloesung vor der Textspalte als Nebelzauber: zeitlich auf
      // ~1,2 s gestreckt, Glimmer stiebt nach links oben davon.
      if (role !== 'duel' || o.u > o.uO1) {
        o.disT = o.stacked
          ? (role === 'duel' ? 0 : wSmoothstep(o.ty1 + 0.02, o.ty1 - 0.04, y))
          : wSmoothstep(o.zs, o.ze, x);
      }
      o.dis += clamp(o.disT - o.dis, -dt / 1.2, dt / 1.2);
      var ne = Math.floor(o.dis * 11.5);
      while (o.emitted < ne) {
        o.emitted++;
        this.emitPart(x + (Math.random() - 0.6) * scl * 0.9, y + (Math.random() - 0.3) * scl * 0.6,
          -(0.03 + Math.random() * 0.05), 0.02 + Math.random() * 0.04, 1.5, 0.5, 0.4, o.xClamp);
      }
      if (o.dis > 0.995) { this.endOwl(o); return; }
    }
    if (role === 'passage') {
      var nm = Math.max(0, 1 - Math.abs(x - this.moonX) / 0.12) * Math.max(0, 1 - Math.abs(y - this.moonY) / 0.12);
      op = 0.85 + 0.15 * nm;
      // moonFlare wird hier gesetzt, das Abklingen gehoert dem Himmel
      this.moonFlare = Math.max(this.moonFlare || 0, nm);
    }
    sp.material.opacity = op * fadeIn * (1 - o.dis);
    sp.visible = true;
  };

  Scene.prototype.endOwl = function (o) {
    o.active = false;
    o.endT = this.t;
    o.sprite.visible = false;
    o.sprite.material.opacity = 0;
  };

  // Teilchen aus dem gemeinsamen Pool: aeltestes wird ueberschrieben.
  Scene.prototype.emitPart = function (x, y, vx, vy, life, alpha, drag, xMax) {
    var best = null, bl = 1e9;
    for (var i = 0; i < this.poolP.length; i++) {
      var q = this.poolP[i];
      if (q.life <= 0) { best = q; break; }
      if (q.life < bl) { bl = q.life; best = q; }
    }
    best.x = Math.min(x, xMax); best.y = y; best.vx = vx; best.vy = vy;
    best.life = best.max = life; best.a = alpha; best.drag = drag; best.xMax = xMax;
    best.c = Math.random() < 0.5 ? this.poolColA : this.poolColB;
    this.pool.visible = true;
  };

  Scene.prototype.stepPool = function (dt) {
    if (!this.pool.visible) return;
    var pos = this.pool.geometry.attributes.position, col = this.pool.geometry.attributes.color;
    var any = false;
    for (var i = 0; i < this.poolP.length; i++) {
      var q = this.poolP[i];
      if (q.life > 0) {
        q.life -= dt;
        q.vx *= 1 - Math.min(1, q.drag * dt);
        q.vy *= 1 - Math.min(1, q.drag * dt);
        q.x = Math.min(q.x + q.vx * dt, q.xMax);
        q.y += q.vy * dt;
      }
      if (q.life > 0) {
        any = true;
        var f = q.life / q.max;
        var a = q.a * Math.min(1, (1 - f) * 8) * f;
        pos.array[i * 3] = q.x; pos.array[i * 3 + 1] = q.y; pos.array[i * 3 + 2] = 0;
        col.array[i * 3] = q.c.r * a; col.array[i * 3 + 1] = q.c.g * a; col.array[i * 3 + 2] = q.c.b * a;
      } else {
        pos.array[i * 3] = 99; col.array[i * 3] = col.array[i * 3 + 1] = col.array[i * 3 + 2] = 0;
      }
    }
    pos.needsUpdate = true;
    col.needsUpdate = true;
    if (!any) this.pool.visible = false;
  };

  // Funke, Ring, Federn und Nebelwirbel des Duells
  Scene.prototype.stepDuelFx = function (dt, px) {
    var D = this.duel, t = this.t, i;
    if (D && !D.sparked && t >= D.tm) {
      D.sparked = true;
      // Der erste Funke weckt den Wald: die Laubwelle laeuft kurz danach
      if (D.opening && this.waveAt == null && t - this.tStart < WALD.waveKulisse) this.waveAt = t - this.tStart + 0.25;
      var mx = D.M[0] - px * 0.02, my = D.M[1];
      this.spark.position.set(mx, my, 0);
      this.sparkT = 0;
      this.ring.position.set(mx, my, 0);
      this.ringT = 0;
      for (i = 0; i < 6; i++) {
        var an = (i + Math.random() * 0.6) / 6 * Math.PI * 2, v = (0.07 + Math.random() * 0.06) * D.k;
        this.emitPart(mx, my, Math.cos(an) * v, Math.sin(an) * v, 0.9, 0.6, 2.5, mx + (D.sparkSize * 0.5));
      }
      // Die naechste Nebelbank darunter wirbelt auf, die Federn
      // trudeln in sie hinein
      var bank = null, bd = 1e9;
      for (i = 0; i < this.layers.length; i++) {
        var LB = this.layers[i];
        if (!LB.mist) continue;
        var dy = my - LB.mesh.position.y;
        var dd = dy >= 0 ? dy : 1 + -dy;
        if (dd < bd) { bd = dd; bank = LB; }
      }
      if (bank) bank.boost = 1;
      D.bank = bank;
      for (i = 0; i < this.feathers.length; i++) {
        var F = this.feathers[i];
        F.t = 0;
        F.x0 = mx + (i ? 0.012 : -0.012);
        F.y = my + (i ? 0.01 : -0.01);
        F.fadeY = bank ? bank.mesh.position.y + 0.04 : my - 0.25;
        F.sprite.scale.set(0.026 * D.k, 0.052 * D.k, 1);
      }
    }
    if (this.sparkT >= 0) {
      this.sparkT += dt;
      var st = this.sparkT, sz = D ? D.sparkSize : 0.2;
      if (st >= 0.75) {
        this.sparkT = -1;
        this.spark.visible = false;
      } else {
        var e;
        if (st < 0.12) {
          var xx = st / 0.12 - 1;
          e = 1.15 * (1 + 2.70158 * xx * xx * xx + 1.70158 * xx * xx);
        } else e = lerp(1.15, 1, wSmooth01((st - 0.12) / 0.18));
        this.spark.scale.set(sz * e, sz * e, 1);
        this.spark.material.opacity = st < 0.1 ? 1 : Math.max(0, 1 - (st - 0.1) / 0.6);
        this.spark.material.rotation = 0.35 * (1 - Math.pow(1 - Math.min(1, st / 0.7), 3));
        this.spark.visible = true;
      }
    }
    if (this.ringT >= 0) {
      this.ringT += dt;
      var rt = this.ringT / 0.9;
      if (rt >= 1) {
        this.ringT = -1;
        this.ring.visible = false;
      } else {
        var R0 = Math.min(0.05 * (D ? D.k : 1), (D ? D.ringMax : 0.3) * 0.5);
        var R = lerp(R0, D ? D.ringMax : 0.3, 1 - Math.pow(1 - rt, 3));
        var asp = D ? D.ringAsp : 0.72;
        this.ring.scale.set(2 * R * 1.08, 2 * R * asp * 1.08, 1);
        this.ring.rotation.z = D ? D.ringTilt : 0;
        var hpx = this.renderer.domElement.height || 900;
        var dpr = this.renderer.getPixelRatio();
        var u = this.ring.material.uniforms;
        u.uPx.value = 1 / (R * hpx);
        u.uHalf.value = 0.75 * dpr / (R * hpx);
        u.uAsp.value = asp;
        // Teilbogen von 30 % schliesst sich in 0,55 s, Spitze 0,35
        u.uSweep.value = lerp(0.3, 1, wSmooth01(rt / 0.6));
        u.uStart.value = D ? D.ringStart : 0;
        u.uOpacity.value = 0.35 * Math.min(1, rt / 0.08) * Math.pow(1 - rt, 1.3);
        this.ring.visible = true;
      }
    }
    for (i = 0; i < this.feathers.length; i++) {
      var Fe = this.feathers[i];
      if (Fe.t < 0) continue;
      Fe.t += dt;
      var kf = D ? D.k : 1;
      Fe.y -= 0.035 * kf * dt;
      var w = 2.2 * Fe.t + Fe.ph;
      Fe.sprite.position.set(Fe.x0 + 0.03 * kf * Math.sin(w), Fe.y, 0);
      Fe.sprite.material.rotation = 0.5 * Math.cos(w);
      var fo = Math.min(1, Fe.t / 0.25) * (1 - wSmoothstep(Fe.fadeY + 0.05, Fe.fadeY - 0.03, Fe.y)) *
        (1 - wSmoothstep(7, 8, Fe.t));
      Fe.sprite.material.opacity = 0.9 * fo;
      Fe.sprite.visible = true;
      if (Fe.t > 0.5 && fo <= 0.001) { Fe.t = -1; Fe.sprite.visible = false; }
    }
  };

  // Uebernimmt die Funken-Skalierung aus resize() und legt Groessen,
  // Deckel und Korridore an das aktuelle Layout. Beim ersten Aufruf
  // werden alle Shader und Texturen der Wesen vorab zur GPU gebracht,
  // damit der erste Funke nicht mitten im Duell ruckelt.
  Scene.prototype.layoutCreatures = function () {
    if (this.cover === undefined || this.wesenOwnLayout) {
      this.wesenOwnLayout = true;
      this.wesenMeasure();
    }
    var a = this.viewW;
    this.wesenK = Math.min(1, a / 1.1);
    var Lo = this.wesenLayout();
    if (this.glim) {
      var u = this.glim.material.uniforms;
      u.uScaleX.value = a * 0.8;
      u.uPR.value = this.renderer.getPixelRatio();
      u.uCapX.value = Lo.T && !Lo.stacked ? Lo.T.x0 - 0.04 : Lo.T ? 1e3 : 0;
      u.uCapY.value = Lo.T && Lo.stacked ? Lo.T.y1 + 0.02 : -1e3;
    }
  };

  // Das Vorwaermen lief frueher hier, also schon in resize() und
  // damit im Boot-Task. renderer.compile() geht in r149 per traverse
  // ueber ALLE Objekte, sichtbar oder nicht -- es haette auch die drei
  // Himmel-Programme (Mond, Sterne, Licht) mitkompiliert, die update()
  // bewusst gestaffelt in Bild 2-4 aufwaermt. Darum jetzt als fuenfte
  // Stufe derselben Staffel (Aufruf aus update()); die Programme der
  // ersten vier Stufen liegen dann schon im Cache. Das erste Wesen
  // erscheint nach ~0,5 s, der Funke bei ~4 s -- beides weit danach.
  Scene.prototype.wesenWarmUp = function () {
    if (!this.wesenWarm && this.renderer.compile) {
      this.wesenWarm = true;
      var hidden = [this.pool, this.spark, this.ring, this.feathers[0].sprite, this.feathers[1].sprite,
        this.owls[0].sprite, this.owls[1].sprite];
      for (var i = 0; i < hidden.length; i++) hidden[i].visible = true;
      try { this.renderer.compile(this.scene, this.camera); } catch (e) { /* nur Vorwaermen */ }
      for (i = 0; i < hidden.length; i++) hidden[i].visible = false;
      if (this.renderer.initTexture) {
        for (i = 0; i < this.ownTextures.length; i++) this.renderer.initTexture(this.ownTextures[i]);
      }
    }
  };

  Scene.prototype.update = function (dt) {
    var cfg = this.cfg;
    this.t += dt;
    var t = this.t;

    // Zeiger traege nachfuehren, sonst ruckt die Parallaxe
    this.pointer.x = lerp(this.pointer.x, this.pointer.tx, Math.min(1, dt * 2.5));
    this.pointer.y = lerp(this.pointer.y, this.pointer.ty, Math.min(1, dt * 2.5));

    // Leerlauf-Drift unter dem Zeiger: zwei langsame, inkommensurable
    // Sinusse. Ohne ihn war die Parallaxe -- die einzige Quelle von
    // Tiefe zwischen den Ebenen -- fuer Touch-Nutzer unsichtbar, und
    // das ist die Mehrheit der Shop-Besucher (Ghosthaven-Lehre).
    var px = this.pointer.x + Math.sin(t * 0.05) * 0.35;
    var py = this.pointer.y + Math.sin(t * 0.037 + 1.7) * 0.2;

    // Atemkurve der Nacht: an ihr haengt alles Organische gemeinsam
    // (Halo, Lichtschacht, Nebelgrund, Funken), zwei inkommensurable
    // Frequenzen, Wertebereich 0..1 -- das Ghosthaven-Rezept.
    var br = 0.5 + 0.35 * Math.sin(t * 0.11) + 0.15 * Math.sin(t * 0.073 + 1.7);

    // Mondposition vor den Ebenen rechnen -- die Nebelbaenke brauchen
    // sie fuer die Aufhellung unter der Scheibe. Prozent der
    // SICHTBAREN Breite, nicht der Weltkoordinaten: nur so steht der
    // Mond auf jedem Geraet dort, wo der Regler ihn hinstellt.
    // im Hochformat setzt measureStage() den Mond an
    // die obere Coverkante (moonBase); sonst gilt der Regler.
    this.moonFlare = Math.max(0, this.moonFlare - dt * 0.8);
    var mb = this.moonBase;
    this.moonX = (mb ? mb.x : (cfg.moonX - 0.5) * this.viewW) - px * 0.008;
    this.moonY = (mb ? mb.y : (0.5 - cfg.moonY)) - py * 0.004;

    // Ebenen, Nebel, Kulissen-Wind und Glimmwelle
    this.updateForest(dt, t, px, py, br);

    // Mond, Strahlenkranz, Sterne, Faecher.
    // Die Scheibe bleibt konstant. Halo und Kernhof atmen mit der
    // Nacht, und die Passage eines Wesens laesst beide aufgluehen
    // (moonFlare setzt der Eulen-Block). Der weite Halo ist gegenueber
    // frueher um 30 % zurueckgenommen, der Kernhof traegt das Licht.
    var it = t - this.tStart;
    // Shader gestaffelt aufwaermen: Mond, Sterne und Licht haben je ein
    // eigenes Programm. Alle drei im ersten Bild kompiliert verlaengerte
    // den Boot-Task um ~70 ms (Lighthouse mobil: LCP +300 ms). Im Auftakt
    // kommt darum je Bild eines dazu -- die Leinwand blendet ohnehin
    // erst ueber 1 s ein, die drei Bilder sieht niemand. Das Standbild
    // (reduced-motion, tStart 60 s zurueck) zeigt sofort alles.
    var warm = this.skyWarm = (this.skyWarm || 0) + 1;
    var gate = it < 1 ? warm : 99;
    this.moon.visible = gate >= 2;
    this.stars.visible = gate >= 3;
    this.rays.visible = !!this.raysOn && gate >= 4;
    // fuenfte Stufe -- Programme der Wesen (siehe wesenWarmUp).
    // Im Standbild (reduced motion) fliegt nichts, dort entfaellt sie.
    if (gate >= 5 && !this.wesenWarm && !this.forestStill) this.wesenWarmUp();
    var mu = this.moon.material.uniforms;
    this.moon.position.set(this.moonX, this.moonY, 0);
    this.moon.scale.set(this.moonScale, this.moonScale, 1);
    mu.uHaloA.value = 0.7 * clamp(0.5 + 0.28 * br + 0.3 * this.moonFlare, 0, 1.1);
    mu.uKernA.value = 0.3 + 0.06 * br + 0.4 * this.moonFlare;

    // Strahlenkranz: der allererste Moment. 0.4-1.4 s zuendet er mit
    // ease-out auf 0.09 und schiesst dabei um ein Fuenftel nach aussen,
    // bis 2.4 s sinkt er auf den Ruhewert, der dann mit der Nacht atmet.
    if (this.rays.visible) {
      var ru = this.rays.material.uniforms, rest = 0.05 + 0.02 * br, ra, grow = 1, e;
      if (it < 0.4) { ra = 0; grow = 0.8; }
      else if (it < 1.4) { e = 1 - Math.pow(1.4 - it, 3); ra = 0.09 * e; grow = 0.8 + 0.2 * e; }
      else if (it < 2.4) { e = it - 1.4; e = e * e * (3 - 2 * e); ra = lerp(0.09, rest, e); }
      else ra = rest;
      ru.uOpacity.value = ra * RAY_GAIN;
      ru.uAngle.value = t * 0.01;
      this.rays.scale.set(this.raysR * 2 * grow, this.raysR * 2 * grow, 1);
    }

    this.stars.material.uniforms.uTime.value = t;

    // Faecher: ~40 s vom linken Rand bis kurz vor die Textspalte, dann
    // 10-20 s Pause. Die harte Grenze haelt der Shader (uXMax/uYMin).
    if (this.fanPause > 0) {
      this.fanPause -= dt;
    } else {
      this.fanP += dt / 40;
      if (this.fanP >= 1) { this.fanP = 0; this.fanPause = 10 + Math.random() * 10; }
    }
    var fp = this.fanP;
    var fenv = this.fanPause > 0 ? 0 :
      clamp(fp / 0.1, 0, 1) * (1 - clamp((fp - 0.7) / 0.3, 0, 1));
    fenv = fenv * fenv * (3 - 2 * fenv);
    this.fan.visible = fenv > 0.001 && this.fanEnd != null && gate >= 4;
    if (this.fan.visible) {
      this.fan.position.set(lerp(-this.viewW / 2 - 0.45, this.fanEnd, fp) - px * 0.035, 0.03, 0);
      this.fan.material.uniforms.uOpacity.value = FAN_PEAK * fenv * (0.85 + 0.15 * br);
    }

    // Glimmer -- Drift und Pulse rechnet der Shader
    if (this.glim) {
      var gu = this.glim.material.uniforms;
      gu.uTime.value = t;
      gu.uBr.value = br;
      this.glim.position.x = -px * 0.03;
    }

    // Wesen, Duell, Teilchen
    if (!this.wesenHold) this.wesenSchedule();
    for (var k = 0; k < this.owls.length; k++) this.stepOwl(this.owls[k], dt, px);
    this.stepDuelFx(dt, px);
    this.stepPool(dt);
  };

  Scene.prototype.render = function () {
    this.renderer.render(this.scene, this.camera);
  };

  Scene.prototype.resize = function () {
    // Mass ist die Leinwand selbst, nicht die Buehne: unter dem
    // schwebenden Header (k-immersive) beginnt sie erst unterhalb von
    // Leiste und Navigation, die Buehne aber am Dokumentanfang.
    var w = this.canvas.clientWidth, h = this.canvas.clientHeight;
    if (!w || !h) return false;
    // Ghosthaven-Mass: ueber 1,6 bringt diese weiche Bildsprache
    // nichts mehr, kostet aber quadratisch Fuellrate -- und die
    // Szene ist reine Fuellraten-Last aus transparenten Quads.
    var dpr = clamp(window.devicePixelRatio || 1, 1, w > 750 ? 1.6 : 1.3);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);
    var a = w / h;
    this.camera.left = -a / 2;
    this.camera.right = a / 2;
    this.camera.updateProjectionMatrix();
    this.viewW = a;
    if (this.measureStage) this.measureStage();
    if (this.layoutForest) this.layoutForest();
    if (this.layoutCreatures) this.layoutCreatures();
    return true;
  };

  // Vermessung der Buehne. Liefert Cover und
  // Textspalte in Weltkoordinaten (x = (px/w - 0.5) * viewW,
  // y = 0.5 - py/h, relativ zur Leinwand), damit Licht und Wesen sich
  // nach dem echten Layout richten statt nach geschaetzten Prozenten:
  //   this.cover   {x0, x1, y0 (unten), y1 (oben), cx, cy, w, h}
  //   this.textCol {x0, x1, y0 (unten), y1 (oben)}
  //   this.stacked  Textblock UNTER dem Cover (Hochformat/schmal)
  // null, wenn nicht messbar -- dann bleibt alles beim Alten (Mond am
  // Regler, kein Strahlenkranz). Laeuft aus resize(), also auch nach
  // retryWhenSized und beim entprellten Resize.
  Scene.prototype.measureStage = function () {
    var cfg = this.cfg, root = this.root, vw = this.viewW;
    this.cover = null;
    this.textCol = null;
    this.moonBase = null;
    this.moonScale = 1;
    var cv = this.canvas.getBoundingClientRect();
    var w = cv.width, h = cv.height;
    // Der Himmel fuellt immer genau die sichtbare Breite
    this.sky.scale.set(vw + 0.1, 1, 1);
    var su = this.stars.material.uniforms;
    su.uHalfW.value = vw / 2;
    su.uDpr.value = this.renderer.getPixelRatio();
    if (!w || !h) return;
    function wx(p) { return (p / w - 0.5) * vw; }
    function wy(p) { return 0.5 - p / h; }

    var img = root.querySelector('.sylvans-hero__image');
    if (img) {
      var r = img.getBoundingClientRect();
      // Die Schwebe-Animation des Elternelements herausrechnen: gemessen
      // wird die Ruhelage, nicht der Moment.
      var dy = 0;
      try {
        var tf = window.getComputedStyle(img.parentNode).transform;
        if (tf && tf !== 'none' && window.DOMMatrixReadOnly) dy = new DOMMatrixReadOnly(tf).m42 || 0;
      } catch (e) { dy = 0; }
      if (r.width > 4 && r.height > 4) {
        var cx0 = wx(r.left - cv.left), cx1 = wx(r.right - cv.left);
        var cyT = wy(r.top - dy - cv.top), cyB = wy(r.bottom - dy - cv.top);
        this.cover = { x0: cx0, x1: cx1, y0: cyB, y1: cyT, cx: (cx0 + cx1) / 2, cy: (cyB + cyT) / 2, w: cx1 - cx0, h: cyT - cyB };
      }
      // Ohne geladenes Bild stimmt das Seitenverhaeltnis noch nicht
      if (!img.complete && !img.__sylMeasureWired) {
        var self = this;
        img.__sylMeasureWired = true;
        var onLoad = function () {
          img.__sylMeasureWired = false;
          if (!self.dead && self.resize()) { self.update(0); self.render(); }
        };
        img.addEventListener('load', onLoad, { once: true });
        this.teardown.push(function () { img.removeEventListener('load', onLoad); img.__sylMeasureWired = false; });
      }
    }
    var body = root.querySelector('.sylvans-hero__body');
    if (body) {
      var b = body.getBoundingClientRect();
      if (b.width > 4 && b.height > 4) {
        this.textCol = { x0: wx(b.left - cv.left), x1: wx(b.right - cv.left), y0: wy(b.bottom - cv.top), y1: wy(b.top - cv.top) };
      }
    }
    var C = this.cover, T = this.textCol;
    var stacked = C && T ? T.y1 < C.y0 + 0.02 : vw < 1;
    this.stacked = stacked;

    // Mond: am Regler -- ausser im gestapelten Layout. Dort stuende er
    // hinter Box oder Text; er wird an die obere Coverkante gekoppelt
    // (halb dahinter, rechts der Mitte) und wird zum Gegenlicht-Kranz.
    if (stacked && C) {
      this.moonScale = 0.7;
      this.moonBase = { x: C.cx + 0.3 * C.w, y: C.y1 - 0.02 * C.h };
    } else if (body) {
      // Nebeneinander: der Regler gilt, solange Scheibe plus Kernhof
      // einen halben Scheibenradius Luft zu Eyebrow, Titel und
      // Unterzeile haben. Bei schmalen Desktop-/Tablet-Breiten
      // (1024-1366 px) rueckte der Titel sonst bis ins 's' an die
      // Scheibe, und das Maskenloch nahm ihm dort auch noch die
      // Textwolke (Abnahme: 1,07:1). Dann wandert der Mond auf
      // kuerzestem Weg nach rechts/oben, notfalls kleiner (x0,85, x0,75).
      var fit = this.fitMoon(body, wx, wy, (cfg.moonX - 0.5) * vw, 0.5 - cfg.moonY);
      if (fit) { this.moonScale = fit.s; if (fit.moved) this.moonBase = { x: fit.x, y: fit.y }; }
    }
    var mX = this.moonBase ? this.moonBase.x : (cfg.moonX - 0.5) * vw;
    var mY = this.moonBase ? this.moonBase.y : 0.5 - cfg.moonY;
    var discR = MOON_RD * cfg.moonSize * this.moonScale;    // Weltradius der Scheibe
    // Maskenloch in Schleier und Textwolke (section-sylvans.css): nur
    // Scheibe und Kernhof, der weite Halo bleibt abgedunkelt.
    // Das Skript schreibt den FERTIGEN Verlauf mit festen px-Werten in
    // je eine Custom Property: keine verschachtelten var()/calc() im
    // mask-image -- die sind in Safari/-webkit-mask-image die Wackel-
    // stelle. Ohne Wert gilt in CSS 'none' (keine Maske).
    // Erst ALLES lesen, dann schreiben: jedes setProperty macht den Stil
    // ungueltig, ein Rect danach erzwingt Neuberechnung (gemessen: der
    // Resize-Haken brauchte bei gemischter Folge bis 19 ms laenger).
    var st = root.style;
    var mpx = (mX / vw + 0.5) * w, mpy = (0.5 - mY) * h;   // relativ zur Leinwand
    var mr = discR * MOON_KERN * h;
    function hole(x, y) {
      return 'radial-gradient(circle at ' + x.toFixed(1) + 'px ' + y.toFixed(1) + 'px, transparent ' +
        (mr * 0.8).toFixed(1) + 'px, #000 ' + (mr * 1.3).toFixed(1) + 'px)';
    }
    var veil = root.querySelector('.sylvans-hero__schleier'), holeS = null, holeB = null;
    if (veil) {
      var vr = veil.getBoundingClientRect();
      holeS = hole(cv.left + mpx - vr.left, cv.top + mpy - vr.top);
    }
    if (body) {
      // Die Textwolke ist body::before mit inset -3.2rem -4rem; deren
      // Ursprung liest getComputedStyle als px, sonst aus den rem.
      var bb = body.getBoundingClientRect(), ox = NaN, oy = NaN;
      try {
        var pcs = window.getComputedStyle(body, '::before');
        ox = -parseFloat(pcs.left); oy = -parseFloat(pcs.top);
      } catch (e) { ox = NaN; }
      if (!isFinite(ox) || !isFinite(oy)) {
        var rem = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
        ox = 4 * rem; oy = 3.2 * rem;
      }
      holeB = hole(cv.left + mpx - bb.left + ox, cv.top + mpy - bb.top + oy);
    }
    // Das Skript schreibt den FERTIGEN Verlauf mit festen px-Werten in
    // je eine Custom Property: keine verschachtelten var()/calc() im
    // mask-image -- die sind in Safari/-webkit-mask-image die Wackel-
    // stelle. Ohne Wert gilt in CSS 'none' (keine Maske). Nur schreiben,
    // was sich geaendert hat: ein gleicher Wert loest sonst trotzdem
    // eine Stil-Neuberechnung der ganzen Buehne aus.
    function put(k, v) { if (v != null && st.getPropertyValue(k) !== v) st.setProperty(k, v); }
    put('--syl-moon-x', mpx.toFixed(1) + 'px');
    put('--syl-moon-y', mpy.toFixed(1) + 'px');
    put('--syl-moon-r', mr.toFixed(1) + 'px');
    put('--syl-moon-loch-s', holeS);
    put('--syl-moon-loch-b', holeB);

    // Grenzen fuer das Licht (Shader-Clip): nebeneinander die linke
    // Kante der Textspalte, gestapelt die Oberkante des Textblocks.
    var xMax = 99, yMin = -99;
    if (T && !stacked) xMax = T.x0 - 0.03;
    else if (T) yMin = T.y1 + 0.03;
    else if (vw >= 1) xMax = -0.02 * vw;

    // Strahlenkranz: zentriert auf dem Cover, Radius rotationsinvariant
    // vor der Textspalte begrenzt. Gestapelt nach oben versetzt und
    // unten ausgeblendet: Strahlen nur nach oben und zur Seite.
    var ray = this.rays, rv = ray.material.uniforms;
    if (C) {
      var R = 1.2 * C.h;
      if (!stacked && T) R = Math.min(R, T.x0 - C.cx - 0.06);
      if (stacked) R = Math.min(R, 0.85 * C.h);
      this.raysR = Math.max(0.05, R);
      ray.position.set(C.cx, stacked ? C.cy + 0.12 * C.h : C.cy, 0);
      rv.uXMax.value = xMax; rv.uYMin.value = yMin; rv.uLow.value = stacked ? 1 : 0;
      this.raysOn = R > 0.05;
    } else {
      this.raysOn = false;
    }

    // Faecher: Endpunkt der Bahn und Clip
    var fv = this.fan.material.uniforms;
    fv.uXMax.value = xMax; fv.uYMin.value = yMin;
    this.fanEnd = stacked ? vw / 2 + 0.45 : Math.min(-0.08 * vw, (T ? T.x0 : 0) - 0.25);

    // Vierzack-Funkelsterne: hoechstens acht, nur im freien Himmel --
    // links der Mitte, oberhalb von 35 % Bildhoehe, nicht hinter Cover,
    // Text oder Mond, nicht im oberen Schleierstreifen. Aus festem Seed:
    // gleiches Layout, gleiche Sterne.
    var rs = mulberry32(cfg.seed * 17 + 3);
    var pos = this.stars.geometry.attributes.position, dat = this.stars.geometry.attributes.aData;
    var placed = [], tries = 0, xLim = Math.min(0, T && !stacked ? T.x0 - 0.06 : 0);
    while (placed.length < STAR4_MAX - 1 && tries++ < 600) {
      var sx = -vw / 2 + 0.03 + rs() * (xLim + vw / 2 - 0.03), sy = 0.15 + rs() * 0.18;
      var ok = true;
      if (C && sx > C.x0 - 0.04 && sx < C.x1 + 0.04 && sy > C.y0 - 0.04 && sy < C.y1 + 0.04) ok = false;
      if (T && sx > T.x0 - 0.04 && sx < T.x1 + 0.04 && sy > T.y0 - 0.04 && sy < T.y1 + 0.04) ok = false;
      if (Math.abs(sx - mX) < 0.2 && Math.abs(sy - mY) < 0.2) ok = false;
      for (var q = 0; ok && q < placed.length; q++) {
        if (Math.abs(placed[q][0] - sx) < 0.16 && Math.abs(placed[q][1] - sy) < 0.1) ok = false;
      }
      if (ok) placed.push([sx, sy, 17 + rs() * 10]);
    }
    for (var k = 0; k < STAR4_MAX; k++) {
      var j = this.starDust + k, P = placed[k];
      pos.array[j * 3] = P ? P[0] / (vw / 2) : 0;
      pos.array[j * 3 + 1] = P ? P[1] : 0;
      dat.array[j * 4] = P ? P[2] : 0;
    }
    pos.needsUpdate = true;
    dat.needsUpdate = true;
  };

  // Mondplatz gegen die Schrift absichern. Gemessen
  // wird die Tinte (Range ueber den Text), nicht der Block: die
  // Bloecke laufen ueber die volle Spaltenbreite und enthielten den
  // Mond schon immer. Liefert {x, y, s, moved} oder null.
  var MOON_KERN = 1.42;   // Kernhof-Radius in Scheibenradien (MOON_FS: 1 + 0.42)
  Scene.prototype.fitMoon = function (body, wx, wy, bx, by) {
    var cv = this.canvas.getBoundingClientRect(), vw = this.viewW;
    var rects = [];
    var sel = ['.sylvans-hero__eyebrow', '.sylvans-hero__heading', '.sylvans-hero__subheading'];
    for (var i = 0; i < sel.length; i++) {
      var el = body.querySelector(sel[i]);
      if (!el || !document.createRange) continue;
      var rg = document.createRange();
      rg.selectNodeContents(el);
      var r = rg.getBoundingClientRect();
      if (rg.detach) rg.detach();
      if (r.width < 2 || r.height < 2) continue;
      rects.push({ x0: wx(r.left - cv.left), x1: wx(r.right - cv.left), y0: wy(r.bottom - cv.top), y1: wy(r.top - cv.top) });
    }
    if (!rects.length) return null;
    var d0 = MOON_RD * this.cfg.moonSize;
    function clearAt(x, y, s) {
      var need = d0 * s * (MOON_KERN + 0.5);
      for (var k = 0; k < rects.length; k++) {
        var R = rects[k];
        var dx = Math.max(R.x0 - x, 0, x - R.x1), dy = Math.max(R.y0 - y, 0, y - R.y1);
        if (dx * dx + dy * dy < need * need) return false;
      }
      return true;
    }
    if (clearAt(bx, by, 1)) return { x: bx, y: by, s: 1, moved: false };
    var scales = [1, 0.85, 0.75];
    for (var si = 0; si < scales.length; si++) {
      var s = scales[si], rim = d0 * s * 1.25;
      // Scheibe bleibt ganz im Bild: rechts und oben je 1,25 Radien Rand
      var xMax = vw / 2 - rim, yMax = 0.5 - rim;
      var best = null, bestCost = 1e9;
      for (var a = 0; a <= 24; a++) {
        for (var c = 0; c <= 12; c++) {
          var x = bx + (Math.max(bx, xMax) - bx) * a / 24, y = by + (Math.max(by, yMax) - by) * c / 12;
          if (!clearAt(x, y, s)) continue;
          var cost = Math.hypot(x - bx, y - by);
          if (cost < bestCost) { bestCost = cost; best = { x: x, y: y, s: s, moved: true }; }
        }
      }
      if (best) return best;
    }
    // Kein Platz: kleinster Mond rechts oben in der Ecke
    return { x: Math.max(bx, vw / 2 - d0 * 0.94), y: Math.max(by, 0.5 - d0 * 0.94), s: 0.75, moved: true };
  };

  Scene.prototype.tick = function () {
    var self = this;
    if (this.dead || !this.running) return;
    var t = now();
    var rawMs = this.lastTick ? t - this.lastTick : 16;
    var dt = Math.min(0.1, rawMs / 1000);
    this.lastTick = t;
    this.update(dt);
    this.render();

    // Bildraten-Waechter: bleibt es zu lange zu langsam, ist der
    // CSS-Fallback das bessere Angebot. Drei Regeln, alle in
    // Ghosthaven teuer gelernt:
    // (1) Schonfrist nach jedem start() -- direkt nach einem Start
    //     ruckeln auch fluessige Geraete (kalte Shader, Drittskripte
    //     nach load, Rueckkehr in den Viewport).
    // (2) Das Messfenster von 240 Bildern zaehlt erst ab Ablauf der
    //     Schonfrist, sonst frisst sie es auf.
    // (3) Abstaende ueber 400 ms sind kein Hardware-Urteil, sondern
    //     ein verdecktes Fenster oder ein fremder langer Task -- sie
    //     werden gar nicht gewertet, in keine Richtung.
    if (t > this.graceUntil && this.frames < 240) {
      this.frames++;
      if (rawMs > 50 && rawMs < 400) this.slowFrames++;
      else if (rawMs <= 50) this.slowFrames = Math.max(0, this.slowFrames - 1);
      if (this.slowFrames > 24 && this.onSlow) { this.onSlow(); return; }
    }
    // Erst hier reservieren, nicht am Kopf: wirft update() oder
    // render() jemals eine Exception, steht die Schleife still, statt
    // den Fehler sechzigmal pro Sekunde zu wiederholen. Und onSlow
    // oben kommt mit einem return aus, ohne einen schon reservierten
    // Frame canceln zu muessen.
    this.raf = window.requestAnimationFrame(function () { self.tick(); });
  };

  Scene.prototype.start = function () {
    if (this.dead || this.running || this.suspended) return;
    this.running = true;
    this.lastTick = 0;
    this.graceUntil = now() + 3000;
    this.tick();
  };

  Scene.prototype.stop = function () {
    this.running = false;
    if (this.raf) window.cancelAnimationFrame(this.raf);
    this.raf = 0;
  };

  Scene.prototype.destroy = function () {
    this.dead = true;
    this.stop();
    for (var i = 0; i < this.teardown.length; i++) { try { this.teardown[i](); } catch (e) {} }
    this.teardown.length = 0;
    var r = this.renderer;
    // Jede Eule haelt fuenf Posen-Texturen plus deren Spiegel-Klone, ihr Material aber immer nur
    // eine als map -- der traverse unten sieht also nur die gerade
    // gebundene. Die uebrigen ausdruecklich entsorgen; ein doppeltes
    // dispose ist in three folgenlos.
    for (var k = 0; k < this.owls.length; k++) {
      var maps = this.owls[k].maps;
      for (var j = 0; j < maps.length; j++) maps[j].dispose();
    }
    // Shader-Uniform-Texturen (Himmel, Wald, ...) haengen nicht an
    // material.map und waeren fuer den traverse unsichtbar.
    var own = this.ownTextures || [];
    for (var oi = 0; oi < own.length; oi++) own[oi].dispose();
    own.length = 0;
    this.scene.traverse(function (obj) {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        if (obj.material.map) obj.material.map.dispose();
        obj.material.dispose();
      }
    });
    if (r) r.dispose();
  };

  /* ------------------------------------------------------------------ */
  /* Aufbau, Layout und Lauf des Waldes                     */
  /* ------------------------------------------------------------------ */

  // Drehpunkt (Stammfuss) einer Kulisse in Welt-x. Rechts haengt er am
  // Mond: der Mondast liegt WALD-fest 0,213 Welt links vom Stamm, also
  // steht der Stamm um diesen Arm rechts vom Mond -- aber nie innerhalb
  // von 92 % Bildbreite, sonst stuende er in der Textspalte.
  function wKulPivot(side, vw, moonXw, ks) {
    if (side < 0) return -vw / 2 + 0.035 * ks;
    return Math.max(moonXw + 0.213 * ks, vw * 0.46);
  }

  // Ebenen-Quad mit Fuss bei y=0: oben top, unten below, darunter auf
  // Wunsch eine Verlaengerung ext mit v=0 -- die Klammer zieht die
  // letzte (Void-)Zeile bis unter den Bildrand, ohne neue Texel.
  function wPlane(w, top, below, ext) {
    var g = new THREE.PlaneGeometry(w, 1, 1, 2);
    var pos = g.attributes.position, uv = g.attributes.uv;
    for (var i = 0; i < pos.count; i++) {
      var y = pos.getY(i), row = y > 0.25 ? 0 : y > -0.25 ? 1 : 2;
      pos.setY(i, row === 0 ? top : row === 1 ? -below : -below - ext);
      uv.setY(i, row === 0 ? 1 : 0);
    }
    pos.needsUpdate = true;
    uv.needsUpdate = true;
    return g;
  }

  // Glimm-Quad als Kind einer Ebene/Kulisse: Einheitsbreite [0,1], die
  // Breite setzt die Skalierung, die Abbildung auf die Textur
  // offset/repeat. 24 Spalten mit Vertexfarben: darueber laeuft der
  // Wellenkamm von links nach rechts (ein paar Dutzend Floats je Bild,
  // nur waehrend der Welle). fade: die letzten 25 % blenden aus -- weiche
  // Kante zur Textspalte hin.
  function wGlowMesh(canvas, y0, y1, wrap, fade) {
    var cols = 24;
    var g = new THREE.PlaneGeometry(1, y1 - y0, cols, 1);
    g.translate(0.5, (y0 + y1) / 2, 0);
    var pos = g.attributes.position, n = pos.count;
    var col = new Float32Array(n * 3), u = new Float32Array(n), e = new Float32Array(n);
    for (var i = 0; i < n; i++) {
      u[i] = clamp(pos.getX(i), 0, 1);
      e[i] = fade ? clamp((1 - u[i]) / 0.25, 0, 1) : 1;
      col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = e[i];
    }
    var attr = new THREE.BufferAttribute(col, 3);
    if (attr.setUsage) attr.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('color', attr);
    var map = tex(canvas);
    if (wrap) map.wrapS = THREE.RepeatWrapping;
    var m = new THREE.Mesh(g, new THREE.MeshBasicMaterial({
      map: map, transparent: true, depthWrite: false, depthTest: false,
      blending: THREE.AdditiveBlending, vertexColors: true, opacity: 0
    }));
    m.visible = false;
    return { mesh: m, u: u, e: e, attr: attr };
  }

  // Ein Punkt der Glimmwelle, s Sekunden nachdem der Kamm ihn erreicht:
  // ease-out auf peak, dann exponentiell zurueck auf den Ruhewert.
  function wWave(s, peak, rest) {
    if (s <= 0) return 0;
    var r = WALD.waveRise;
    if (s < r) return peak * (1 - Math.pow(1 - s / r, 3));
    return rest + (peak - rest) * Math.exp(-(s - r) / WALD.waveFall);
  }

  // Baut Ebenen und Nebelbaenke sofort (das erste Bild braucht sie) und
  // stellt die Kulissen samt Glimmen als Nacharbeit in die Warteschlange:
  // sie werden in Leerlauf-Momenten nach dem ersten Bild gebacken und
  // hochgeladen und blenden weich ein -- der Hauptthread bekommt keinen
  // einzelnen langen Block mehr. Gibt die naechste freie renderOrder
  // zurueck. Reihenfolge von hinten: Kamm, Bank, Mitte, Mitte-Glimmen,
  // [lightSlot], Bank, vorn, Bank, Kulisse links, ihr Glimmen, Kulisse
  // rechts.
  Scene.prototype.buildForest = function (cfg, order) {
    var t0 = now();
    var self = this;
    var cw = this.canvas.clientWidth, ch = this.canvas.clientHeight;
    var a0 = cw && ch ? cw / ch : 2;
    var portrait = a0 < 1;
    var moonXw = (cfg.moonX - 0.5) * a0;
    var ks0 = portrait ? clamp(a0 * 0.9, 0.3, 0.6) : clamp(a0 / 1.6, 0.75, 1);
    var zF = WALD.zFoliage;
    var view = {
      viewW: a0, moonXw: moonXw, ks: ks0,
      kulPivot: function (side) { return wKulPivot(side, a0, moonXw, ks0); },
      kulX: function (side, dx) { return wKulPivot(side, a0, moonXw, ks0) + dx * ks0; },
      // Tonstufen nach Textur-Welt-x: Licht nur links der Mitte und
      // ganz rechts, hinter der Textspalte gar kein Laub (-1).
      allowW: function (wx) {
        if (wx > zF[0] && wx < zF[1]) return -1;
        if (wx <= -0.12) return 2;
        if (wx <= zF[0]) return 1;
        return wx < 0.9 ? 1 : 2;
      },
      moonEdgeW: function (wx) {
        var s = 0.35 * Math.max(0, 1 - Math.abs(wx - moonXw) / 1.2);
        return wx > zF[0] && wx < zF[1] ? Math.min(s, 0.12) : s;
      },
      moonEdgeK: function (wx) { return 0.35 * Math.max(0, 1 - Math.abs(wx - moonXw) / 1.2); }
    };
    var stampCache = {};
    function stampOf(col) { return stampCache[col] || (stampCache[col] = texLeafStamp(col)); }
    var maxC = wChroma(cfg.glimmer) * 0.9;
    function tone(hex, sat) { return wCapChroma(wMix(cfg.haze, hex, sat), maxC); }
    var kit = {
      tones: function (sat) {
        return {
          core: tone(WALD.core, sat), shadow: tone(WALD.shadow, sat), mid: tone(WALD.midTone, sat), light: tone(WALD.light, sat),
          // Kern dunkler als die Schattenstufe: er traegt die Tiefe und
          // darf nie als helles Oval durchscheinen
          coreDark: wGain(tone(WALD.core, sat), 0.72)
        };
      },
      stamps: function (sat) {
        return [stampOf(tone(WALD.shadow, sat)), stampOf(tone(WALD.midTone, sat)), stampOf(tone(WALD.light, sat))];
      },
      glowStamp: stampOf(cfg.glimmer),
      grain: texGrainNoise(mulberry32(cfg.seed * 13 + 5))
    };

    function mat(map, opts) {
      return new THREE.MeshBasicMaterial(Object.assign({
        map: map, transparent: true, depthWrite: false, depthTest: false
      }, opts || {}));
    }

    var F = this.forest = { layers: [], mist: [], kulissen: [], glows: [], jobs: [], jobMs: [], handle: 0 };
    this.forestStill = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    this.layers = [];
    for (var i = 0; i < WALD_LAYERS.length; i++) {
      var spec = WALD_LAYERS[i];
      var baked = texForest(mulberry32(cfg.seed * 13 + i), cfg, spec, view, kit);
      var map = tex(baked.canvas);
      map.wrapS = THREE.RepeatWrapping;
      var last = i === WALD_LAYERS.length - 1;
      var mesh = new THREE.Mesh(wPlane(WALD.worldW, spec.top, spec.below, last ? 1.5 : 0), mat(map));
      mesh.renderOrder = order++;
      this.scene.add(mesh);
      var L = { mesh: mesh, px: 0.012 + spec.d * 0.05, baseX: 0, baseY: 0.5 - spec.foot, d: spec.d, spec: spec, forest: true, u0: 0 };
      this.layers.push(L);
      F.layers.push(L);
      if (baked.glow) {
        var gw = wGlowMesh(baked.glow, -spec.below, spec.top, true, true);
        gw.mesh.renderOrder = order++;
        mesh.add(gw.mesh);
        F.glows.push({ mesh: gw.mesh, u: gw.u, e: gw.e, attr: gw.attr, start: WALD.waveMid,
                       sweep: WALD.sweepMid, peak: WALD.peakMid, host: L, kind: 'layer', level: 0 });
        // Freier Platz fuer den Mondlicht-Faecher (Paket himmel):
        // hinter vorderer Ebene und Kulissen, vor der Mitte.
        this.lightSlot = order++;
      }
      // Nebelbank hinter jeder Ebene ausser der hintersten -- so liegt
      // ihre Unterkante auf der Fusslinie der Ebene davor. Die dritte
      // Bank liegt vor der vorderen Ebene am Boden und nimmt mit einem
      // Tuerkisstich das Leuchtlaub auf.
      if (cfg.mist > 0) {
        var bankAfter = i;            // Bank i liegt hinter Ebene i+1
        if (bankAfter < WALD_LAYERS.length) {
          var low = bankAfter === WALD_LAYERS.length - 1;
          var mg = new THREE.PlaneGeometry(4.4, WALD.mistH);
          mg.translate(0, WALD.mistH / 2, 0);
          var mistC = texMist(mulberry32(cfg.seed * 7 + i), cfg, low ? WALD.mistLow : WALD.mistHigh);
          var M = new THREE.Mesh(mg, mat(tex(mistC), {
            blending: THREE.AdditiveBlending,
            opacity: cfg.mist * (0.5 + spec.d * 0.5)
          }));
          M.renderOrder = order++;
          this.scene.add(M);
          var ML = { mesh: M, px: 0.02 + spec.d * 0.04, baseX: 0, baseY: 0, mist: true, d0: spec.d, speed: i % 2 ? -1 : 1, low: low };
          this.layers.push(ML);
          F.mist.push(ML);
        }
      }
    }
    // Die beiden Kulissen, scharf und am naechsten: Parallaxe 0,08, Wind
    // als Drehung um den Stammfuss (Pivot per geometry.translate). Ihre
    // Plaetze in der Zeichenfolge stehen schon jetzt fest.
    var kulOrder = { '-1': order, '1': order + 2 };
    var glowOrder = order + 1;
    order += 3;
    var pending = {};
    function bakeKulisse(s) {
      return function () { pending[s] = texKulisse(mulberry32(cfg.seed * 13 + (s < 0 ? 3 : 4)), cfg, s, view, kit); };
    }
    function addKulisse(s) {
      return function () {
        var kb = pending[s];
        delete pending[s];
        if (!kb) return;
        var kg = new THREE.PlaneGeometry(kb.w, kb.h);
        kg.translate(kb.w / 2 - kb.foot, kb.h / 2, 0);
        var kmap = tex(kb.canvas);
        // Die Kulissen werden mobil und auf schmalen Desktops stark
        // verkleinert (bis ~2,5 Texel je Pixel) -- ohne Mipmaps flirrt das
        // Laub dort blockig. NPOT-Mipmaps kann WebGL2 nativ.
        kmap.generateMipmaps = true;
        kmap.minFilter = THREE.LinearMipmapLinearFilter;
        // Einblenden nur, wenn die Szene schon laeuft -- im Standbild
        // (reduced motion, Harness) steht die Kulisse sofort.
        var km = new THREE.Mesh(kg, mat(kmap, { opacity: self.running && !self.forestStill ? 0 : 1 }));
        km.renderOrder = kulOrder[s];
        self.scene.add(km);
        var K = {
          mesh: km, px: 0.08, baseX: 0, baseY: -0.53, d: 1, kulisse: true, side: s,
          period: s < 0 ? 7 : 11, phase: s < 0 ? 0 : 1.9, kw: kb.w, kh: kb.h, kfoot: kb.foot, tall: kb.tall,
          leafL: kb.leafL, leafR: kb.leafR, boxes: kb.boxes, fade: km.material.opacity
        };
        self.layers.push(K);
        F.kulissen.push(K);
        if (self.renderer.initTexture) self.renderer.initTexture(kmap);
        if (kb.glow) {
          var gw = wGlowMesh(kb.glow, 0, kb.h, false, false);
          gw.mesh.renderOrder = glowOrder;
          km.add(gw.mesh);
          F.glows.unshift({ mesh: gw.mesh, u: gw.u, e: gw.e, attr: gw.attr, start: WALD.waveKulisse,
                            sweep: WALD.sweepKul, peak: WALD.peakKul, host: K, kind: 'kulisse', level: 0 });
          if (self.renderer.initTexture) self.renderer.initTexture(gw.mesh.material.map);
        }
      };
    }
    F.jobs.push(bakeKulisse(-1), addKulisse(-1), bakeKulisse(1), addKulisse(1), function () {
      // Das Mittel-Glimmen ist bis zur Welle unsichtbar -- ohne
      // Vorab-Upload kaeme sein Hochladen mitten im inszenierten Moment.
      for (var g = 0; g < F.glows.length; g++) {
        if (F.glows[g].kind === 'layer' && self.renderer.initTexture) self.renderer.initTexture(F.glows[g].mesh.material.map);
      }
    });
    this.teardown.push(function () { wCancelIdle(F.handle); F.handle = 0; F.jobs.length = 0; });
    this.glowLeft = 0;
    this.forestBakeMs = now() - t0;
    this.forestPump();
    return order;
  };

  function wIdle(fn) {
    return window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 300 }) : window.setTimeout(fn, 40);
  }
  function wCancelIdle(h) {
    if (!h) return;
    if (window.cancelIdleCallback && window.requestIdleCallback) window.cancelIdleCallback(h);
    else window.clearTimeout(h);
  }

  // Nacharbeit: ein Schritt je Leerlauf-Moment.
  Scene.prototype.forestPump = function () {
    var self = this, F = this.forest;
    if (!F || this.dead || F.handle || !F.jobs.length) return;
    F.handle = wIdle(function () {
      F.handle = 0;
      if (self.dead) return;
      self.forestStep();
      self.forestPump();
    });
  };
  Scene.prototype.forestStep = function () {
    var F = this.forest, job = F.jobs.shift();
    if (!job) return;
    var t0 = now();
    job();
    F.jobMs.push(Math.round((now() - t0) * 10) / 10);
    this.layoutForest();
    // Steht die Schleife (reduced motion, ausserhalb des Bildes), das
    // Standbild mit der neuen Kulisse einmal neu zeichnen.
    if (!this.running && !this.dead && this.viewW) { this.update(0); this.render(); }
  };
  // Alles Ausstehende sofort erledigen (Harness, Messungen).
  Scene.prototype.forestFlush = function () {
    var F = this.forest;
    if (!F) return;
    wCancelIdle(F.handle);
    F.handle = 0;
    while (F.jobs.length) this.forestStep();
  };

  // Wo steht das Cover? Normalerweise misst das Paket himmel (this.cover);
  // ohne es misst der Wald selbst, und ohne DOM gilt eine Schaetzung.
  Scene.prototype.forestCover = function () {
    if (this.cover) return this.cover;
    try {
      var img = this.root && this.root.querySelector('.sylvans-hero__image');
      var c = this.canvas.getBoundingClientRect();
      if (img && c.width && c.height) {
        var r = img.getBoundingClientRect(), vw = this.viewW;
        if (r.width && r.height) {
          var x0 = ((r.left - c.left) / c.width - 0.5) * vw, x1 = ((r.right - c.left) / c.width - 0.5) * vw;
          var yb = 0.5 - (r.bottom - c.top) / c.height, yt = 0.5 - (r.top - c.top) / c.height;
          return { x0: x0, x1: x1, y0: yb, y1: yt, cx: (x0 + x1) / 2, cy: (yb + yt) / 2 };
        }
      }
    } catch (e) { /* ohne Layout eben geschaetzt */ }
    return null;
  };

  // Wo steht der Text? Je Zeilenblock (Eyebrow, Titel, Untertitel,
  // Tagline, Preis, Button) die Ausdehnung der SCHRIFT, nicht der Box --
  // als Rechtecke in Weltkoordinaten. Ohne DOM die nachgemessene Spalte.
  Scene.prototype.forestText = function () {
    var vw = this.viewW, out = [];
    try {
      var body = this.root && this.root.querySelector('.sylvans-hero__body');
      var c = this.canvas.getBoundingClientRect();
      if (body && c.width && c.height && document.createRange) {
        for (var i = 0; i < body.children.length; i++) {
          var rg = document.createRange();
          rg.selectNodeContents(body.children[i]);
          var r = rg.getBoundingClientRect();
          if (!r.width || !r.height) continue;
          out.push({
            x0: ((r.left - c.left) / c.width - 0.5) * vw, x1: ((r.right - c.left) / c.width - 0.5) * vw,
            y0: 0.5 - (r.bottom - c.top) / c.height, y1: 0.5 - (r.top - c.top) / c.height
          });
        }
      }
    } catch (e) { out = []; }
    if (!out.length) out.push({ x0: 0.06, x1: 0.7, y0: -0.32, y1: 0.4 });
    return out;
  };

  // Ueberlappt eine Kulisse mit ihrem Laub den Text (mit Abstand)?
  function wKulHitsText(K, pivot, ks, baseY, txt, mx, my, ksy) {
    var ky = ksy || ks;
    for (var b = 0; b < K.boxes.length; b++) {
      var q = K.boxes[b];
      var x0 = pivot + q[0] * ks, x1 = pivot + q[2] * ks, y0 = baseY + q[1] * ky, y1 = baseY + q[3] * ky;
      for (var t = 0; t < txt.length; t++) {
        var r = txt[t];
        if (x1 > r.x0 - mx && x0 < r.x1 + mx && y1 > r.y0 - my && y0 < r.y1 + my) return true;
      }
    }
    return false;
  }

  // Alle Positionen in Bruchteilen der SICHTBAREN Flaeche -- feste
  // Weltwerte gelten nur bei einem Seitenverhaeltnis (Ortho-Falle).
  Scene.prototype.layoutForest = function () {
    var F = this.forest;
    if (!F) return;
    var cfg = this.cfg, vw = this.viewW, portrait = vw < 1, i;
    var feet = [];
    for (i = 0; i < F.layers.length; i++) feet.push(F.layers[i].spec.foot);
    if (portrait) {
      // Hochkant: der Wald rueckt hoch, die Wipfel stehen neben dem
      // Cover, der Boden liegt unter ihm -- dort beginnt der Text auf
      // dunklem Grund. Anker ist die Unterkante des Covers.
      var cov = this.forestCover();
      var bottom = cov ? Math.min(cov.y0, cov.y1) : -0.1;
      var ff = clamp(0.5 - bottom + 0.012, 0.35, 0.85);
      feet = [ff - 0.045, ff - 0.022, ff];
    }
    // Texeldichte: Quads fest 2,7 breit; bis 1,2 wird gestreckt, darueber
    // kachelt die (nahtlose) Textur -- neu gebacken wird nie. Hochkant
    // zeigt das schmale Fenster einen baumreichen Ausschnitt links der
    // Schutzzone (die Textspalte liegt dort ja unter dem Cover).
    var S = Math.max(1, (vw + 0.2) / WALD.worldW), R = Math.max(1, S / 1.2);
    var u0 = portrait ? WALD.portraitCenter / WALD.worldW : 0;
    for (i = 0; i < F.layers.length; i++) {
      var L = F.layers[i];
      L.mesh.scale.x = S;
      L.mesh.material.map.repeat.x = R;
      L.mesh.material.map.offset.x = (1 - R) / 2 + u0;
      L.baseX = 0;
      L.baseY = 0.5 - feet[i];
      L.S = S; L.R = R; L.u0 = u0;
    }
    // Hochkant steht der Mond (Paket himmel) als Gegenlicht an
    // der oberen Coverkante -- und genau dort trugen Mittel- und
    // Vorderebene im baumreichen Ausschnitt ihre Kronen. Die Ebenen
    // werden darum als Ganzes um den kuerzesten Weg verschoben, bei dem
    // Scheibe und Kernhof (plus Parallaxe-Spiel) in einer Kronenluecke
    // stehen. Gemessen wird am gebackenen Alpha, einmal je Layout.
    if (portrait && this.moonBase) {
      var dSh = this.forestMoonGap(F.layers, S, R, u0);
      if (dSh) {
        for (i = 0; i < F.layers.length; i++) {
          var Ls = F.layers[i];
          Ls.u0 = u0 + dSh * R / (WALD.worldW * S);
          Ls.mesh.material.map.offset.x = (1 - R) / 2 + Ls.u0;
        }
      }
      this.forestMoonShift = dSh;
    }
    var mistS = Math.max(1, (vw + 0.9) / 3.35);
    for (i = 0; i < F.mist.length; i++) {
      var M = F.mist[i];
      var onFoot = i + 1 < feet.length ? feet[i + 1] : Math.min(0.97, feet[feet.length - 1] + 0.08);
      M.mesh.scale.x = mistS;
      M.baseY = 0.5 - onFoot;
    }
    var txt = portrait ? [] : this.forestText();
    var txL = 0.06;
    for (i = 0; i < txt.length; i++) txL = Math.min(txL, txt[i].x0);
    var moonXw = (cfg.moonX - 0.5) * vw;
    // Auf schmalen Querformaten (1024-1366 px) schiebt fitMoon()
    // (Paket himmel, laeuft in resize() VOR diesem Layout) den Mond aus
    // dem Titel nach rechts/oben. Der Mondast der rechten Kulisse folgt
    // ihm dorthin. Hochkant bleibt der Regler massgeblich: dort haengt
    // der Mond an der Coverkante, die Kulisse aber am Bildrand.
    var mbF = !portrait && this.moonBase ? this.moonBase : null;
    if (mbF) moonXw = mbF.x;
    var moonYf = mbF ? 0.5 - mbF.y : cfg.moonY;
    // Hochkant an die Bildbreite gekoppelt: der Stamm nimmt hoechstens
    // ~7 % der Breite ein, statt als schwarzer Block neben dem Cover.
    var ks = portrait ? Math.min(clamp((feet[2] + 0.06) / 1.03, 0.3, 1), vw * 0.9)
                      : clamp(vw / 1.6, 0.75, 1);
    for (i = 0; i < F.kulissen.length; i++) {
      var K = F.kulissen[i], kk = ks, pv, by;
      if (portrait) {
        by = 0.5 - feet[2] - 0.004;
      } else {
        // Mondast unter die Scheibe: gebaut fuer Mond bei 18 % Hoehe
        var dy = K.side > 0 ? (moonYf - 0.18) : 0;
        by = Math.min(-0.53, 0.55 - K.kh * kk) - Math.max(0, dy);
      }
      pv = wKulPivot(K.side, vw, moonXw, kk);
      // Unter 1,0 Massstab (Querformate unter ~1530 px Breite,
      // iPad hochkant) endete die Textur unterhalb der Bildoberkante --
      // eine harte waagerechte Laubkante quer ueber dem Cover (sichtbar
      // bei 1024x700, Oberkante 0,40 statt 0,55). Die Kulisse wird darum
      // nur senkrecht so weit gestreckt, dass ihr Blaetterdach wie
      // gebaut ueber die Oberkante blutet; Stammfuss und Breite bleiben.
      // Eine im Querformat gebackene Kulisse (Geraet nach dem Laden
      // gedreht) hat keine runde Kronenkante und wird auch hochkant
      // gestreckt.
      function kyOf(k, b) { return portrait && K.tall ? k : Math.max(k, (0.55 - b) / K.kh); }
      var ky = kyOf(kk, by);
      var show = K.side < 0 || portrait || cfg.moonX >= 0.7;
      if (!portrait && show) {
        if (K.side < 0) {
          // links: notfalls kleiner, bis das Laub vor der Spalte endet
          while (kk > 0.55 && wKulHitsText(K, pv, kk, by, txt, 0.08, 0.03, ky)) {
            kk -= 0.03;
            pv = wKulPivot(-1, vw, moonXw, kk);
            by = Math.min(-0.53, 0.55 - K.kh * kk);
            ky = kyOf(kk, by);
          }
        } else {
          // rechts: so weit nach rechts, bis das Laub mit Abstand rechts
          // der gemessenen Schrift endet; geht das nur mit dem Stamm
          // jenseits der Bildkante, bleibt die Kulisse aus.
          while (show && wKulHitsText(K, pv, kk, by, txt, 0.07, 0.04, ky)) {
            pv += 0.02;
            if (pv > vw / 2 + 0.03) show = false;
          }
        }
      }
      // Hochkant koppelt der Himmel den Mond an die obere
      // Coverkante (moonBase) -- genau dorthin, wo die rechte Kulisse ihr
      // Blaetterdach traegt. Die Kulisse lag damit VOR dem Mond (sie wird
      // spaeter gezeichnet), der Gegenlicht-Kranz und die Mondpassage
      // der Ouvertuere verschwanden im Laub. Darum rueckt sie hochkant
      // nach rechts, bis Scheibe und Kernhof frei sind, notfalls ganz
      // aus dem Bild.
      if (portrait && show && K.side > 0 && this.moonBase) {
        var mR = MOON_RD * cfg.moonSize * (this.moonScale || 1) * MOON_KERN + 0.02;
        var mRect = [{ x0: this.moonBase.x - mR, x1: this.moonBase.x + mR, y0: this.moonBase.y - mR, y1: this.moonBase.y + mR }];
        while (show && wKulHitsText(K, pv, kk, by, mRect, 0, 0, ky)) {
          pv += 0.01;
          if (pv > vw / 2 + 0.03) show = false;
        }
      }
      K.mesh.scale.set(kk, ky, 1);
      K.ky = ky;
      K.baseX = pv;
      K.baseY = by;
      K.mesh.visible = show;
      K.ks = kk;
    }
    // Glimm-Quads: Mittelebene nur links, bis 45 % Bildbreite und mit
    // Abstand vor der gemessenen Textspalte; hochkant ueber die Breite.
    for (i = 0; i < F.glows.length; i++) {
      var G = F.glows[i], m = G.mesh.material.map;
      if (G.kind === 'layer') {
        var H = G.host;
        var wl = -vw / 2 - 0.12;
        var wr = portrait ? vw / 2 + 0.12 : Math.min(-vw / 2 + 0.45 * vw, txL - 0.1);
        G.a = (wl - H.baseX) / H.S;
        G.span = Math.max(0.05, (wr - wl) / H.S);
        G.mesh.position.x = G.a;
        G.mesh.scale.x = G.span;
        m.offset.x = (1 - H.R) / 2 + H.u0 + H.R * (G.a + 1.35) / 2.7;
        m.repeat.x = H.R * G.span / 2.7;
      } else {
        G.mesh.position.x = -G.host.kfoot;
        G.mesh.scale.x = G.host.kw;
        m.offset.x = 0;
        m.repeat.x = 1;
      }
    }
  };

  // Kronendeckung ueber dem Mond fuer eine Ebenen-Verschiebung
  // d (Welt). Liefert das kleinste |d| in [-0,5; 0,5], bei dem Mittel-
  // und Vorderebene die Scheibe samt Kernhof hoechstens zu 12 % decken,
  // sonst das d mit der geringsten Deckung.
  Scene.prototype.forestMoonGap = function (layers, S, R, u0) {
    var cfg = this.cfg, mb = this.moonBase;
    var r = MOON_RD * cfg.moonSize * (this.moonScale || 1) * MOON_KERN + 0.025;
    var bands = [];
    for (var li = 1; li < layers.length; li++) {
      var L = layers[li], img = L.mesh.material.map.image;
      if (!img || !img.getContext) return 0;
      var W = img.width, H = img.height, sp = L.spec;
      function ty(y) { return Math.round((1 - (y - L.baseY + sp.below) / (sp.top + sp.below)) * H); }
      var y0 = clamp(ty(mb.y + r), 0, H - 1), y1 = clamp(ty(mb.y - r), 0, H - 1);
      if (y1 <= y0) continue;
      var data;
      try { data = img.getContext('2d').getImageData(0, y0, W, y1 - y0 + 1).data; } catch (e) { return 0; }
      bands.push({ L: L, W: W, y0: y0, n: y1 - y0 + 1, d: data, ty: ty });
    }
    if (!bands.length) return 0;
    function cover(dw) {
      var hit = 0, tot = 0;
      for (var a = -3; a <= 3; a++) {
        for (var b = -3; b <= 3; b++) {
          if (a * a + b * b > 10) continue;
          var x = mb.x + r * a / 3, y = mb.y + r * b / 3, al = 0;
          for (var k = 0; k < bands.length; k++) {
            var B = bands[k];
            var u = (1 - R) / 2 + u0 + dw * R / (WALD.worldW * S) + R * (x / (WALD.worldW * S) + 0.5);
            var px = Math.floor((u - Math.floor(u)) * B.W), py = B.ty(y) - B.y0;
            if (py < 0 || py >= B.n) continue;
            al = Math.max(al, B.d[(py * B.W + px) * 4 + 3] / 255);
          }
          hit += al; tot++;
        }
      }
      return hit / tot;
    }
    var best = 0, bestC = cover(0);
    if (bestC <= 0.12) return 0;
    for (var s = 1; s <= 25; s++) {
      for (var sg = -1; sg <= 1; sg += 2) {
        var dw = sg * s * 0.02, c = cover(dw);
        if (c <= 0.12) return dw;
        if (c < bestC - 0.02) { bestC = c; best = dw; }
      }
    }
    return best;
  };

  Scene.prototype.updateForest = function (dt, t, px, py, br) {
    var cfg = this.cfg, tau = t - this.tStart, i;
    for (i = 0; i < this.layers.length; i++) {
      var L = this.layers[i];
      var sgn = L.speed < 0 ? -1 : 1;
      // Kein endloses Wandern mit Umbruch -- der Sprung waere sichtbar.
      var drift = L.mist
        ? Math.sin(t * (0.02 + L.d0 * 0.012) + i * 2.1) * 0.28 * sgn
        : L.kulisse ? 0 : Math.sin(t * 0.016 + i) * 0.006;
      // Erst-Nebelschub: 3 s statt 6 s -- er soll den Auftakt (Strahlen-
      // kranz, erste Eule ab 1,6 s) nicht ueberlagern, nur anschieben.
      if (L.mist) drift += Math.max(0, 1 - tau / 3) * 0.12 * sgn;
      L.mesh.position.x = (L.baseX || 0) + drift - px * L.px;
      L.mesh.position.y = L.baseY + py * L.px * 0.35;
      if (L.kulisse) {
        L.mesh.rotation.z = this.forestStill ? 0 : 0.006 * Math.sin(t * 6.2832 / L.period + L.phase);
        // nachgereichte Kulisse blendet in 0,6 s ein
        if (L.fade < 1) {
          L.fade = this.running && !this.forestStill ? Math.min(1, L.fade + dt / 0.6) : 1;
          L.mesh.material.opacity = L.fade;
        }
      }
      if (L.mist) {
        L.boost = Math.max(0, (L.boost || 0) - dt / 2.5);
        // Tiefenstaffelung, Atem, Mondlicht unter der Scheibe und
        // Duell-Aufwirbeln wie bisher. Der Faktor 0.75 haelt die Spitzen
        // auf dem nachgemessenen Kontrastniveau; WALD_MIST_NORM gleicht
        // die mittlere Deckung der neuen Bodennebel-Textur an die alte an.
        var shaftNear = Math.max(0, 1 - Math.abs(L.mesh.position.x - this.moonX) / 0.9);
        L.mesh.material.opacity = cfg.mist * (0.5 + L.d0 * 0.5) * 0.75 * WALD_MIST_NORM *
          (0.72 + 0.28 * Math.sin(t * 0.1 + i * 1.7)) *
          (0.82 + 0.18 * br) *
          (1 + 0.2 * shaftNear * br + 0.45 * L.boost);
      }
    }
    // Das Laub wacht auf: ein Kamm laeuft von links ueber die Bueschel --
    // Kulisse ab 2,8 s, Mitte ab 3,3 s --, jeder Punkt glueht kurz auf und
    // sinkt auf den Ruhewert, der danach mit der Nacht atmet. Reduced
    // motion: der Ruhewert steht sofort.
    var rest = 0.35 + 0.25 * br, F = this.forest;
    var waveShift = this.waveAt != null ? this.waveAt - WALD.waveKulisse : 0;
    for (i = 0; i < F.glows.length; i++) {
      var G = F.glows[i], a = G.attr.array, n = G.u.length, k;
      var s0 = tau - G.start - waveShift;
      var settled = this.forestStill || s0 - G.sweep - WALD.waveRise > 6 * WALD.waveFall;
      var fade = G.host.fade != null ? G.host.fade : 1;
      if (settled) {
        if (!G.flat) {
          for (k = 0; k < n; k++) a[k * 3] = a[k * 3 + 1] = a[k * 3 + 2] = G.e[k];
          G.attr.needsUpdate = true;
          G.flat = true;
        }
        G.level = rest;
        G.mesh.visible = true;
        G.mesh.material.opacity = rest * fade;
        continue;
      }
      if (G.flat) G.flat = false;
      if (s0 <= 0) { G.level = 0; G.mesh.visible = false; continue; }
      for (k = 0; k < n; k++) {
        var lv = wWave(s0 - G.u[k] * G.sweep, G.peak, rest) * G.e[k];
        a[k * 3] = a[k * 3 + 1] = a[k * 3 + 2] = lv;
      }
      G.attr.needsUpdate = true;
      G.level = wWave(s0 - 0.5 * G.sweep, G.peak, rest);
      G.mesh.visible = true;
      G.mesh.material.opacity = fade;
    }
    this.glowLeft = 0;
    for (i = 0; i < F.glows.length; i++) {
      if (F.glows[i].kind === 'kulisse') this.glowLeft = Math.min(1, F.glows[i].level);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Anbindung                                                           */
  /* ------------------------------------------------------------------ */

  function readConfig(root) {
    var el = document.getElementById(root.getAttribute('data-sylvans-scene'));
    var raw = {};
    if (el) { try { raw = JSON.parse(el.textContent) || {}; } catch (e) { raw = {}; } }
    function num(v, d) { var n = parseFloat(v); return isNaN(n) ? d : n; }
    return {
      seed: num(raw.seed, 8),
      density: clamp(num(raw.density, 100) / 100, 0.4, 1.6),
      mist: clamp(num(raw.mist, 100) / 100, 0, 1.5) * 0.55,
      fireflies: Math.round(clamp(num(raw.fireflies, 36), 0, 80)),
      owlEvery: clamp(num(raw.owl_every, 22), 8, 90),
      // Standard rechts der Mitte: mittig sass der Mond hinter dem
      // Cover und ueberdeckte das Artwork (Rueckmeldung von Chris).
      moonX: clamp(num(raw.moon_x, 86) / 100, 0.1, 0.9),
      moonY: clamp(num(raw.moon_y, 18) / 100, 0.05, 0.6),
      moonSize: clamp(num(raw.moon_size, 100) / 100, 0.5, 1.5) * 0.9,
      grain: clamp(num(raw.grain, 5), 0, 20) / 100 * 0.5,
      skyTop: raw.sky_top || '#120c26',
      skyLow: raw.sky_low || '#1A1233',
      haze: raw.haze || '#444881',
      ink: raw.ink || '#0d081c',
      moon: raw.moon || '#F2F6F8',
      frost: raw.frost || '#C3E2EA',
      glimmer: raw.glimmer || '#57B1D0'
    };
  }

  function grain(root, cfg, rnd) {
    var el = root.querySelector('[data-syl-grain]');
    if (!el || cfg.grain <= 0) return;
    var size = 180;
    var c = makeCanvas(size, size), x = c.getContext('2d');
    var img = x.createImageData(size, size), d = img.data;
    for (var i = 0; i < size * size; i++) {
      var v = (Math.floor(rnd() * 255));
      var p = i * 4;
      d[p] = d[p + 1] = d[p + 2] = v; d[p + 3] = 255;
    }
    x.putImageData(img, 0, 0);
    try {
      el.style.backgroundImage = 'url(' + c.toDataURL() + ')';
      el.style.opacity = cfg.grain;
    } catch (e) { /* verunreinigte Canvas: dann eben kein Korn */ }
  }

  // Wartet nach einem Aufbau ohne Flaeche auf den ersten Moment, in dem
  // der Hero wirklich Layout hat, und versucht boot() genau dann erneut.
  // Ein Satz Zuhoerer pro Hero; wer zuerst greift, raeumt alle ab. Der
  // Abbruchgriff haengt am root, damit section:unload im Theme-Editor
  // auch wartende Zuhoerer entsorgt.
  function retryWhenSized(root) {
    if (root.__sylRetryCancel) return;
    var undo = [];
    function cancel() {
      for (var i = 0; i < undo.length; i++) { try { undo[i](); } catch (e) {} }
      undo.length = 0;
      root.__sylRetryCancel = null;
    }
    function attempt() {
      if (root.__sylvansScene) { cancel(); return; }
      if (!root.clientWidth || !root.clientHeight) return;
      cancel();
      boot(root);
    }
    function on(target, type) {
      var fn = function () { attempt(); };
      target.addEventListener(type, fn, { passive: true });
      undo.push(function () { target.removeEventListener(type, fn); });
    }
    root.__sylRetryCancel = cancel;
    on(window, 'resize');
    on(window, 'orientationchange');
    on(document, 'visibilitychange');
    if (window.IntersectionObserver) {
      var io = new IntersectionObserver(function () { attempt(); });
      io.observe(root);
      undo.push(function () { io.disconnect(); });
    }
  }

  function boot(root) {
    if (!root || root.__sylvansScene) return;
    var canvas = root.querySelector('[data-syl-canvas]');
    if (!canvas) return;

    var cfg = readConfig(root);

    function giveUp(err, scene) {
      // Der CSS-Hero bleibt stehen und traegt die Seite allein --
      // besser als eine haengende oder ruckelnde Szene.
      root.classList.remove('syl-scene--ready');
      if (scene) scene.destroy();
      root.__sylvansScene = null;
      if (window.console && console.warn) console.warn('[sylvans]', err);
    }

    if (!THREE) THREE = window.THREE;
    if (!THREE) { giveUp(new Error('Three.js nicht geladen')); return; }
    if (softwareRenderer()) { giveUp(new Error('WebGL laeuft ueber die CPU')); return; }

    var scene;
    try {
      scene = new Scene(root, canvas, cfg);
      if (!scene.resize()) {
        // Ein Hero ohne Flaeche ist meist kein Fehler, sondern ein
        // Zeitpunkt: Prerender, Hintergrund-Tab und Editor-Zustaende
        // liefern beim load-Moment Breite 0, kurz darauf steht das
        // Layout. Sauber abbauen und auf den ersten brauchbaren
        // Moment warten, statt endgueltig aufzugeben.
        giveUp(new Error('Hero ohne Flaeche'), scene);
        retryWhenSized(root);
        return;
      }
    } catch (err) { giveUp(err, scene); return; }

    root.__sylvansScene = scene;

    if (!canvas.__sylWired) {
      canvas.__sylWired = true;
      canvas.addEventListener('webglcontextlost', function (e) {
        e.preventDefault();
        var sc = root.__sylvansScene;
        if (sc) { sc.stop(); sc.suspended = true; }
        root.classList.remove('syl-scene--ready');
      }, false);
      canvas.addEventListener('webglcontextrestored', function () {
        if (root.__sylvansScene) { root.__sylvansScene.destroy(); root.__sylvansScene = null; }
        boot(root);
      }, false);
    }

    grain(root, cfg, mulberry32(cfg.seed * 41 + 5));
    // reduzierte Bewegung zeigt den Ruhezustand, nicht
    // den Auftakt -- alle Intro-Zeiten haengen an tStart.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) scene.tStart = scene.t - 60;
    scene.update(0);
    scene.render();
    // Alle Posen-Texturen sofort zur GPU: sonst ruckelt ausgerechnet
    // der erste Fluegelschlag, wenn Pose 0 und 2 erstmals gebunden
    // werden -- mitten im inszenierten Moment.
    if (scene.renderer.initTexture) {
      for (var oi = 0; oi < scene.owls.length; oi++) {
        for (var mi = 0; mi < scene.owls[oi].maps.length; mi++) {
          scene.renderer.initTexture(scene.owls[oi].maps[mi]);
        }
      }
    }
    root.classList.add('syl-scene--ready');
    wire(root, scene, giveUp);
  }

  function wire(root, scene, giveUp) {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function on(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      scene.teardown.push(function () { target.removeEventListener(type, fn, opts); });
    }

    // Ein Riss ist noch kein Urteil: im selben Fenster stecken kalte
    // Shader-Kompilierung und Drittskripte, und ein Waechter kann ein
    // hoffnungsloses Geraet nicht von diesem einmaligen Zoll
    // unterscheiden (Ghosthaven-Lehre, dort als Handy-Doppellade-
    // Mysterium teuer bezahlt). Erster Riss: 800 ms Luft, Zaehler
    // nullen, ein stiller zweiter Anlauf. Erst der zweite Riss beendet
    // die Szene endgueltig.
    scene.onSlow = function () {
      if (!scene.slowRetried) {
        scene.slowRetried = true;
        scene.stop();
        var timer = window.setTimeout(function () {
          if (scene.dead || scene.suspended) return;
          scene.frames = 0;
          scene.slowFrames = 0;
          if (scene.visible !== false && !document.hidden) scene.start();
        }, 800);
        scene.teardown.push(function () { window.clearTimeout(timer); });
        return;
      }
      giveUp(new Error('Bildrate zu niedrig'), scene);
    };

    var timer = 0;
    function onResize() {
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        if (scene.dead || scene.suspended) return;
        if (scene.resize()) { scene.update(0); scene.render(); }
      }, 180);
    }
    // Vor dem reduced-motion-Ausstieg registrieren, damit auch das
    // Standbild bei Drehung des Geraets neu gezeichnet wird.
    on(window, 'resize', onResize, { passive: true });
    on(window, 'orientationchange', onResize, { passive: true });

    if (reduce) return;   // ein Standbild genuegt, keine Schleife

    if (window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
      on(window, 'pointermove', function (e) {
        if (scene.dead || scene.suspended) return;
        var r = scene.canvas.getBoundingClientRect();
        if (!r.width || !r.height) return;
        scene.pointer.tx = clamp((e.clientX - r.left) / r.width * 2 - 1, -1, 1);
        scene.pointer.ty = clamp((e.clientY - r.top) / r.height * 2 - 1, -1, 1);
      }, { passive: true });
    }

    on(document, 'visibilitychange', function () {
      if (scene.dead || scene.suspended) return;
      if (document.hidden) scene.stop();
      else if (scene.visible !== false) scene.start();
    });

    if (window.IntersectionObserver) {
      var io = new IntersectionObserver(function (entries) {
        if (scene.dead || scene.suspended) return;
        for (var i = 0; i < entries.length; i++) {
          scene.visible = entries[i].isIntersecting;
          if (scene.visible && !document.hidden) scene.start(); else scene.stop();
        }
      }, { rootMargin: '120px' });
      io.observe(root);
      scene.teardown.push(function () { io.disconnect(); });
    } else {
      scene.start();
    }
  }

  function init(scope) {
    var nodes = (scope || document).querySelectorAll('[data-sylvans-scene]');
    for (var i = 0; i < nodes.length; i++) boot(nodes[i]);
  }

  // Erst nach load: der CSS-Hero steht dann laengst, die Szene soll
  // sich nicht mit dem ersten Bildaufbau um den Hauptthread streiten.
  function kickOff() {
    if (window.requestIdleCallback) window.requestIdleCallback(function () { init(document); }, { timeout: 600 });
    else window.setTimeout(function () { init(document); }, 60);
  }
  if (document.readyState === 'complete') kickOff();
  else window.addEventListener('load', kickOff);

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-sylvans-scene]');
    if (!root) return;
    if (root.__sylRetryCancel) root.__sylRetryCancel();
    if (root.__sylvansScene) { root.__sylvansScene.destroy(); root.__sylvansScene = null; }
  });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
